import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';
import {
   Box,
   Typography,
   CircularProgress,
   Paper,
   Chip,
   Avatar,
   Grid,
   Divider,
   Card,
   CardMedia,
   CardContent,
   Button,
   IconButton,
   Skeleton,
   Stack,
   AvatarGroup,
   Alert,
   FormControl,
   Select,
   MenuItem,
   InputLabel,
   ToggleButton,
   ToggleButtonGroup,
   Tabs,
   Tab,
   Snackbar,
   LinearProgress,
   Tooltip,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Pagination,
   Checkbox,
   List,
   ListItem,
   ListItemAvatar,
   ListItemText,
   ListItemSecondaryAction,
   Switch,
   FormControlLabel,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import CloseIcon from '@mui/icons-material/Close';
import MessageIcon from '@mui/icons-material/Message';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import PhotoSelector from './PersonalAccount/AIPhotoStudio/components/PhotoSelector';
import PhotoLikeButton from '../components/common/PhotoLikeButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useUser } from '../contexts/UserContext';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import GridViewIcon from '@mui/icons-material/GridView';
import ArticleIcon from '@mui/icons-material/Article';
import TimelineControls, {
   TimelineSort,
   TimelineView,
   TimelineSpeed,
} from '../components/albums/TimelineControls';
import MagazineExport from '../components/albums/MagazineExport';
import AlbumSharingDialog from '../components/albums/AlbumSharingDialog';
import AlbumInviteDialog from '../components/albums/AlbumInviteDialog';
import ParticipantReminderModal from '../components/albums/ParticipantReminderModal';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ContactsIcon from '@mui/icons-material/Contacts';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ChatIcon from '@mui/icons-material/Chat';
import LinkIcon from '@mui/icons-material/Link';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InfoIcon from '@mui/icons-material/Info';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TextField from '@mui/material/TextField';
import QrCodeIcon from '@mui/icons-material/QrCode';
import FolderIcon from '@mui/icons-material/Folder';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import BusinessIcon from '@mui/icons-material/Business';
import type { PhotographerProfilePublic } from '../components/public-gallery/types';
import { PhotographerBadge } from '../components/common/PhotographerBadge';
import QRCode from 'qrcode';
import jsPDF from 'jspdf';
import { getImageTakenDate } from '../utils/imageUtils';
import { getAuthHeaders } from '../utils/auth';
import { batchUpload, BatchUploadProgress } from '../utils/batchUpload';
import { preparePhotosForUpload } from '../utils/preparePhotosForUpload';
import { useDrag, useDrop } from 'react-dnd';
import { useInfiniteScrollPhotos, useInfiniteScroll } from '../hooks/useInfiniteScrollPhotos';
import {
   collection,
   addDoc,
   query,
   where,
   getDocs,
   getDoc,
   doc,
   updateDoc,
   serverTimestamp,
   Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import PhotoGalleryPopup from '../components/Photos/PhotoGalleryPopup';
import ParticipantProfileModal from '../components/ParticipantProfileModal';
import { getDefaultBackgroundStyle } from '../utils/defaultImages';
import SharePhotosButton from '../components/PhotoSharing/SharePhotosButton';
import PhotoSharingModal from '../components/PhotoSharing/PhotoSharingModal';
import { EntityData } from '../utils/photoSharingPermissions';
import AlbumAnalytics from './albums/components/AlbumAnalytics';
import PhotoGroupsBento from '../components/albums/PhotoGroupsBento';
import CollectionsIcon from '@mui/icons-material/Collections';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import GalleryView from '../components/gallery/GalleryView';

interface Album {
   id: string;
   title: string;
   description?: string;
   theme?: string;
   memberIds?: string[]; // Legacy field
   members?: Array<{
      userId: string;
      email: string;
      displayName?: string;
      photoURL?: string;
      role: 'owner' | 'editor' | 'viewer';
      joinedAt?: any;
   }>;
   photoIds?: string[];
   photoCount?: number;
   createdAt?: any;
   albumDate?: string;
   coverPhotoId?: string | null;
   coverPhotoUrl?: string;
   location?: { latitude: number; longitude: number };
   camera?: { make: string; model: string };
   settings?: { iso: number; aperture: number };
   isPublic?: boolean;
   type?: 'private' | 'public' | 'paid';
   pricePerPhoto?: number;
   pricingModel?: 'per_photo' | 'one_time';
   shareToken?: string;
   shareEnabled?: boolean;
   createdBy?: string;
   enableParticipantParticipation?: boolean;
   enableParticipantUpload?: boolean;
   enableParticipantInvite?: boolean;
   allowPhotographerUpload?: boolean;
}

interface AlbumMember {
   id: string;
   email: string;
   displayName?: string;
   photoURL?: string;
   role: 'owner' | 'editor' | 'viewer';
   joinedAt?: string;
   profileData?: any; // User profile data from userProfile collection
   connectionStatus?: {
      status: 'none' | 'connected' | 'pending_sent' | 'pending_received';
      requestId?: string;
   };
}

// React DnD types
const ItemTypes = {
   PHOTO: 'photo',
};

interface DragItem {
   type: string;
   photoId: string;
}

// Draggable Photo Card Component
const DraggablePhotoCard: React.FC<{
   photo: any;
   children: React.ReactNode;
   photoFoldersExist: boolean;
}> = ({ photo, children, photoFoldersExist }) => {
   const [{ isDragging }, drag] = useDrag({
      type: ItemTypes.PHOTO,
      item: () => {
         console.log('🎯 Starting drag for photo:', photo.id);
         return { type: ItemTypes.PHOTO, photoId: photo.id };
      },
      collect: (monitor) => ({
         isDragging: monitor.isDragging(),
      }),
      canDrag: () => {
         console.log('✋ Can drag?', photoFoldersExist);
         return photoFoldersExist;
      },
   });

   if (!photoFoldersExist) {
      return <>{children}</>;
   }

   return (
      <Box
         ref={drag}
         sx={{
            opacity: isDragging ? 0.5 : 1,
            cursor: 'move',
            '&:hover': {
               opacity: isDragging ? 0.5 : 0.9,
            },
         }}
      >
         {children}
      </Box>
   );
};

// Droppable Folder Component
const DroppableFolderCard: React.FC<{
   folder: { id: string; name: string; photoIds: string[] };
   onDrop: (photoId: string, folderId: string) => void;
   children: React.ReactNode;
}> = ({ folder, onDrop, children }) => {
   const [{ isOver, canDrop }, drop] = useDrop({
      accept: ItemTypes.PHOTO,
      drop: (item: DragItem) => {
         console.log('📥 Dropping photo', item.photoId, 'into folder', folder.id, folder.name);
         onDrop(item.photoId, folder.id);
         return { dropped: true };
      },
      hover: (item: DragItem) => {
         console.log('👆 Hovering over folder', folder.name);
      },
      collect: (monitor) => {
         const over = monitor.isOver();
         const can = monitor.canDrop();
         if (over || can) {
            console.log('📍 Folder', folder.name, '- isOver:', over, 'canDrop:', can);
         }
         return {
            isOver: over,
            canDrop: can,
         };
      },
   });

   const isActive = canDrop && isOver;
   const borderColor = isActive ? '#00C4A7' : canDrop ? '#00C4A7' : 'transparent';
   const borderStyle = isActive ? 'solid' : 'dashed';

   return (
      <Box
         ref={drop}
         sx={{
            border: `2px ${borderStyle} ${borderColor}`,
            borderRadius: 2,
            transition: 'all 0.2s ease',
            backgroundColor: isActive ? 'rgba(1, 148, 154, 0.1)' : 'transparent',
            minHeight: '60px',
         }}
      >
         {children}
      </Box>
   );
};

const AlbumDetailPage: React.FC = () => {
   const { albumId } = useParams<{ albumId: string }>();

   // Responsive hooks
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'));
   const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

   const [album, setAlbum] = useState<Album | null>(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);
   const [addPhotosOpen, setAddPhotosOpen] = useState(false);
   const [selectedPhotos, setSelectedPhotos] = useState<any[]>([]);
   const [photoSelectionSet, setPhotoSelectionSet] = useState<Set<string>>(new Set());
   const [addingPhotos, setAddingPhotos] = useState(false);
   const [addError, setAddError] = useState<string | null>(null);
   // Infinite scroll photo fetching
   const {
      photos: albumPhotos,
      loading: loadingAlbumPhotos,
      hasMore: hasMorePhotos,
      error: photosError,
      loadMore: loadMorePhotos,
      reload: reloadPhotos,
   } = useInfiniteScrollPhotos({
      albumId: album?.id,
      pageSize: 10,
      enabled: !!album?.id,
   });

   const isVideo = (photo: { mediaType?: string }) => photo.mediaType === 'video';

   const navigate = useNavigate();

   // Ref for scrollable content container
   const scrollContainerRef = useRef<HTMLDivElement>(null);

   // Member management states
   const [members, setMembers] = useState<AlbumMember[]>([]);
   const [loadingMembers, setLoadingMembers] = useState(false);

   // Participant reminder states
   const [reminderModalOpen, setReminderModalOpen] = useState(false);
   const [selectedPhotosForReminder, setSelectedPhotosForReminder] = useState<Set<string>>(
      new Set()
   );
   const [reminderMessage, setReminderMessage] = useState('');
   const [sendingReminder, setSendingReminder] = useState(false);

   // Direct upload states
   const [directUploadOpen, setDirectUploadOpen] = useState(false);
   const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
   const [uploading, setUploading] = useState(false);
   const [uploadProgress, setUploadProgress] = useState(0);
   const [uploadError, setUploadError] = useState<string | null>(null);
   const [filePreviews, setFilePreviews] = useState<{ [key: string]: string }>({});
   const [batchProgress, setBatchProgress] = useState<BatchUploadProgress | null>(null);
   const [extractedMetadata, setExtractedMetadata] = useState<{
      [fileName: string]: { takenDate: Date | null; location: any };
   }>({});

   // Cover photo management states
   const [photoMenuAnchor, setPhotoMenuAnchor] = useState<null | HTMLElement>(null);
   const [selectedPhotoForMenu, setSelectedPhotoForMenu] = useState<any | null>(null);
   const [settingCover, setSettingCover] = useState(false);

   // Delete photo dialog state
   const [deletePhotoDialogOpen, setDeletePhotoDialogOpen] = useState(false);
   const [deletingPhoto, setDeletingPhoto] = useState(false);

   // Invite dialog state
   const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

   // Album settings update states
   const [updatingSettings, setUpdatingSettings] = useState(false);

   // Cover photo upload states
   const [uploadingCover, setUploadingCover] = useState(false);
   const [coverPhotoFile, setCoverPhotoFile] = useState<File | null>(null);
   const [coverPhotoPreview, setCoverPhotoPreview] = useState<string | null>(null);

   // Sharing dialog state
   const [sharingDialogOpen, setSharingDialogOpen] = useState(false);

   // Photo modal states
   const [modalOpen, setModalOpen] = useState(false);
   const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

   // View All Photos modal state
   const [viewAllPhotosOpen, setViewAllPhotosOpen] = useState(false);
   const [currentPage, setCurrentPage] = useState(1);
   const photosPerPage = 8;

   // Photo organization states
   const [sortBy, setSortBy] = useState<'date-newest' | 'date-oldest' | 'title'>('date-newest');
   const [groupByDate, setGroupByDate] = useState(false);

   // Photo gallery popup states
   const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);
   const [photoGalleryIndex, setPhotoGalleryIndex] = useState(0);

   // Tab state - default to 'grid' for non-owners, 'details' for owners
   const [currentTab, setCurrentTab] = useState<
      | 'details'
      | 'participants'
      | 'grid'
      | 'timeline'
      | 'gallery'
      | 'magazine'
      | 'photoGroups'
      | 'sharing'
      | 'waitlist'
      | 'analytics'
   >('grid');

   // Waiting list states
   const [waitingList, setWaitingList] = useState<any[]>([]);
   const [loadingWaitlist, setLoadingWaitlist] = useState(false);
   const [approvingUsers, setApprovingUsers] = useState<Set<string>>(new Set());
   const [rejectingUsers, setRejectingUsers] = useState<Set<string>>(new Set());
   const [approvingAll, setApprovingAll] = useState(false);
   const [confirmApproveAllOpen, setConfirmApproveAllOpen] = useState(false);

   // Timeline controls state
   const [timelineSort, setTimelineSort] = useState<TimelineSort>('date-newest');
   const [timelineView, setTimelineView] = useState<TimelineView>('detailed');
   const [timelineSpeed, setTimelineSpeed] = useState<TimelineSpeed>('normal');
   const [showLocationOnly, setShowLocationOnly] = useState(false);
   const [showCameraInfoOnly, setShowCameraInfoOnly] = useState(false);
   const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

   // Snackbar state for user feedback
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>(
      'success'
   );

   // QR Code state
   const [showQRCode, setShowQRCode] = useState(false);
   const [qrCodeDataURL, setQrCodeDataURL] = useState<string>('');

   // Connection status states
   const [addingContact, setAddingContact] = useState<string>(''); // Store member ID being added
   const [connectionStatuses, setConnectionStatuses] = useState<
      Map<
         string,
         {
            status: 'none' | 'connected' | 'pending_sent' | 'pending_received';
            requestId?: string;
         }
      >
   >(new Map()); // Track connection status for each member

   // Participant profile modal states
   const [participantProfileModalOpen, setParticipantProfileModalOpen] = useState(false);
   const [selectedParticipant, setSelectedParticipant] = useState<AlbumMember | null>(null);

   // Download states
   const [downloadingPhotos, setDownloadingPhotos] = useState<Set<string>>(new Set());
   const [bulkDownloading, setBulkDownloading] = useState(false);
   const [bulkDownloadProgress, setBulkDownloadProgress] = useState({ current: 0, total: 0 });

   // Prepare entity data for permission checks
   const entityData: EntityData | null = album
      ? {
           id: album.id,
           createdBy: (album as any).createdBy,
           organizerId: undefined, // Albums don't have organizerId
           userId: undefined, // Albums use createdBy instead of userId
           members:
              members?.map((m) => ({
                 userId: m.id,
                 email: m.email,
                 role: m.role as any,
              })) || [],
           participants:
              members?.map((m) => ({
                 userId: m.id,
                 email: m.email,
                 role: m.role as any,
              })) || [],
        }
      : null;

   // Member management states
   const [editMemberDialog, setEditMemberDialog] = useState(false);
   const [memberToEdit, setMemberToEdit] = useState<AlbumMember | null>(null);
   const [deleteMemberDialog, setDeleteMemberDialog] = useState(false);
   const [memberToDelete, setMemberToDelete] = useState<AlbumMember | null>(null);
   const [deletingMember, setDeletingMember] = useState(false);

   // Delete album state
   const [deleteAlbumDialog, setDeleteAlbumDialog] = useState(false);
   const [deletingAlbum, setDeletingAlbum] = useState(false);
   const [newMemberRole, setNewMemberRole] = useState<'owner' | 'editor' | 'viewer'>('viewer');

   // Photo folder/grouping states
   const [photoFolders, setPhotoFolders] = useState<
      Array<{ id: string; name: string; photoIds: string[] }>
   >([]);
   const [folderModalOpen, setFolderModalOpen] = useState(false);
   const [newFolderName, setNewFolderName] = useState('');
   // Track the starting index for pagination in each folder (folderId -> startIndex)
   const [folderPhotoStartIndex, setFolderPhotoStartIndex] = useState<{
      [folderId: string]: number;
   }>({});
   const [editFolderModalOpen, setEditFolderModalOpen] = useState(false);
   const [folderToEdit, setFolderToEdit] = useState<{
      id: string;
      name: string;
      photoIds: string[];
   } | null>(null);
   const [showStickyFolders, setShowStickyFolders] = useState(false);
   const foldersAreaRef = useRef<HTMLDivElement>(null);
   const [editedFolderName, setEditedFolderName] = useState('');
   const [viewFolderModalOpen, setViewFolderModalOpen] = useState(false);
   const [folderToView, setFolderToView] = useState<{
      id: string;
      name: string;
      photoIds: string[];
   } | null>(null);
   const [currentFolderPhotoIndex, setCurrentFolderPhotoIndex] = useState(0);
   const [loadingFolders, setLoadingFolders] = useState(false);
   const [savingFolder, setSavingFolder] = useState(false);

   // Uploader profile popup (same as public album page)
   const [profilePopoverUserId, setProfilePopoverUserId] = useState<string | null>(null);
   const [profilePopoverData, setProfilePopoverData] = useState<{
      displayName?: string;
      photoURL?: string;
      accountType?: string[];
      photographerProfile?: PhotographerProfilePublic | null;
   } | null>(null);
   const [profilePopoverLoading, setProfilePopoverLoading] = useState(false);
   const [profilePopoverMessageMode, setProfilePopoverMessageMode] = useState(false);
   const [profilePopoverMessageText, setProfilePopoverMessageText] = useState('');
   const [profilePopoverSenderEmail, setProfilePopoverSenderEmail] = useState('');
   const [profilePopoverSenderName, setProfilePopoverSenderName] = useState('');
   const [profilePopoverSending, setProfilePopoverSending] = useState(false);
   const [profilePopoverMessageError, setProfilePopoverMessageError] = useState<string | null>(null);

   const { currentUser } = useUser();

   // Determine current user's role in the album
   const getCurrentUserRole = () => {
      if (!currentUser || !album) {
         console.log('🔍 getCurrentUserRole: No user or album', {
            hasUser: !!currentUser,
            hasAlbum: !!album,
         });
         return null;
      }

      console.log('🔍 getCurrentUserRole: Starting check', {
         currentUserId: currentUser.uid,
         albumCreatedBy: album.createdBy,
         isCreator: album.createdBy === currentUser.uid,
         hasMembers: !!album.members,
         membersCount: album.members?.length || 0,
         hasMemberIds: !!album.memberIds,
      });

      // Check if user is the album creator/owner
      if (album.createdBy === currentUser.uid) {
         console.log('✅ User is album creator/owner');
         return 'owner';
      }

      // Check in members array
      if (album.members && Array.isArray(album.members)) {
         console.log(
            '🔍 Checking members array:',
            album.members.map((m) => ({ userId: m.userId, role: m.role }))
         );
         const userMember = album.members.find((member) => member.userId === currentUser.uid);
         if (userMember) {
            // Normalize role to lowercase and trim whitespace
            const role = userMember?.role?.toLowerCase().trim() || null;
            console.log('✅ User member found:', {
               userId: currentUser.uid,
               rawRole: userMember?.role,
               normalizedRole: role,
               fullMember: userMember,
            });
            return role;
         } else {
            console.log('❌ User not found in members array');
         }
      }

      // Check in legacy memberIds array
      if (album.memberIds && Array.isArray(album.memberIds)) {
         const isMember = album.memberIds.includes(currentUser.uid);
         console.log('🔍 Checking legacy memberIds:', { isMember, memberIds: album.memberIds });
         return isMember ? 'viewer' : null; // Default to viewer for legacy members
      }

      console.log('❌ User has no role in this album');
      return null;
   };

   const userRole = getCurrentUserRole();
   const isOwner = userRole === 'owner';
   const isPublicAlbum = album?.isPublic === true;

   // Debug logging for role
   useEffect(() => {
      if (album && currentUser) {
         console.log('='.repeat(80));
         console.log('🔍 ALBUM ROLE SUMMARY:');
         console.log('='.repeat(80));
         console.log('Current User ID:', currentUser.uid);
         console.log('Album Created By:', album.createdBy);
         console.log('Detected User Role:', userRole);
         console.log('Is Owner:', isOwner);
         console.log(
            'Album Members:',
            album.members?.map((m) => `${m.displayName || m.email} (${m.role})`).join(', ')
         );
         console.log('Should Show Photo Tabs:', userRole === 'owner' || userRole === 'editor');
         console.log('Should HIDE Photo Tabs (is viewer):', userRole === 'viewer');
         console.log('='.repeat(80));
      }
   }, [album, currentUser, userRole, isOwner]);

   // Fetch public profile when uploader is clicked (profile popup)
   useEffect(() => {
      if (!profilePopoverUserId) {
         setProfilePopoverData(null);
         return;
      }
      let cancelled = false;
      setProfilePopoverLoading(true);
      setProfilePopoverData(null);
      fetch(`${process.env.REACT_APP_API_URL}/api/profile/public/${profilePopoverUserId}`)
         .then((res) => (res.ok ? res.json() : null))
         .then((data) => {
            if (!cancelled && data) setProfilePopoverData(data);
         })
         .catch(() => {
            if (!cancelled) setProfilePopoverData(null);
         })
         .finally(() => {
            if (!cancelled) setProfilePopoverLoading(false);
         });
      return () => {
         cancelled = true;
      };
   }, [profilePopoverUserId]);

   // Check for pending photo download after payment redirect
   useEffect(() => {
      const checkPendingPhotoDownload = () => {
         const pendingPhotoDownload = localStorage.getItem('pendingPhotoDownload');
         if (pendingPhotoDownload) {
            try {
               const downloadData = JSON.parse(pendingPhotoDownload);
               // Check if this is for the current album and not too old (within 5 minutes)
               if (downloadData.albumId === albumId && Date.now() - downloadData.timestamp < 300000) {
                  setSnackbarMessage('Payment successful! Downloading your photo...');
                  setSnackbarSeverity('success');
                  setSnackbarOpen(true);
                  
                  // Find the photo and auto-download it
                  setTimeout(() => {
                     const photo = albumPhotos.find(p => p.id === downloadData.photoId);
                     if (photo) {
                        handleDownloadPhoto(photo);
                     }
                  }, 1000); // Small delay to show the success message first
               }
               // Clear the pending download data
               localStorage.removeItem('pendingPhotoDownload');
            } catch (error) {
               console.error('Error parsing pending photo download:', error);
               localStorage.removeItem('pendingPhotoDownload');
            }
         }
      };

      if (albumId && albumPhotos.length > 0) {
         checkPendingPhotoDownload();
      }
   }, [albumId, albumPhotos]);

   // Invite permissions:
   // - Owner can always invite
   // - Participants can invite if enableParticipantInvite is true (regardless of public/private)
   const canInvite = isOwner || album?.enableParticipantInvite === true;

   // Upload permissions:
   // - Owner can always upload
   // - Participants can upload if enableParticipantUpload is true (regardless of public/private)
   // Editors and Owners can upload, plus participants if enableParticipantUpload is true
   const canUpload = isOwner || userRole === 'editor' || album?.enableParticipantUpload === true;

   // Photo selection functions for sharing feature
   const togglePhotoSelection = (photoId: string) => {
      setPhotoSelectionSet((prev) => {
         const newSet = new Set(prev);
         if (newSet.has(photoId)) {
            newSet.delete(photoId);
         } else {
            newSet.add(photoId);
         }
         return newSet;
      });
   };

   // Individual photo sharing function
   const [shareModalOpen, setShareModalOpen] = useState(false);
   const [photoToShare, setPhotoToShare] = useState<any>(null);

   const handleShareSinglePhoto = (photo: any) => {
      setPhotoToShare(photo);
      setShareModalOpen(true);
   };

   const handlePerPhotoPayment = async (photoId: string) => {
      if (!currentUser || !albumId || !album) return;

      setDownloadingPhotos((prev) => new Set(prev).add(photoId));
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/create-payment-session`,
            {
               method: 'POST',
               headers: {
                  ...headers,
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  userId: currentUser.uid,
                  email: currentUser.email,
                  albumTitle: album.title,
                  albumId: albumId,
                  photoId,
                  amount: album.pricePerPhoto || 5.0,
                  pricingModel: 'per_photo',
                  returnUrl: `${window.location.origin}/albums/${albumId}`,
                  cancelUrl: `${window.location.origin}/albums/${albumId}`,
               }),
            }
         );

         const result = await response.json();

         if (response.ok && result.sessionUrl) {
            // Store photo download intent in localStorage
            localStorage.setItem('pendingPhotoDownload', JSON.stringify({
               albumId,
               photoId,
               paymentType: 'per_photo',
               timestamp: Date.now(),
            }));
            
            // Redirect to Stripe checkout
            window.location.href = result.sessionUrl;
         } else {
            setSnackbarMessage(result.message || 'Failed to create payment session');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setDownloadingPhotos((prev) => {
               const newSet = new Set(prev);
               newSet.delete(photoId);
               return newSet;
            });
         }
      } catch (error) {
         console.error('Error creating payment session:', error);
         setSnackbarMessage('An error occurred. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         setDownloadingPhotos((prev) => {
            const newSet = new Set(prev);
            newSet.delete(photoId);
            return newSet;
         });
      }
   };

   const handleDownloadPhoto = async (photo: any) => {
      try {
         console.log('Downloading photo:', photo.id);

         // Check if this is a pay-per-photo album AND user is not the owner
         if (album?.type === 'paid' && album?.pricingModel === 'per_photo' && userRole !== 'owner') {
            console.log('💳 Pay-per-photo album - checking payment status...');
            
            // Check if user has already paid for this photo
            try {
               const headers = await getAuthHeaders();
               const paymentCheckResponse = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/check-photo-payment/${photo.id}`,
                  { headers }
               );
               
               if (paymentCheckResponse.ok) {
                  const paymentData = await paymentCheckResponse.json();
                  if (!paymentData.hasPaid) {
                     // User hasn't paid yet, redirect to payment
                     console.log('❌ User has not paid for this photo - redirecting to payment');
                     await handlePerPhotoPayment(photo.id);
                     return;
                  }
                  console.log('✅ User has already paid for this photo - proceeding with download');
                  // User has already paid, proceed with download
               } else {
                  // If check fails, require payment to be safe
                  console.log('⚠️ Payment check failed - requiring payment');
                  await handlePerPhotoPayment(photo.id);
                  return;
               }
            } catch (error) {
               console.error('Error checking photo payment:', error);
               await handlePerPhotoPayment(photo.id);
               return;
            }
         }

         // Add photo to downloading set
         setDownloadingPhotos((prev) => new Set(prev).add(photo.id));

         const headers = await getAuthHeaders();
         
         // Track download activity in background (non-blocking)
         const trackDownload = async () => {
            try {
               console.log('📊 [AlbumDetail] Calling track-download API...');
               console.log('📊 [AlbumDetail] Album ID:', albumId);
               console.log('📊 [AlbumDetail] Photo ID:', photo.id);
               console.log('📊 [AlbumDetail] API URL:', `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/track-download`);
               
               const trackHeaders = await getAuthHeaders();
               const trackResponse = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/track-download`,
                  {
                     method: 'POST',
                     headers: {
                        ...trackHeaders,
                        'Content-Type': 'application/json',
                     },
                     body: JSON.stringify({ photoId: photo.id }),
                  }
               );

               console.log('📊 [AlbumDetail] Track response status:', trackResponse.status);

               if (trackResponse.ok) {
                  const trackData = await trackResponse.json();
                  console.log('✅ [AlbumDetail] Download tracked successfully:', trackData);
                  
                  // Check if photo was already downloaded
                  if (trackData.alreadyDownloaded) {
                     console.log('ℹ️ [AlbumDetail] Photo already downloaded, showing view button');
                     return { alreadyDownloaded: true };
                  }
               } else {
                  const errorText = await trackResponse.text();
                  console.error('❌ [AlbumDetail] Failed to track download:', trackResponse.status, errorText);
               }
               return { alreadyDownloaded: false };
            } catch (error) {
               console.error('❌ [AlbumDetail] Could not track download:', error);
            }
         };

         // Start tracking in background and check if already downloaded
         console.log('📊 [AlbumDetail] Starting download tracking...');
         const trackResult = await trackDownload();
         
         // If already downloaded, show message with view button and stop
         if (trackResult?.alreadyDownloaded) {
            setSnackbarMessage('You have already downloaded this photo');
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
            // Remove photo from downloading set
            setDownloadingPhotos((prev) => {
               const newSet = new Set(prev);
               newSet.delete(photo.id);
               return newSet;
            });
            return;
         }

         // Get signed download URL from backend
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/photos/${photo.id}/download`,
            {
               headers,
            }
         );

         console.log('Response status:', response.status);

         if (!response.ok) {
            let errorMessage = 'Failed to download photo';
            try {
               const errorData = await response.json();
               errorMessage = errorData.message || errorMessage;
            } catch (e) {
               errorMessage = response.statusText || errorMessage;
            }
            throw new Error(errorMessage);
         }

         console.log('Streaming photo from server...');

         // Get filename from Content-Disposition header or use default
         const contentDisposition = response.headers.get('content-disposition');
         const isVideoFile = photo.mediaType === 'video';
         let fileName = photo.fileName || (isVideoFile ? `video-${photo.id}.mp4` : `photo-${photo.id}.jpg`);
         if (contentDisposition) {
            const match = contentDisposition.match(/filename="(.+)"/);
            if (match && match[1]) {
               fileName = match[1];
            }
         }

         // Get the blob from streamed response
         const blob = await response.blob();
         console.log(`Downloaded blob: ${blob.size} bytes, type: ${blob.type}`);
         
         // Create temporary download link and trigger immediate download
         const blobUrl = URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = blobUrl;
         link.download = fileName;
         link.style.display = 'none';
         
         // Trigger download immediately
         document.body.appendChild(link);
         link.click();
         
         // Clean up immediately
         document.body.removeChild(link);
         URL.revokeObjectURL(blobUrl);

         setSnackbarMessage(photo.mediaType === 'video' ? 'Video downloaded successfully!' : 'Photo downloaded successfully!');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error: any) {
         console.error('Error downloading photo:', error);
         setSnackbarMessage(error.message || 'Failed to download photo');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         // Remove photo from downloading set
         setDownloadingPhotos((prev) => {
            const newSet = new Set(prev);
            newSet.delete(photo.id);
            return newSet;
         });
      }
   };

   // Folder management handlers
   const handleCreateFolder = async () => {
      if (!newFolderName.trim()) {
         setSnackbarMessage('Please enter a folder name');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSavingFolder(true);
      try {
         const newFolder = {
            id: `folder-${Date.now()}`,
            name: newFolderName.trim(),
            photoIds: [],
         };

         // Save to database
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/folders`,
            {
               method: 'POST',
               headers: {
                  ...headers,
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify(newFolder),
            }
         );

         if (!response.ok) throw new Error('Failed to create folder');

         const savedFolder = await response.json();

         setPhotoFolders((prev) => [...prev, savedFolder]);
         setNewFolderName('');
         setFolderModalOpen(false);
         setSnackbarMessage('Folder created successfully!');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Error creating folder:', error);
         setSnackbarMessage('Failed to create folder');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSavingFolder(false);
      }
   };

   const handleEditFolder = (folder: { id: string; name: string; photoIds: string[] }) => {
      setFolderToEdit(folder);
      setEditedFolderName(folder.name);
      setEditFolderModalOpen(true);
   };

   const handleSaveEditedFolder = async () => {
      if (!editedFolderName.trim() || !folderToEdit) {
         setSnackbarMessage('Please enter a folder name');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSavingFolder(true);
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/folders/${folderToEdit.id}`,
            {
               method: 'PUT',
               headers: {
                  ...headers,
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({ name: editedFolderName.trim() }),
            }
         );

         if (!response.ok) throw new Error('Failed to update folder');

         setPhotoFolders((prev) =>
            prev.map((folder) =>
               folder.id === folderToEdit.id ? { ...folder, name: editedFolderName.trim() } : folder
            )
         );

         setEditFolderModalOpen(false);
         setFolderToEdit(null);
         setEditedFolderName('');
         setSnackbarMessage('Folder renamed successfully!');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Error updating folder:', error);
         setSnackbarMessage('Failed to rename folder');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSavingFolder(false);
      }
   };

   const handleViewFolder = (folder: { id: string; name: string; photoIds: string[] }) => {
      setFolderToView(folder);
      setCurrentFolderPhotoIndex(0);
      setViewFolderModalOpen(true);
   };

   const handleNextFolderPhoto = () => {
      if (folderToView && currentFolderPhotoIndex < folderToView.photoIds.length - 1) {
         setCurrentFolderPhotoIndex((prev) => prev + 1);
      }
   };

   const handlePrevFolderPhoto = () => {
      if (currentFolderPhotoIndex > 0) {
         setCurrentFolderPhotoIndex((prev) => prev - 1);
      }
   };

   const handleDropToFolder = async (photoId: string, folderId: string) => {
      console.log('🎯 handleDropToFolder called:', { photoId, folderId });

      if (!photoId) {
         console.warn('⚠️ No photoId provided');
         return;
      }

      const targetFolder = photoFolders.find((f) => f.id === folderId);
      console.log('📁 Target folder:', targetFolder);

      const updatedFolders = photoFolders.map((folder) => {
         // Remove photo from all folders first (prevents duplicates)
         const updatedFolder = {
            ...folder,
            photoIds: folder.photoIds.filter((id) => id !== photoId),
         };
         // Add to target folder if it's not already there
         if (folder.id === folderId) {
            // Only add if not already present (extra safety check)
            if (!updatedFolder.photoIds.includes(photoId)) {
               updatedFolder.photoIds.push(photoId);
               console.log('✅ Added photo', photoId, 'to folder', folder.name);
            } else {
               console.log('⚠️ Photo', photoId, 'already in folder', folder.name);
            }
         }
         return updatedFolder;
      });

      setPhotoFolders(updatedFolders);

      // Save to database
      try {
         const headers = await getAuthHeaders();
         await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/folders/${folderId}/photos`,
            {
               method: 'PUT',
               headers: {
                  ...headers,
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  photoIds: updatedFolders.find((f) => f.id === folderId)?.photoIds || [],
               }),
            }
         );

         setSnackbarMessage(`Photo moved to ${targetFolder?.name || 'folder'}`);
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Error updating folder photos:', error);
         setSnackbarMessage('Failed to move photo');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }
   };

   const handleDeleteFolder = async (folderId: string) => {
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/folders/${folderId}`,
            {
               method: 'DELETE',
               headers,
            }
         );

         const data = await response.json();

         if (!response.ok) {
            // Show specific error message from backend
            setSnackbarMessage(data.error || data.message || 'Failed to delete folder');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
         }

         setPhotoFolders((prev) => prev.filter((folder) => folder.id !== folderId));
         setSnackbarMessage('Folder deleted successfully');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Error deleting folder:', error);
         setSnackbarMessage('Failed to delete folder');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }
   };

   const handleRemovePhotoFromFolder = async (folderId: string, photoId: string) => {
      console.log('🗑️ Removing photo from folder:', { photoId, folderId });

      const updatedFolders = photoFolders.map((folder) => {
         if (folder.id === folderId) {
            const beforeCount = folder.photoIds.length;
            const filtered = folder.photoIds.filter((id) => id !== photoId);
            console.log(`📊 Folder ${folder.name}: ${beforeCount} → ${filtered.length} photos`);
            return {
               ...folder,
               photoIds: filtered,
            };
         }
         return folder;
      });

      setPhotoFolders(updatedFolders);

      // Adjust start index if we removed the last photo on the current page
      const folder = updatedFolders.find((f) => f.id === folderId);
      if (folder) {
         const currentStartIndex = folderPhotoStartIndex[folderId] || 0;
         const maxPhotosPerPage = 4;
         // If current start index is beyond the new length, adjust it
         if (currentStartIndex >= folder.photoIds.length && currentStartIndex > 0) {
            setFolderPhotoStartIndex((prev: { [folderId: string]: number }) => ({
               ...prev,
               [folderId]: Math.max(
                  0,
                  Math.floor((folder.photoIds.length - 1) / maxPhotosPerPage) * maxPhotosPerPage
               ),
            }));
         }
      }

      // Save to database
      try {
         const headers = await getAuthHeaders();
         await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/folders/${folderId}/photos`,
            {
               method: 'PUT',
               headers: {
                  ...headers,
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  photoIds: updatedFolders.find((f) => f.id === folderId)?.photoIds || [],
               }),
            }
         );

         setSnackbarMessage('Photo removed from folder');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Error removing photo from folder:', error);
         setSnackbarMessage('Failed to remove photo');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }
   };

   // Navigate to previous set of photos in folder
   const handleFolderPhotosPrev = (folderId: string) => {
      const maxPhotosPerPage = 4;
      setFolderPhotoStartIndex((prev: { [folderId: string]: number }) => ({
         ...prev,
         [folderId]: Math.max(0, (prev[folderId] || 0) - maxPhotosPerPage),
      }));
   };

   // Navigate to next set of photos in folder
   const handleFolderPhotosNext = (folderId: string, totalPhotos: number) => {
      const maxPhotosPerPage = 4;
      const currentStart = folderPhotoStartIndex[folderId] || 0;
      if (currentStart + maxPhotosPerPage < totalPhotos) {
         setFolderPhotoStartIndex((prev: { [folderId: string]: number }) => ({
            ...prev,
            [folderId]: currentStart + maxPhotosPerPage,
         }));
      }
   };

   // Load folders from database
   const loadFolders = async () => {
      if (!albumId) return;

      setLoadingFolders(true);
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/folders`,
            {
               method: 'GET',
               headers,
            }
         );

         if (response.ok) {
            const folders = await response.json();

            // Deduplicate photo IDs within each folder
            const deduplicatedFolders = folders.map((folder: any) => ({
               ...folder,
               photoIds: Array.from(new Set(folder.photoIds || [])), // Remove duplicates using Set
            }));

            console.log('📁 Loaded folders from DB:', {
               folderCount: deduplicatedFolders.length,
               folders: deduplicatedFolders.map((f: any) => ({
                  name: f.name,
                  photoCount: f.photoIds.length,
               })),
            });

            setPhotoFolders(deduplicatedFolders);
         }
      } catch (error) {
         console.error('Error loading folders:', error);
      } finally {
         setLoadingFolders(false);
      }
   };

   // Load folders when album is loaded
   useEffect(() => {
      if (album && albumId) {
         loadFolders();
      }
   }, [album?.id, albumId]);

   // Get photos that are not in any folder - using Set for O(1) lookup
   const getUngroupedPhotos = useMemo(() => {
      // Create a Set of all photo IDs that are in folders for fast lookup
      const folderPhotoIdsSet = new Set<string>();
      photoFolders.forEach((folder) => {
         folder.photoIds.forEach((id) => folderPhotoIdsSet.add(id));
      });

      // Filter out photos that are in any folder
      const ungrouped = albumPhotos.filter((photo) => !folderPhotoIdsSet.has(photo.id));

      console.log('📁 Folder stats:', {
         totalPhotos: albumPhotos.length,
         photosInFolders: folderPhotoIdsSet.size,
         ungroupedPhotos: ungrouped.length,
         folders: photoFolders.length,
      });

      return ungrouped;
   }, [albumPhotos, photoFolders]);

   const handleBulkDownload = async () => {
      const selectedPhotos = albumPhotos.filter((photo) => photoSelectionSet.has(photo.id));

      if (selectedPhotos.length === 0) {
         setSnackbarMessage('Please select photos to download');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      if (selectedPhotos.length > 5) {
         setSnackbarMessage('You can download a maximum of 5 photos at a time');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      try {
         setBulkDownloading(true);
         setBulkDownloadProgress({ current: 0, total: selectedPhotos.length });

         setSnackbarMessage(`Starting bulk download of ${selectedPhotos.length} photos...`);
         setSnackbarSeverity('success');
         setSnackbarOpen(true);

         // Download photos one by one
         for (let i = 0; i < selectedPhotos.length; i++) {
            const photo = selectedPhotos[i];

            // Add to downloading set
            setDownloadingPhotos((prev) => new Set(prev).add(photo.id));
            setBulkDownloadProgress({ current: i + 1, total: selectedPhotos.length });

            try {
               // Track download analytics (non-blocking)
               const trackDownload = async () => {
                  try {
                     console.log('📊 [BulkDownload] Calling track-download API...');
                     console.log('📊 [BulkDownload] Album ID:', albumId);
                     console.log('📊 [BulkDownload] Photo ID:', photo.id);
                     
                     const trackHeaders = await getAuthHeaders();
                     const trackResponse = await fetch(
                        `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/track-download`,
                        {
                           method: 'POST',
                           headers: {
                              ...trackHeaders,
                              'Content-Type': 'application/json',
                           },
                           body: JSON.stringify({ photoId: photo.id }),
                        }
                     );

                     console.log('📊 [BulkDownload] Track response status:', trackResponse.status);

                     if (trackResponse.ok) {
                        const trackData = await trackResponse.json();
                        console.log('✅ [BulkDownload] Download tracked:', trackData);
                     } else {
                        console.warn('Failed to track download analytics');
                     }
                  } catch (error) {
                     console.warn('Analytics tracking failed:', error);
                  }
               };

               trackDownload();

               // Download the photo
               let response;

               try {
                  // Try direct Firebase Storage URL first
                  if (photo.url) {
                     response = await fetch(photo.url, {
                        method: 'GET',
                        mode: 'cors',
                        credentials: 'omit',
                     });

                     if (!response.ok) {
                        throw new Error(`Direct URL failed: ${response.status}`);
                     }
                  } else {
                     throw new Error('No photo URL available');
                  }
               } catch (error) {
                  // Fallback to server endpoint
                  const downloadUrl = `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/photos/${photo.id}/download`;
                  const headers = await getAuthHeaders();

                  response = await fetch(downloadUrl, {
                     method: 'GET',
                     headers,
                  });

                  if (!response.ok) {
                     throw new Error(`Server endpoint failed: ${response.status}`);
                  }
               }

               const blob = await response.blob();
               const url = window.URL.createObjectURL(blob);
               const link = document.createElement('a');
               link.href = url;
               link.download = photo.title || photo.fileName || (photo.mediaType === 'video' ? `video-${photo.id}.mp4` : `photo-${photo.id}.jpg`);
               link.style.display = 'none';
               document.body.appendChild(link);
               link.click();
               document.body.removeChild(link);
               window.URL.revokeObjectURL(url);

               console.log(`Downloaded photo ${i + 1}/${selectedPhotos.length}: ${photo.id}`);
            } catch (error) {
               console.error(`Error downloading photo ${photo.id}:`, error);
            } finally {
               // Remove from downloading set
               setDownloadingPhotos((prev) => {
                  const newSet = new Set(prev);
                  newSet.delete(photo.id);
                  return newSet;
               });
            }
         }

         setSnackbarMessage(`Bulk download completed! Downloaded ${selectedPhotos.length} photos.`);
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Bulk download error:', error);
         setSnackbarMessage(
            `Bulk download failed: ${error instanceof Error ? error.message : 'Unknown error'}`
         );
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setBulkDownloading(false);
         setBulkDownloadProgress({ current: 0, total: 0 });
      }
   };

   const selectAllPhotos = () => {
      if (photoSelectionSet.size === albumPhotos.length) {
         // Deselect all
         setPhotoSelectionSet(new Set());
      } else {
         // Select all
         setPhotoSelectionSet(new Set(albumPhotos.map((photo) => photo.id)));
      }
   };

   // Function to fetch connection statuses for all members
   const fetchConnectionStatuses = useCallback(async () => {
      console.log('🔍 fetchConnectionStatuses called');
      if (!currentUser || !members || members.length === 0) {
         console.log('❌ No current user or members, returning');
         return;
      }
      console.log(`👥 Checking connection status for ${members.length} members`);

      try {
         const statusMap = new Map<
            string,
            {
               status: 'none' | 'connected' | 'pending_sent' | 'pending_received';
               requestId?: string;
            }
         >();

         // Check relationships (connected)
         const relationshipNetworkRef = collection(db, 'relationship_network');
         const relationshipQuery = query(
            relationshipNetworkRef,
            where('userId', '==', currentUser.uid)
         );
         const relationshipSnapshot = await getDocs(relationshipQuery);

         const connectedEmails = new Set<string>();
         relationshipSnapshot.forEach((doc) => {
            const data = doc.data();
            if (data.relationToEmail) {
               connectedEmails.add(data.relationToEmail);
            }
         });

         // Check pending requests
         const networkRequestsRef = collection(db, 'networkRequests');

         // Requests I sent
         const sentQuery = query(
            networkRequestsRef,
            where('fromUserId', '==', currentUser.uid),
            where('status', '==', 'pending')
         );
         const sentSnapshot = await getDocs(sentQuery);
         const sentRequestMap = new Map<string, string>(); // email -> requestId
         sentSnapshot.forEach((doc) => {
            const data = doc.data();
            if (data.toUserEmail) {
               sentRequestMap.set(data.toUserEmail, doc.id);
            }
         });

         // Requests I received
         const receivedByIdQuery = query(
            networkRequestsRef,
            where('toUserId', '==', currentUser.uid),
            where('status', '==', 'pending')
         );
         const receivedByIdSnapshot = await getDocs(receivedByIdQuery);
         const receivedRequestMap = new Map<string, string>(); // email -> requestId
         receivedByIdSnapshot.forEach((doc) => {
            const data = doc.data();
            if (data.fromUserEmail) {
               receivedRequestMap.set(data.fromUserEmail, doc.id);
            }
         });

         // Also check by email
         if (currentUser.email) {
            const receivedByEmailQuery = query(
               networkRequestsRef,
               where('toUserEmail', '==', currentUser.email),
               where('status', '==', 'pending')
            );
            const receivedByEmailSnapshot = await getDocs(receivedByEmailQuery);
            receivedByEmailSnapshot.forEach((doc) => {
               const data = doc.data();
               if (data.fromUserEmail && !receivedRequestMap.has(data.fromUserEmail)) {
                  receivedRequestMap.set(data.fromUserEmail, doc.id);
               }
            });
         }

         // Map statuses for each member
         members.forEach((member) => {
            if (!member.email || member.id === currentUser.uid) return;

            if (connectedEmails.has(member.email)) {
               statusMap.set(member.id, { status: 'connected' });
            } else if (sentRequestMap.has(member.email)) {
               statusMap.set(member.id, {
                  status: 'pending_sent',
                  requestId: sentRequestMap.get(member.email),
               });
            } else if (receivedRequestMap.has(member.email)) {
               statusMap.set(member.id, {
                  status: 'pending_received',
                  requestId: receivedRequestMap.get(member.email),
               });
            } else {
               statusMap.set(member.id, { status: 'none' });
            }
         });

         console.log(
            '📊 Connection statuses:',
            Array.from(statusMap.entries()).map(([id, status]) => {
               const member = members.find((m) => m.id === id);
               return {
                  memberId: id,
                  memberEmail: member?.email,
                  memberName: member?.displayName,
                  status: status.status,
                  requestId: status.requestId,
               };
            })
         );

         setConnectionStatuses(statusMap);
      } catch (error) {
         console.error('❌ Error fetching connection statuses:', error);
      }
   }, [currentUser, members]);

   // Connection statuses are now fetched from backend with album data
   // No need for separate useEffect - statuses are loaded in loadAlbumMembers

   // Function to add participant to relationship network
   const addToContacts = async (member: AlbumMember) => {
      if (!currentUser || addingContact === member.id) return;

      if (!member.email) {
         setSnackbarMessage('Cannot send connection request: member email is missing');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setAddingContact(member.id);

      try {
         // Check if relationship or pending request already exists
         const relationshipNetworkRef = collection(db, 'relationship_network');
         const relationshipQuery = query(
            relationshipNetworkRef,
            where('userId', '==', currentUser.uid),
            where('relationToEmail', '==', member.email)
         );

         const relationshipSnapshot = await getDocs(relationshipQuery);

         if (!relationshipSnapshot.empty) {
            setSnackbarMessage('You are already connected with this person');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setAddingContact('');
            return;
         }

         // Check for pending requests
         const networkRequestsRef = collection(db, 'networkRequests');
         const pendingQuery = query(
            networkRequestsRef,
            where('fromUserId', '==', currentUser.uid),
            where('toUserEmail', '==', member.email),
            where('status', '==', 'pending')
         );

         const pendingSnapshot = await getDocs(pendingQuery);

         if (!pendingSnapshot.empty) {
            setSnackbarMessage('You already have a pending connection request with this person');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            setAddingContact('');
            return;
         }

         // Get current user's profile data
         const userProfileDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
         const userProfileData = userProfileDoc.exists() ? userProfileDoc.data() : {};

         // Send connection request via API
         const headers = await getAuthHeaders();
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/network/invite`, {
            method: 'POST',
            headers: {
               ...headers,
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               fromUserId: currentUser.uid,
               fromUserName: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
               fromUserEmail: currentUser.email,
               fromUserPhoto: currentUser.photoURL || '',
               fromUserTitle: userProfileData.title || '',
               fromUserCompany: userProfileData.companyName || '',
               toUserEmail: member.email,
               relationshipType: 'friend',
               relationshipLabel: 'Album Collaborator',
               message: `I'd like to connect with you from the album "${album?.title || 'Shared Album'}"`,
               notes: `Connected through album: ${album?.title || 'Shared Album'}`,
            }),
         });

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to send connection request');
         }

         const result = await response.json();

         // Update connection status to pending_sent
         setConnectionStatuses((prev) => {
            const newMap = new Map(prev);
            newMap.set(member.id, {
               status: 'pending_sent',
               requestId: result.requestId,
            });
            return newMap;
         });

         setSnackbarMessage(
            `Connection request sent to ${member.displayName || member.email || 'the user'}! They'll receive an email notification.`
         );
         setSnackbarSeverity('success');
         setSnackbarOpen(true);

         console.log('✅ Connection request sent:', result);
      } catch (error) {
         console.error('Error sending connection request:', error);
         setSnackbarMessage(
            error instanceof Error
               ? error.message
               : 'Failed to send connection request. Please try again.'
         );
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setAddingContact('');
      }
   };

   // Function to accept connection request
   const handleAcceptConnection = async (member: AlbumMember, requestId: string) => {
      if (!currentUser) return;

      setAddingContact(member.id);

      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/network/accept/${requestId}`,
            {
               method: 'POST',
               headers: {
                  ...headers,
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({ userId: currentUser.uid }),
            }
         );

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to accept connection request');
         }

         // Update connection status to connected
         setConnectionStatuses((prev) => {
            const newMap = new Map(prev);
            newMap.set(member.id, { status: 'connected' });
            return newMap;
         });

         setSnackbarMessage(`You are now connected with ${member.displayName || member.email}!`);
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Error accepting connection:', error);
         setSnackbarMessage(
            error instanceof Error ? error.message : 'Failed to accept connection request.'
         );
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setAddingContact('');
      }
   };

   // Function to decline connection request
   const handleDeclineConnection = async (member: AlbumMember, requestId: string) => {
      if (!currentUser) return;

      setAddingContact(member.id);

      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/network/decline/${requestId}`,
            {
               method: 'POST',
               headers: {
                  ...headers,
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({ userId: currentUser.uid }),
            }
         );

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to decline connection request');
         }

         // Update connection status to none
         setConnectionStatuses((prev) => {
            const newMap = new Map(prev);
            newMap.set(member.id, { status: 'none' });
            return newMap;
         });

         setSnackbarMessage('Connection request declined.');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Error declining connection:', error);
         setSnackbarMessage(
            error instanceof Error ? error.message : 'Failed to decline connection request.'
         );
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setAddingContact('');
      }
   };

   // Function to handle editing member role
   const handleEditMember = (member: AlbumMember) => {
      setMemberToEdit(member);
      setNewMemberRole(member.role);
      setEditMemberDialog(true);
   };

   // Function to handle deleting member
   const handleDeleteMember = (member: AlbumMember) => {
      setMemberToDelete(member);
      setDeleteMemberDialog(true);
   };

   // Function to confirm role change
   const confirmMemberRoleChange = async () => {
      if (!memberToEdit || !album) return;

      try {
         const headers = await getAuthHeaders();

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${album.id}/update-member-role`,
            {
               method: 'PUT',
               headers: {
                  ...headers,
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  memberEmail: memberToEdit.email,
                  newRole: newMemberRole,
               }),
            }
         );

         if (response.ok) {
            setSnackbarMessage('Member role updated successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            // Refresh album data to update member list from server
            const albumResponse = await fetch(
               `${process.env.REACT_APP_API_URL}/api/albums/${album.id}`,
               {
                  headers: await getAuthHeaders(),
               }
            );

            if (albumResponse.ok) {
               const albumData = await albumResponse.json();
               setAlbum(albumData.album);
               // Reload members from the fresh album data
               loadAlbumMembers(albumData.album);
            }
         } else {
            throw new Error('Failed to update member role');
         }
      } catch (error) {
         console.error('Error updating member role:', error);
         setSnackbarMessage('Failed to update member role');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }

      setEditMemberDialog(false);
      setMemberToEdit(null);
   };

   // Function to confirm member deletion
   const confirmMemberDeletion = async () => {
      if (!memberToDelete || !album) return;

      setDeletingMember(true);
      try {
         const headers = await getAuthHeaders();

         console.log('🗑️ Removing member:', {
            userId: memberToDelete.id,
            email: memberToDelete.email,
            displayName: memberToDelete.displayName,
         });

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${album.id}/remove-member`,
            {
               method: 'DELETE',
               headers: {
                  ...headers,
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  userId: memberToDelete.id,
               }),
            }
         );

         if (response.ok) {
            console.log('✅ Member removal API successful');

            // Refresh album data to update member list from server
            const albumResponse = await fetch(
               `${process.env.REACT_APP_API_URL}/api/albums/${album.id}`,
               {
                  headers: await getAuthHeaders(),
               }
            );

            if (albumResponse.ok) {
               const albumData = await albumResponse.json();
               console.log('📦 Refetched album data after removal:', {
                  albumId: albumData.album?.id,
                  membersCount: albumData.album?.members?.length,
                  members: albumData.album?.members,
               });

               // Update album state
               setAlbum(albumData.album);

               // Reload members from the fresh album data - this updates the UI
               console.log('🔄 Calling loadAlbumMembers with updated album data...');
               await loadAlbumMembers(albumData.album);

               console.log('✅ Members list should be updated in UI now');
            } else {
               console.error('❌ Failed to refetch album data:', albumResponse.status);
            }

            setSnackbarMessage('Member removed successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
         } else {
            const errorText = await response.text();
            console.error('❌ Member removal failed:', errorText);
            throw new Error('Failed to remove member');
         }
      } catch (error) {
         console.error('Error removing member:', error);
         setSnackbarMessage('Failed to remove member');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setDeletingMember(false);
         setDeleteMemberDialog(false);
         setMemberToDelete(null);
      }
   };

   // Function to delete the entire album
   const handleDeleteAlbum = async () => {
      if (!album || !currentUser) return;

      setDeletingAlbum(true);
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${album.id}?userId=${currentUser.uid}`,
            {
               method: 'DELETE',
               headers,
            }
         );

         if (response.ok) {
            setSnackbarMessage('Album deleted successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            // Redirect to albums list after brief delay
            setTimeout(() => {
               navigate('/albums');
            }, 1500);
         } else {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to delete album');
         }
      } catch (error) {
         console.error('Error deleting album:', error);
         setSnackbarMessage(error instanceof Error ? error.message : 'Failed to delete album');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setDeletingAlbum(false);
         setDeleteAlbumDialog(false);
      }
   };

   // Function to handle opening participant profile modal
   const handleOpenParticipantProfile = (member: AlbumMember) => {
      setSelectedParticipant(member);
      setParticipantProfileModalOpen(true);
   };

   // Function to handle closing participant profile modal
   const handleCloseParticipantProfile = () => {
      setParticipantProfileModalOpen(false);
      setSelectedParticipant(null);
   };

   const handleOpenReminderModal = () => {
      setReminderModalOpen(true);
   };

   const handleCloseReminderModal = () => {
      setReminderModalOpen(false);
      setSelectedPhotosForReminder(new Set());
      setReminderMessage('');
   };

   const handleTogglePhotoForReminder = (photoId: string) => {
      setSelectedPhotosForReminder((prev) => {
         const newSet = new Set(prev);
         if (newSet.has(photoId)) {
            newSet.delete(photoId);
         } else {
            if (newSet.size >= 5) {
               setSnackbarMessage('You can select a maximum of 5 photos');
               setSnackbarSeverity('warning');
               setSnackbarOpen(true);
               return prev;
            }
            newSet.add(photoId);
         }
         return newSet;
      });
   };

   const handleSendReminder = async (selectedParticipantIds: string[]) => {
      try {
         if (selectedParticipantIds.length === 0) {
            setSnackbarMessage('Please select at least one participant');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
         }

         setSendingReminder(true);

         const selectedParticipantsList = members.filter((m) =>
            selectedParticipantIds.includes(m.id)
         );

         const recipients = selectedParticipantsList.map((m) => ({
            userId: m.id,
            email: m.email,
         }));

      const selectedPhotosData = albumPhotos.filter((p) => selectedPhotosForReminder.has(p.id));

      const authHeaders = await getAuthHeaders();

      const response = await fetch(
         `${process.env.REACT_APP_API_URL}/api/albums/send-participant-reminder`,
         {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               ...authHeaders,
            },
            body: JSON.stringify({
               albumId: album?.id,
               albumTitle: album?.title,
               recipients,
               message: reminderMessage,
               photos: selectedPhotosData.map((p) => ({
                  id: p.id,
                  url: p.url,
                  title: p.title || 'Untitled',
               })),
            }),
         }
      );

         if (!response.ok) {
            const errorData = await response
               .json()
               .catch(() => ({ message: 'Failed to send reminders' }));
            throw new Error(errorData.message || 'Failed to send reminders');
         }

         const result = await response.json();

         setSnackbarMessage(
            `Sending reminders to ${recipients.length} participant(s)... This may take a moment.`
         );
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
         handleCloseReminderModal();
      } catch (error: any) {
         console.error('Error sending reminder:', error);
         setSnackbarMessage(error.message || 'Failed to send reminder. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSendingReminder(false);
      }
   };

   // Function to update album settings
   const updateAlbumSettings = async (settingKey: string, value: boolean) => {
      if (!albumId || !isOwner) {
         setSnackbarMessage('Only album owners can change settings');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setUpdatingSettings(true);
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/settings`,
            {
               method: 'PUT',
               headers: {
                  ...headers,
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({ [settingKey]: value }),
            }
         );

         if (!response.ok) {
            throw new Error('Failed to update album settings');
         }

         const data = await response.json();

         // Update local state
         setAlbum((prev) => {
            if (!prev) return null;
            return { ...prev, [settingKey]: value };
         });

         setSnackbarMessage('Settings updated successfully');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Error updating album settings:', error);
         setSnackbarMessage('Failed to update settings');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setUpdatingSettings(false);
      }
   };

   // Handle toggle for album privacy
   const handleTogglePrivacy = async (event: React.ChangeEvent<HTMLInputElement>) => {
      await updateAlbumSettings('isPublic', !event.target.checked);
   };

   // Handle toggle for participant visibility
   const handleToggleParticipantVisibility = async (event: React.ChangeEvent<HTMLInputElement>) => {
      await updateAlbumSettings('enableParticipantParticipation', event.target.checked);
   };

   // Handle toggle for participant photo upload
   const handleToggleParticipantUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      await updateAlbumSettings('enableParticipantUpload', event.target.checked);
   };

   // Handle toggle for participant invite
   const handleToggleParticipantInvite = async (event: React.ChangeEvent<HTMLInputElement>) => {
      await updateAlbumSettings('enableParticipantInvite', event.target.checked);
   };

   // Handle toggle for photographer upload on public page
   const handleTogglePhotographerUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      await updateAlbumSettings('allowPhotographerUpload', event.target.checked);
   };

   // Function to refetch album details
   const refetchAlbum = async () => {
      if (!albumId) return;

      try {
         const headers = await getAuthHeaders();
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/albums/${albumId}`, {
            headers,
         });

         if (response.ok) {
            const data = await response.json();
            const albumData = data.album || null;
            console.log('Refetched album data:', albumData);
            setAlbum(albumData);

            // Reload member details
            if (albumData) {
               loadAlbumMembers(albumData);
            }
         }
      } catch (error) {
         console.error('Error refetching album:', error);
      }
   };

   // Handle cover photo selection
   const handleCoverPhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file || !albumId || !currentUser) return;

      setUploadingCover(true);
      try {
         // Generate unique file name
         const timestamp = Date.now();
         const fileName = `cover_${albumId}_${timestamp}_${file.name}`;
         const storagePath = `albums/${albumId}/cover/${fileName}`;

         // Upload to Firebase Storage
         const storageRef = ref(storage, storagePath);
         await uploadBytes(storageRef, file);

         // Get download URL
         const coverPhotoUrl = await getDownloadURL(storageRef);

         // Create photo document in Firestore
         const photoData = {
            url: coverPhotoUrl,
            title: 'Album Cover Photo',
            description: '',
            uploadedBy: currentUser.uid,
            uploadedAt: new Date(),
            albumId: albumId,
            storagePath: storagePath,
            isCoverPhoto: true,
         };

         const photoRef = await addDoc(collection(db, 'photos'), photoData);
         const coverPhotoId = photoRef.id;

         // Update album document directly in Firestore
         const albumRef = doc(db, 'albums', albumId);
         await updateDoc(albumRef, {
            coverPhotoId: coverPhotoId,
            coverPhotoUrl: coverPhotoUrl,
            updatedAt: new Date(),
         });

         // Refetch the complete album details from the API
         await refetchAlbum();

         // Clear file input
         event.target.value = '';

         setSnackbarMessage('Cover photo uploaded successfully');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Error uploading cover photo:', error);
         setSnackbarMessage('Failed to upload cover photo');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setUploadingCover(false);
      }
   };

   // Handle remove cover photo
   const handleRemoveCoverPhoto = async () => {
      if (!albumId || !album?.coverPhotoId) return;

      setUploadingCover(true);
      try {
         // Update album document directly in Firestore
         const albumRef = doc(db, 'albums', albumId);
         await updateDoc(albumRef, {
            coverPhotoId: null,
            coverPhotoUrl: null,
            updatedAt: new Date(),
         });

         // Refetch the complete album details from the API
         await refetchAlbum();

         setSnackbarMessage('Cover photo removed successfully');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (error) {
         console.error('Error removing cover photo:', error);
         setSnackbarMessage('Failed to remove cover photo');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setUploadingCover(false);
      }
   };

   useEffect(() => {
      if (!albumId) return;
      setLoading(true);

      getAuthHeaders()
         .then((headers) => {
            // Add currentUserId as query parameter to fetch connection statuses
            const url = new URL(`${process.env.REACT_APP_API_URL}/api/albums/${albumId}`);
            if (currentUser?.uid) {
               url.searchParams.append('currentUserId', currentUser.uid);
            }
            return fetch(url.toString(), { headers });
         })
         .then((res) => res.json())
         .then((data) => {
            const albumData = data.album || null;
            console.log('Fetched album data:', albumData);
            console.log('Album members array:', albumData?.members);
            console.log('Album memberIds array:', albumData?.memberIds);
            setAlbum(albumData);
            setLoading(false);
            // Load member details after album is loaded
            if (albumData) {
               loadAlbumMembers(albumData);
            }
         })
         .catch(() => {
            setError('Failed to load album');
            setLoading(false);
         });
   }, [albumId, currentUser]);

   // Protect against viewers being on the grid tab; timeline is hidden so redirect to gallery
   const validCurrentTab = useMemo(() => {
      if (userRole === 'viewer' && currentTab === 'grid') {
         return 'gallery';
      }
      if (currentTab === 'timeline') {
         return 'gallery';
      }
      return currentTab;
   }, [currentTab, userRole]);

   // Infinite scroll - load more photos when user scrolls near bottom
   useInfiniteScroll(loadMorePhotos, {
      threshold: 300,
      enabled: !!album?.id && hasMorePhotos && !loadingAlbumPhotos,
      containerRef: scrollContainerRef,
   });

   // Auto-load more photos if content doesn't fill the scroll container
   useEffect(() => {
      if (
         !scrollContainerRef.current ||
         !hasMorePhotos ||
         loadingAlbumPhotos ||
         albumPhotos.length === 0
      ) {
         return;
      }

      const container = scrollContainerRef.current;
      const hasScrollbar = container.scrollHeight > container.clientHeight;

      // If no scrollbar exists and we have more photos to load, load them automatically
      if (!hasScrollbar && hasMorePhotos && !loadingAlbumPhotos) {
         console.log('📏 No scrollbar detected, auto-loading more photos...');
         const timer = setTimeout(() => {
            loadMorePhotos();
         }, 500); // Small delay to avoid rapid calls

         return () => clearTimeout(timer);
      }
   }, [albumPhotos.length, hasMorePhotos, loadingAlbumPhotos, loadMorePhotos]);

   // Track scrolling to show/hide sticky folder bar in tab header
   useEffect(() => {
      if (!scrollContainerRef.current || currentTab !== 'grid' || photoFolders.length === 0) {
         setShowStickyFolders(false);
         return;
      }

      const handleScroll = () => {
         if (!scrollContainerRef.current) return;

         const container = scrollContainerRef.current;
         const scrollTop = container.scrollTop;

         // Show sticky folders after scrolling down 50px
         setShowStickyFolders(scrollTop > 50);
      };

      const container = scrollContainerRef.current;
      container.addEventListener('scroll', handleScroll);
      handleScroll(); // Check initial state

      return () => {
         container.removeEventListener('scroll', handleScroll);
      };
   }, [currentTab, photoFolders.length]);

   // Set default tab based on user role (only once on initial load)
   const [hasSetInitialTab, setHasSetInitialTab] = useState(false);

   useEffect(() => {
      if (album && userRole && !hasSetInitialTab) {
         if (userRole === 'owner') {
            setCurrentTab('participants');
         } else if (userRole === 'editor') {
            setCurrentTab('grid');
         } else {
            // Viewers default to gallery tab (since they can't see grid/photos; timeline is hidden)
            setCurrentTab('gallery');
         }
         setHasSetInitialTab(true);
      }
   }, [album, userRole, hasSetInitialTab]);

   const loadAlbumMembers = async (albumData?: Album) => {
      const albumToUse = albumData || album;
      if (!albumToUse) {
         console.log('❌ No album data to load members from');
         setMembers([]);
         return;
      }

      console.log('🔍 Loading album members...', {
         albumId: albumToUse.id,
         hasMembers: !!albumToUse.members,
         membersCount: albumToUse.members?.length || 0,
      });

      // Load members from the members array
      if (albumToUse.members && albumToUse.members.length > 0) {
         console.log('✅ Using members array:', albumToUse.members);

         // Debug each member individually
         albumToUse.members.forEach((member: any, index: number) => {
            console.log(`👤 Member ${index}:`, {
               userId: member.userId,
               email: member.email,
               displayName: member.displayName,
               photoURL: member.photoURL,
               role: member.role,
               joinedAt: member.joinedAt,
               fullMemberObject: member,
            });
         });

         let formattedMembers: AlbumMember[] = albumToUse.members.map((member: any) => ({
            id: member.userId,
            email: member.email,
            displayName: member.displayName,
            photoURL: member.photoURL,
            role: member.role,
            joinedAt: member.joinedAt,
            profileData: null, // Initialize as null, will be populated later
            connectionStatus: member.connectionStatus, // Include connection status from backend
         }));

         // Fetch user profiles for member avatars
         if (formattedMembers.length > 0) {
            try {
               const userIds = formattedMembers.map((member) => member.id).filter((id) => id);

               if (userIds.length > 0) {
                  const userProfiles: { [userId: string]: any } = {};

                  for (const userId of userIds) {
                     try {
                        // Try userProfiles first (where complete profile data is stored)
                        let userProfileDoc = await getDoc(doc(db, 'userProfiles', userId));

                        if (!userProfileDoc.exists()) {
                           // Fallback to userProfile collection
                           userProfileDoc = await getDoc(doc(db, 'userProfile', userId));
                        }

                        if (!userProfileDoc.exists()) {
                           // Fallback to users collection
                           userProfileDoc = await getDoc(doc(db, 'users', userId));
                        }

                        if (userProfileDoc.exists()) {
                           userProfiles[userId] = userProfileDoc.data();
                        }
                     } catch (error) {
                        console.error(`Error fetching profile for user ${userId}:`, error);
                     }
                  }

                  // Add profile data to members
                  formattedMembers = formattedMembers.map((member) => ({
                     ...member,
                     profileData: userProfiles[member.id] || null,
                  }));
               }
            } catch (profileError) {
               console.error('Error fetching user profiles:', profileError);
               // Continue without profile data if there's an error
            }
         }

         console.log('Formatted members for display:', formattedMembers);

         // Debug: Show profile data for each member, especially owners
         formattedMembers.forEach((member, index) => {
            console.log(`Member ${index} (${member.role}):`, {
               id: member.id,
               email: member.email,
               displayName: member.displayName,
               photoURL: member.photoURL,
               profileData: member.profileData,
               hasProfilePhoto: !!(
                  member.profileData?.photoURL ||
                  member.profileData?.profilePicture ||
                  member.profileData?.avatar
               ),
               profilePhotoUrl:
                  member.profileData?.photoURL ||
                  member.profileData?.profilePicture ||
                  member.profileData?.avatar ||
                  'none',
            });
         });

         console.log('✅ Setting members state with formatted members:', {
            count: formattedMembers.length,
            members: formattedMembers.map((m) => ({
               id: m.id,
               email: m.email,
               displayName: m.displayName,
               role: m.role,
               connectionStatus: m.connectionStatus,
            })),
         });
         setMembers(formattedMembers);

         // Update connection statuses from backend data
         if (currentUser) {
            const statusMap = new Map();
            formattedMembers.forEach((member) => {
               if (member.connectionStatus && member.id !== currentUser.uid) {
                  statusMap.set(member.id, {
                     status: member.connectionStatus.status,
                     requestId: member.connectionStatus.requestId,
                  });
               }
            });
            setConnectionStatuses(statusMap);
            console.log(
               '✅ Connection statuses loaded from backend:',
               Array.from(statusMap.entries()).length,
               'members'
            );
         }

         console.log('✅ Members state updated successfully');
         return;
      }

      // No members array found
      console.log('⚠️ No members array found, setting empty members array');
      setMembers([]);
   };

   // File size validation function
   const validateFileSizes = (files: File[]) => {
      const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes
      const validFiles: File[] = [];
      const invalidFiles: { file: File; error: string }[] = [];

      files.forEach((file) => {
         if (file.size > MAX_FILE_SIZE) {
            invalidFiles.push({
               file,
               error: `File "${file.name}" is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Maximum size is 50MB.`,
            });
         } else {
            validFiles.push(file);
         }
      });

      return { validFiles, invalidFiles };
   };

   const handleDirectUpload = async () => {
      if (!selectedFiles.length || !currentUser?.uid || !album) return;

      setUploading(true);
      setUploadError(null);
      setUploadProgress(0);
      setBatchProgress(null);

      try {
         // Validate file types first - only allow images
         const imageFiles = selectedFiles.filter((file) => file.type.startsWith('image/'));
         const nonImageFiles = selectedFiles.filter((file) => !file.type.startsWith('image/'));

         if (nonImageFiles.length > 0) {
            const fileNames = nonImageFiles.map((file) => file.name).join(', ');
            setUploadError(`Only image files are allowed. Invalid files: ${fileNames}`);
            setUploading(false);
            return;
         }

         // Simple file size validation
         const { validFiles, invalidFiles } = validateFileSizes(imageFiles);

         if (invalidFiles.length > 0) {
            const errorMessages = invalidFiles.map((item) => item.error).join('\n');
            setUploadError(`Some files are too large:\n${errorMessages}`);
            if (validFiles.length === 0) {
               setUploading(false);
               return;
            }
         }

         if (validFiles.length > 0) {
            const uploadedPhotoIds: string[] = [];
            const { prepared, errors: prepareErrors } = await preparePhotosForUpload(validFiles, {
               maxSizeBytes: 50 * 1024 * 1024,
            });
            if (prepareErrors.length > 0) {
               setUploadError(
                  (prev) =>
                     (prev ? prev + '\n' : '') +
                     `${prepareErrors.length} file(s) skipped due to conversion/size.`
               );
            }
            if (prepared.length === 0) {
               setUploading(false);
               return;
            }

            // Upload prepared files directly to Firebase Storage in batches
            const results = await batchUpload(
               prepared,
               async (file, fileIndex) => {
                  // Extract metadata from file
                  const metadata = await getImageTakenDate(file);

                  // Create safe filename
                  const safeFileName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
                  const fileName = `albums/${album.id}/photos/${Date.now()}_${safeFileName}`;
                  const storageRef = ref(storage, fileName);

                  // Upload to Firebase Storage
                  await uploadBytes(storageRef, file);

                  // Get download URL
                  const downloadURL = await getDownloadURL(storageRef);

                  // Create photo document in Firestore
                  const photoData = {
                     url: downloadURL,
                     filePath: fileName,
                     thumbnailUrl: downloadURL, // Can be replaced with actual thumbnail later
                     title: file.name.split('.')[0] || 'Untitled Photo',
                     description: '',
                     fileName: file.name,
                     size: file.size,
                     mimeType: file.type,
                     albumId: album.id,
                     uploadedBy: currentUser.uid,
                     userId: currentUser.uid,
                     uploadedAt: serverTimestamp(),
                     createdAt: serverTimestamp(),
                     takenAt: metadata?.takenDate
                        ? Timestamp.fromDate(metadata.takenDate)
                        : serverTimestamp(),
                     tags: [],
                     likeCount: 0,
                     likedBy: [],
                     isEnhanced: false,
                     isTransformed: false,
                     // Include location if available
                     ...(metadata?.location?.latitude && metadata?.location?.longitude
                        ? {
                             location: {
                                address: '',
                                name: '',
                                coordinates: {
                                   latitude: metadata.location.latitude,
                                   longitude: metadata.location.longitude,
                                },
                             },
                          }
                        : {}),
                  };

                  // Save to Firestore photos collection
                  const photoRef = await addDoc(collection(db, 'photos'), photoData);

                  return {
                     id: photoRef.id,
                     url: downloadURL,
                     fileName: file.name,
                  };
               },
               {
                  batchSize: 60,
                  onProgress: (progress) => {
                     // Update progress
                     setUploadProgress(progress.overallProgress);
                     setBatchProgress(progress);

                     console.log(
                        `Batch ${progress.currentBatch}/${progress.totalBatches}: ` +
                           `${progress.completedFiles}/${progress.totalFiles} files uploaded ` +
                           `(${progress.overallProgress}%)`
                     );
                  },
                  onFileSuccess: (fileIndex, result) => {
                     // Store photo ID for later
                     uploadedPhotoIds.push(result.id);
                     console.log(`Photo ${fileIndex + 1} uploaded:`, result.fileName);
                  },
                  onFileError: (fileIndex, error) => {
                     console.error(`Photo ${fileIndex + 1} failed:`, error.message);
                  },
                  onBatchComplete: (batchIndex, batchResults) => {
                     console.log(`Completed batch ${batchIndex + 1}`);
                  },
               }
            );

            console.log('All batches completed:', results);

            // Update album with new photo IDs
            if (uploadedPhotoIds.length > 0) {
               try {
                  const albumRef = doc(db, 'albums', album.id);
                  const albumDoc = await getDoc(albumRef);

                  if (albumDoc.exists()) {
                     const currentPhotoIds = albumDoc.data().photoIds || [];
                     await updateDoc(albumRef, {
                        photoIds: [...currentPhotoIds, ...uploadedPhotoIds],
                        updatedAt: serverTimestamp(),
                     });
                  }
               } catch (error) {
                  console.error('Error updating album with photo IDs:', error);
               }
            }

            // Count successful and failed uploads
            const successCount = results.filter((r) => !(r instanceof Error)).length;
            const failCount = results.filter((r) => r instanceof Error).length;

            // Show success message
            if (invalidFiles.length > 0 || failCount > 0) {
               const messages: string[] = [];
               if (successCount > 0) {
                  messages.push(
                     `Successfully uploaded ${successCount} photo${successCount !== 1 ? 's' : ''}`
                  );
               }
               if (failCount > 0) {
                  messages.push(`${failCount} photo${failCount !== 1 ? 's' : ''} failed`);
               }
               if (invalidFiles.length > 0) {
                  messages.push(
                     `${invalidFiles.length} file${invalidFiles.length !== 1 ? 's' : ''} skipped`
                  );
               }
               setUploadError(messages.join('. ') + '.');
            } else {
               setUploadError(null);
               setSnackbarMessage(
                  `Successfully uploaded ${successCount} photo${successCount !== 1 ? 's' : ''} in ${Math.ceil(validFiles.length / 60)} batch${Math.ceil(validFiles.length / 60) !== 1 ? 'es' : ''}!`
               );
               setSnackbarSeverity('success');
               setSnackbarOpen(true);
            }

            // Close dialog and reset
            setDirectUploadOpen(false);
            setSelectedFiles([]);
            setExtractedMetadata({});
            setBatchProgress(null);

            // Refresh album data and reload photos
            await refetchAlbum();
            reloadPhotos();
         }
      } catch (err: any) {
         setUploadError(err.message || 'Failed to upload photos');
      } finally {
         setUploading(false);
         setBatchProgress(null);
      }
   };

   const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);

      // Validate file types - only allow images
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      files.forEach((file) => {
         if (file.type.startsWith('image/')) {
            validFiles.push(file);
         } else {
            invalidFiles.push(file.name);
         }
      });

      // Show toast message for invalid files
      if (invalidFiles.length > 0) {
         const fileTypes = invalidFiles
            .map((name) => {
               const extension = name.split('.').pop()?.toUpperCase();
               return extension || 'Unknown';
            })
            .join(', ');

         setSnackbarMessage(
            `Only image files are allowed. Invalid files: ${invalidFiles.join(', ')} (${fileTypes} files not supported)`
         );
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      }

      // Only set valid image files
      setSelectedFiles(validFiles);

      // Create simple preview URLs for images
      const previews: { [key: string]: string } = {};
      validFiles.forEach((file) => {
         if (file.type.startsWith('image/')) {
            previews[file.name] = URL.createObjectURL(file);
         }
      });
      setFilePreviews(previews);

      // Simple metadata - no complex extraction
      const metadataMap: { [fileName: string]: { takenDate: Date | null; location: any } } = {};
      validFiles.forEach((file) => {
         metadataMap[file.name] = {
            takenDate: null,
            location: null,
         };
      });
      setExtractedMetadata(metadataMap);
   };

   const removeFile = (fileToRemove: File) => {
      // Clean up the preview URL
      if (filePreviews[fileToRemove.name]) {
         URL.revokeObjectURL(filePreviews[fileToRemove.name]);
      }

      setSelectedFiles((files) => files.filter((file) => file !== fileToRemove));
      setFilePreviews((prev) => {
         const newPreviews = { ...prev };
         delete newPreviews[fileToRemove.name];
         return newPreviews;
      });

      // Clean up extracted metadata
      setExtractedMetadata((prev) => {
         const newMetadata = { ...prev };
         delete newMetadata[fileToRemove.name];
         return newMetadata;
      });
   };

   // Cleanup preview URLs when dialog closes
   const handleCloseDirectUpload = () => {
      // Clean up all preview URLs
      Object.values(filePreviews).forEach((url) => URL.revokeObjectURL(url));
      setFilePreviews({});
      setSelectedFiles([]);
      setExtractedMetadata({});
      setDirectUploadOpen(false);
   };

   const handlePhotoMenuOpen = (event: React.MouseEvent<HTMLElement>, photo: any) => {
      event.stopPropagation();
      setPhotoMenuAnchor(event.currentTarget);
      setSelectedPhotoForMenu(photo);
   };

   const handlePhotoMenuClose = () => {
      setPhotoMenuAnchor(null);
      setSelectedPhotoForMenu(null);
   };

   const handleSetCover = async () => {
      if (!selectedPhotoForMenu || !album) return;

      setSettingCover(true);
      try {
         // Use the correct endpoint: /set-cover with POST method
         const headers = await getAuthHeaders();

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${album.id}/set-cover`,
            {
               method: 'POST',
               headers,
               body: JSON.stringify({ photoId: selectedPhotoForMenu.id }),
            }
         );

         if (!response.ok) throw new Error('Failed to set cover');

         // Update local state
         setAlbum((prev) => (prev ? { ...prev, coverPhotoId: selectedPhotoForMenu.id } : null));
      } catch (error) {
         console.error('Error setting cover:', error);
      } finally {
         setSettingCover(false);
         handlePhotoMenuClose();
      }
   };

   const handleRemoveFromAlbum = async () => {
      if (!selectedPhotoForMenu || !album) return;

      try {
         // Use the correct endpoint: /remove-photo (singular) with photoId (singular)
         const headers = await getAuthHeaders();

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${album.id}/remove-photo`,
            {
               method: 'POST',
               headers,
               body: JSON.stringify({ photoId: selectedPhotoForMenu.id }),
            }
         );

         if (!response.ok) {
            // Log the full response for debugging
            const responseText = await response.text();
            console.error('Remove photo API response:', {
               status: response.status,
               statusText: response.statusText,
               body: responseText,
            });

            // Try to parse as JSON, fallback to text
            let errorMessage = 'Failed to remove photo from album';
            try {
               const errorData = JSON.parse(responseText);
               errorMessage = errorData.message || errorMessage;
            } catch {
               errorMessage = `Server error (${response.status}): ${response.statusText}`;
            }

            throw new Error(errorMessage);
         }

         // Update local state - remove photo from album
         setAlbum((prev) => {
            if (!prev) return null;
            const updatedPhotoIds = (prev.photoIds || []).filter(
               (id) => id !== selectedPhotoForMenu.id
            );
            return { ...prev, photoIds: updatedPhotoIds };
         });

         // Reload photos after removal
         reloadPhotos();

         // Show success message
         setSnackbarMessage('Photo removed from album successfully');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);

         console.log('Photo removed from album successfully');
      } catch (error) {
         console.error('Error removing photo from album:', error);

         // Show error message
         setSnackbarMessage(
            error instanceof Error ? error.message : 'Failed to remove photo from album'
         );
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         handlePhotoMenuClose();
      }
   };

   // Photo modal handlers
   const openPhotoModal = (photoIndex: number) => {
      setCurrentPhotoIndex(photoIndex);
      setModalOpen(true);
   };

   const closePhotoModal = () => {
      setModalOpen(false);
   };

   const handleCloseViewAllPhotos = () => {
      setViewAllPhotosOpen(false);
      setCurrentPage(1); // Reset to first page when closing
   };

   const goToNextPhoto = () => {
      setCurrentPhotoIndex((prev) => (prev + 1) % albumPhotos.length);
   };

   const goToPrevPhoto = () => {
      setCurrentPhotoIndex((prev) => (prev - 1 + albumPhotos.length) % albumPhotos.length);
   };

   // Keyboard navigation
   React.useEffect(() => {
      const handleKeyPress = (event: KeyboardEvent) => {
         if (!modalOpen) return;

         switch (event.key) {
            case 'ArrowRight':
               goToNextPhoto();
               break;
            case 'ArrowLeft':
               goToPrevPhoto();
               break;
            case 'Escape':
               closePhotoModal();
               break;
         }
      };

      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
   }, [modalOpen, albumPhotos.length]);

   // Photo organization utilities
   const sortPhotos = (photos: any[]) => {
      const sorted = [...photos].sort((a, b) => {
         switch (sortBy) {
            case 'date-newest':
               const dateA = getBestPhotoDate(a);
               const dateB = getBestPhotoDate(b);
               if (!dateA && !dateB) return 0;
               if (!dateA) return 1;
               if (!dateB) return -1;
               return dateB.getTime() - dateA.getTime();
            case 'date-oldest':
               const dateA2 = getBestPhotoDate(a);
               const dateB2 = getBestPhotoDate(b);
               if (!dateA2 && !dateB2) return 0;
               if (!dateA2) return 1;
               if (!dateB2) return -1;
               return dateA2.getTime() - dateB2.getTime();
            case 'title':
               return (a.title || '').localeCompare(b.title || '');
            default:
               return 0;
         }
      });
      return sorted;
   };

   // Utility function to safely parse dates from Firestore
   const parsePhotoDate = (dateField: any): Date | null => {
      if (!dateField) return null;

      try {
         // Handle Firestore Timestamp
         if (dateField.seconds) {
            return new Date(dateField.seconds * 1000);
         }

         // Handle Firestore Timestamp with nanoseconds
         if (dateField.seconds && dateField.nanoseconds) {
            return new Date(dateField.seconds * 1000 + dateField.nanoseconds / 1000000);
         }

         // Handle Firestore Timestamp with toDate method
         if (typeof dateField.toDate === 'function') {
            return dateField.toDate();
         }

         // Handle _seconds property (sometimes used in Firestore)
         if (dateField._seconds) {
            return new Date(dateField._seconds * 1000);
         }

         // Handle regular date string or number
         const parsedDate = new Date(dateField);
         if (!isNaN(parsedDate.getTime())) {
            return parsedDate;
         }

         return null;
      } catch (error) {
         console.warn('Error parsing date:', dateField, error);
         return null;
      }
   };

   // Enhanced function to get the best available date from a photo
   const getBestPhotoDate = (photo: any): Date | null => {
      // Try different date fields in order of preference
      const dateFields = [
         photo.takenAt, // EXIF date when photo was taken
         photo.createdAt, // When photo was created/uploaded
         photo.uploadedAt, // When photo was uploaded
         photo.dateTime, // Alternative date field
         photo.date, // Generic date field
      ];

      for (const dateField of dateFields) {
         const parsedDate = parsePhotoDate(dateField);
         if (parsedDate) {
            return parsedDate;
         }
      }

      return null;
   };

   // Helper function to safely format location coordinates
   const formatLocationCoordinates = (location: any): string | null => {
      if (!location) return null;

      const lat = location.latitude;
      const lng = location.longitude;

      if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng)) {
         return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
      }

      return null;
   };

   // Helper function to safely format camera information
   const formatCameraInfo = (photo: any): string | null => {
      if (!photo.camera?.make) return null;

      let cameraInfo = `📷 ${photo.camera.make}`;
      if (photo.camera.model) {
         cameraInfo += ` ${photo.camera.model}`;
      }

      const settings = [];
      if (photo.settings?.iso) {
         settings.push(`ISO ${photo.settings.iso}`);
      }
      if (photo.settings?.aperture) {
         settings.push(`f/${photo.settings.aperture}`);
      }

      if (settings.length > 0) {
         cameraInfo += ` • ${settings.join(' • ')}`;
      }

      return cameraInfo;
   };

   const formatPhotoDate = (photo: any, format: 'short' | 'long' = 'short'): string => {
      const date = getBestPhotoDate(photo);
      if (!date) {
         return 'Unknown Date';
      }

      if (format === 'short') {
         return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
         });
      } else {
         return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
         });
      }
   };

   const groupPhotosByDate = (photos: any[]) => {
      const groups: { [key: string]: any[] } = {};

      photos.forEach((photo) => {
         const date = getBestPhotoDate(photo);
         const dateKey = date
            ? date.toLocaleDateString('en-US', {
                 year: 'numeric',
                 month: 'long',
                 day: 'numeric',
              })
            : 'Unknown Date';

         if (!groups[dateKey]) {
            groups[dateKey] = [];
         }
         groups[dateKey].push(photo);
      });

      return groups;
   };

   const organizedPhotos = sortPhotos(albumPhotos);
   const groupedPhotos = groupByDate ? groupPhotosByDate(organizedPhotos) : null;

   // Filter photos for timeline
   const filteredTimelinePhotos = useMemo(() => {
      let filtered = [...albumPhotos];

      // Apply location filter
      if (showLocationOnly) {
         filtered = filtered.filter((photo) => photo.location);
      }

      // Apply camera info filter
      if (showCameraInfoOnly) {
         filtered = filtered.filter((photo) => {
            if (typeof photo.camera === 'string') {
               return !!photo.camera;
            }
            return !!photo.camera?.make;
         });
      }

      // Sort timeline photos
      filtered.sort((a, b) => {
         switch (timelineSort) {
            case 'date-newest':
               const dateA = getBestPhotoDate(a);
               const dateB = getBestPhotoDate(b);
               if (!dateA && !dateB) return 0;
               if (!dateA) return 1;
               if (!dateB) return -1;
               return dateB.getTime() - dateA.getTime();
            case 'date-oldest':
               const dateA2 = getBestPhotoDate(a);
               const dateB2 = getBestPhotoDate(b);
               if (!dateA2 && !dateB2) return 0;
               if (!dateA2) return 1;
               if (!dateB2) return -1;
               return dateA2.getTime() - dateB2.getTime();
            case 'location':
               return (a.location ? 0 : 1) - (b.location ? 0 : 1);
            case 'camera':
               const aMake = typeof a.camera === 'string' ? a.camera : a.camera?.make || '';
               const bMake = typeof b.camera === 'string' ? b.camera : b.camera?.make || '';
               return aMake.localeCompare(bMake);
            default:
               return 0;
         }
      });

      return filtered;
   }, [albumPhotos, showLocationOnly, showCameraInfoOnly, timelineSort]);

   const fetchWaitlist = async () => {
      if (!albumId) return;

      console.log('🔍 Fetching waitlist for album:', albumId);
      console.log('🔍 User role:', userRole);

      try {
         setLoadingWaitlist(true);
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/waitlist`,
            {
               headers,
            }
         );

         console.log('🔍 Waitlist response status:', response.status);

         if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ Waitlist fetch failed:', errorText);
            throw new Error('Failed to fetch waiting list');
         }

         const data = await response.json();
         console.log('✅ Waitlist data received:', data);
         console.log('✅ Waitlist array:', data.waitlist);
         setWaitingList(data.waitlist || []);
      } catch (err) {
         console.error('❌ Error fetching waiting list:', err);
         setWaitingList([]);
      } finally {
         setLoadingWaitlist(false);
      }
   };

   // Fetch waiting list on mount and when user role changes
   useEffect(() => {
      if (albumId && isOwner) {
         console.log('📋 Initial waitlist fetch triggered');
         fetchWaitlist();
      }
   }, [albumId, isOwner]);

   // Fetch waiting list when switching to waitlist tab
   useEffect(() => {
      if (currentTab === 'waitlist' && albumId && isOwner) {
         console.log('📋 Waitlist tab opened, fetching data');
         fetchWaitlist();
      }
   }, [currentTab]);

   // Render Album Sharing tab
   const renderAlbumSharing = () => {
      // Only owners can see shareable links
      if (!isOwner) {
         return null;
      }

      if (!album) {
         return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
               <CircularProgress />
            </Box>
         );
      }

      const albumPublicUrl = `${window.location.origin}/albums/public/${albumId}?source=qr_scan`;

      // Function to generate QR code
      const generateQRCode = async () => {
         try {
            const qrDataURL = await QRCode.toDataURL(albumPublicUrl, {
               width: 512,
               margin: 2,
               color: {
                  dark: '#000000',
                  light: '#ffffff',
               },
            });
            setQrCodeDataURL(qrDataURL);
            setShowQRCode(true);
         } catch (error) {
            console.error('Error generating QR code:', error);
            setSnackbarMessage('Failed to generate QR code');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         }
      };

      // Function to download QR code as PDF
      const downloadQRCode = () => {
         if (!qrCodeDataURL) return;

         try {
            // Create a new PDF document (A4 size: 210mm x 297mm)
            const pdf = new jsPDF({
               orientation: 'portrait',
               unit: 'mm',
               format: 'a4',
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Add background color
            pdf.setFillColor(245, 245, 220); // Light beige
            pdf.rect(0, 0, pageWidth, pageHeight, 'F');

            // Add title with better styling
            pdf.setFontSize(24);
            pdf.setTextColor(51, 51, 51);
            pdf.setFont('helvetica', 'bold');
            const title = album?.title || 'Album';
            const titleWidth = pdf.getTextWidth(title);
            pdf.text(title, (pageWidth - titleWidth) / 2, 30);

            // Add subtitle
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(102, 102, 102);
            const subtitle = 'Scan to view album';
            const subtitleWidth = pdf.getTextWidth(subtitle);
            pdf.text(subtitle, (pageWidth - subtitleWidth) / 2, 40);

            // Add QR code (centered on page)
            const qrSize = 120; // mm
            const qrX = (pageWidth - qrSize) / 2;
            const qrY = 60;

            // Add white background for QR code
            pdf.setFillColor(255, 255, 255);
            pdf.roundedRect(qrX - 5, qrY - 5, qrSize + 10, qrSize + 10, 3, 3, 'F');

            // Add QR code image
            pdf.addImage(qrCodeDataURL, 'PNG', qrX, qrY, qrSize, qrSize);

            // Save the PDF
            pdf.save(`${album?.title || 'album'}-qr-code.pdf`);

            setSnackbarMessage('QR code PDF downloaded!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
         } catch (error) {
            console.error('Error generating PDF:', error);
            setSnackbarMessage('Failed to generate PDF');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         }
      };

      return (
         <Box sx={{ p: isMobile ? 2 : 3 }}>
            {/* Public Link Section */}
            <Box sx={{ maxWidth: '600px' }}>
               <Card>
                  <CardContent>
                     <Typography variant='h6' sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <LinkIcon sx={{ mr: 1 }} />
                        Public Album Link
                     </Typography>
                     <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                        Share this link to allow anyone to view and request to join your album.
                     </Typography>
                     <Box
                        sx={{
                           display: 'flex',
                           gap: 1,
                           mb: 2,
                           flexDirection: isMobile ? 'column' : 'row',
                        }}
                     >
                        <TextField
                           value={`${window.location.origin}/albums/public/${albumId}?source=shareable_link`}
                           fullWidth
                           InputProps={{
                              readOnly: true,
                           }}
                           size='small'
                        />
                        <Button
                           variant='outlined'
                           onClick={() => {
                              navigator.clipboard.writeText(
                                 `${window.location.origin}/albums/public/${albumId}?source=shareable_link`
                              );
                              setSnackbarMessage('Link copied to clipboard!');
                              setSnackbarSeverity('success');
                              setSnackbarOpen(true);
                           }}
                           sx={{ minWidth: isMobile ? '100%' : 'auto' }}
                        >
                           Copy
                        </Button>
                     </Box>

                     <Divider sx={{ my: 3 }} />

                     {/* QR Code Section */}
                     <Typography variant='h6' sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <QrCodeIcon sx={{ mr: 1 }} />
                        Album QR Code
                     </Typography>
                     <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                        Generate a QR code that links directly to your album. Perfect for printing
                        or sharing digitally.
                     </Typography>

                     {!showQRCode ? (
                        <Button
                           variant='contained'
                           startIcon={<QrCodeIcon />}
                           onClick={generateQRCode}
                           fullWidth={isMobile}
                        >
                           Generate QR Code
                        </Button>
                     ) : (
                        <Box
                           sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: 2,
                           }}
                        >
                           <Box
                              sx={{
                                 p: 2,
                                 bgcolor: 'white',
                                 borderRadius: 2,
                                 border: '1px solid',
                                 borderColor: 'divider',
                                 display: 'flex',
                                 justifyContent: 'center',
                              }}
                           >
                              <img
                                 src={qrCodeDataURL}
                                 alt='Album QR Code'
                                 style={{ width: '256px', height: '256px', display: 'block' }}
                              />
                           </Box>
                           <Box
                              sx={{
                                 display: 'flex',
                                 gap: 1,
                                 width: '100%',
                                 flexDirection: isMobile ? 'column' : 'row',
                              }}
                           >
                              <Button
                                 variant='contained'
                                 startIcon={<DownloadIcon />}
                                 onClick={downloadQRCode}
                                 fullWidth={isMobile}
                              >
                                 Download as PDF
                              </Button>
                              <Button
                                 variant='outlined'
                                 onClick={() => setShowQRCode(false)}
                                 fullWidth={isMobile}
                              >
                                 Hide QR Code
                              </Button>
                           </Box>
                        </Box>
                     )}
                  </CardContent>
               </Card>
            </Box>
         </Box>
      );
   };

   // Render Waitlist tab
   const renderWaitlist = () => {
      // Only owners can see the waitlist
      if (!isOwner) {
         return null;
      }

      const handleApproveUser = async (waitingListId: string, userId: string) => {
         try {
            // Add user to approving set
            setApprovingUsers((prev) => new Set(prev).add(waitingListId));

            // Get auth headers
            const headers = await getAuthHeaders();
            headers['Content-Type'] = 'application/json';

            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/waitlist/${waitingListId}/approve`,
               {
                  method: 'POST',
                  headers,
                  body: JSON.stringify({ userId }),
               }
            );

            const result = await response.json();

            if (result.success) {
               setSnackbarMessage('User approved successfully');
               setSnackbarSeverity('success');
               setSnackbarOpen(true);
               fetchWaitlist(); // Refresh waitlist
               // Refresh album data to show new member
               const albumResponse = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/albums/${albumId}`,
                  {
                     headers: await getAuthHeaders(),
                  }
               );
               const albumData = await albumResponse.json();
               setAlbum(albumData.album);
               loadAlbumMembers(albumData.album);
            } else {
               setSnackbarMessage(result.error || 'Failed to approve user');
               setSnackbarSeverity('error');
               setSnackbarOpen(true);
            }
         } catch (err) {
            console.error('Error approving user:', err);
            setSnackbarMessage('Failed to approve user');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         } finally {
            // Remove user from approving set
            setApprovingUsers((prev) => {
               const newSet = new Set(prev);
               newSet.delete(waitingListId);
               return newSet;
            });
         }
      };

      const handleRejectUser = async (waitingListId: string) => {
         try {
            // Add user to rejecting set
            setRejectingUsers((prev) => new Set(prev).add(waitingListId));

            const headers = await getAuthHeaders();
            headers['Content-Type'] = 'application/json';

            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/waitlist/${waitingListId}/reject`,
               {
                  method: 'POST',
                  headers,
               }
            );

            const result = await response.json();

            if (result.success) {
               setSnackbarMessage('User rejected');
               setSnackbarSeverity('success');
               setSnackbarOpen(true);
               fetchWaitlist(); // Refresh waitlist
            } else {
               setSnackbarMessage(result.error || 'Failed to reject user');
               setSnackbarSeverity('error');
               setSnackbarOpen(true);
            }
         } catch (err) {
            console.error('Error rejecting user:', err);
            setSnackbarMessage('Failed to reject user');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         } finally {
            // Remove user from rejecting set
            setRejectingUsers((prev) => {
               const newSet = new Set(prev);
               newSet.delete(waitingListId);
               return newSet;
            });
         }
      };

      const handleApproveAll = async () => {
         try {
            setApprovingAll(true);
            setConfirmApproveAllOpen(false);

            // Get auth headers
            const headers = await getAuthHeaders();
            headers['Content-Type'] = 'application/json';

            const response = await fetch(
               `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/waitlist/approve-all`,
               {
                  method: 'POST',
                  headers,
               }
            );

            const result = await response.json();

            if (result.success) {
               const message =
                  result.failedCount > 0
                     ? `Approved ${result.approvedCount} user(s), ${result.failedCount} failed`
                     : `Successfully approved ${result.approvedCount} user(s)`;

               setSnackbarMessage(message);
               setSnackbarSeverity(result.failedCount > 0 ? 'warning' : 'success');
               setSnackbarOpen(true);

               // Refresh waitlist
               fetchWaitlist();

               // Refresh album data to show new members
               const albumResponse = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/albums/${albumId}`,
                  {
                     headers: await getAuthHeaders(),
                  }
               );
               const albumData = await albumResponse.json();
               setAlbum(albumData.album);
               loadAlbumMembers(albumData.album);
            } else {
               setSnackbarMessage(result.message || 'Failed to approve users');
               setSnackbarSeverity('error');
               setSnackbarOpen(true);
            }
         } catch (err) {
            console.error('Error approving all users:', err);
            setSnackbarMessage('Failed to approve users');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         } finally {
            setApprovingAll(false);
         }
      };

      return (
         <Box sx={{ p: isMobile ? 2 : 3 }}>
            <Card>
               <CardContent>
                  <Box
                     sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                     }}
                  >
                     <Typography variant='h6' sx={{ display: 'flex', alignItems: 'center' }}>
                        <HourglassEmptyIcon sx={{ mr: 1 }} />
                        Waiting List
                     </Typography>
                     {waitingList.filter((r) => r.status === 'pending').length > 0 && (
                        <Button
                           variant='contained'
                           color='primary'
                           size='small'
                           disabled={approvingAll || loadingWaitlist}
                           onClick={() => setConfirmApproveAllOpen(true)}
                           startIcon={
                              approvingAll ? (
                                 <CircularProgress size={16} color='inherit' />
                              ) : (
                                 <CheckCircleIcon />
                              )
                           }
                        >
                           {approvingAll
                              ? 'Approving...'
                              : `Approve All (${waitingList.filter((r) => r.status === 'pending').length})`}
                        </Button>
                     )}
                  </Box>
                  <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                     Users who have requested to join your album
                  </Typography>

                  {loadingWaitlist ? (
                     <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                     </Box>
                  ) : waitingList.length === 0 ? (
                     <Alert severity='info'>No pending requests</Alert>
                  ) : (
                     <List>
                        {waitingList.map((request) => {
                           const getStatusColor = (status: string) => {
                              switch (status) {
                                 case 'approved':
                                    return 'success';
                                 case 'rejected':
                                    return 'error';
                                 default:
                                    return 'default';
                              }
                           };

                           return (
                              <ListItem
                                 key={request.id}
                                 sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    mb: 1,
                                 }}
                              >
                                 <ListItemAvatar>
                                    <Avatar src={request.photoURL || undefined}>
                                       {request.displayName?.charAt(0)?.toUpperCase() || '?'}
                                    </Avatar>
                                 </ListItemAvatar>
                                 <ListItemText
                                    primary={request.displayName || 'Unknown User'}
                                    secondary={
                                       <>
                                          {request.email || 'No email'}
                                          {request.message && (
                                             <>
                                                <br />
                                                <Typography
                                                   component='span'
                                                   variant='body2'
                                                   color='text.primary'
                                                >
                                                   Message: {request.message}
                                                </Typography>
                                             </>
                                          )}
                                          <br />
                                          <Chip
                                             label={request.status || 'pending'}
                                             size='small'
                                             color={getStatusColor(request.status)}
                                             sx={{ mt: 0.5 }}
                                          />
                                       </>
                                    }
                                 />
                                 <ListItemSecondaryAction>
                                    {request.status === 'pending' ? (
                                       <Box sx={{ display: 'flex', gap: 1 }}>
                                          <Button
                                             size='small'
                                             variant='contained'
                                             color='success'
                                             disabled={
                                                approvingUsers.has(request.id) ||
                                                rejectingUsers.has(request.id)
                                             }
                                             onClick={() =>
                                                handleApproveUser(request.id, request.userId)
                                             }
                                             startIcon={
                                                approvingUsers.has(request.id) ? (
                                                   <CircularProgress size={16} color='inherit' />
                                                ) : null
                                             }
                                          >
                                             {approvingUsers.has(request.id)
                                                ? 'Approving...'
                                                : 'Approve'}
                                          </Button>
                                          <Button
                                             size='small'
                                             variant='outlined'
                                             color='error'
                                             disabled={
                                                approvingUsers.has(request.id) ||
                                                rejectingUsers.has(request.id)
                                             }
                                             onClick={() => handleRejectUser(request.id)}
                                             startIcon={
                                                rejectingUsers.has(request.id) ? (
                                                   <CircularProgress size={16} color='inherit' />
                                                ) : null
                                             }
                                          >
                                             {rejectingUsers.has(request.id)
                                                ? 'Rejecting...'
                                                : 'Reject'}
                                          </Button>
                                       </Box>
                                    ) : (
                                       <Chip
                                          label={
                                             request.status === 'approved' ? 'Approved' : 'Rejected'
                                          }
                                          color={getStatusColor(request.status)}
                                          size='small'
                                       />
                                    )}
                                 </ListItemSecondaryAction>
                              </ListItem>
                           );
                        })}
                     </List>
                  )}
               </CardContent>
            </Card>

            {/* Confirmation Dialog for Approve All */}
            <Dialog
               open={confirmApproveAllOpen}
               onClose={() => setConfirmApproveAllOpen(false)}
               aria-labelledby='approve-all-dialog-title'
               aria-describedby='approve-all-dialog-description'
            >
               <DialogTitle id='approve-all-dialog-title'>Approve All Pending Members?</DialogTitle>
               <DialogContent>
                  <Typography id='approve-all-dialog-description'>
                     Are you sure you want to approve all{' '}
                     {waitingList.filter((r) => r.status === 'pending').length} pending member(s)?
                     This will grant them access to the album and they will be notified via email.
                  </Typography>
               </DialogContent>
               <DialogActions>
                  <Button
                     onClick={() => setConfirmApproveAllOpen(false)}
                     color='inherit'
                     disabled={approvingAll}
                  >
                     Cancel
                  </Button>
                  <Button
                     onClick={handleApproveAll}
                     color='primary'
                     variant='contained'
                     disabled={approvingAll}
                     startIcon={
                        approvingAll ? <CircularProgress size={16} color='inherit' /> : null
                     }
                  >
                     {approvingAll ? 'Approving...' : 'Approve All'}
                  </Button>
               </DialogActions>
            </Dialog>
         </Box>
      );
   };

   // Render Analytics tab
   const renderAnalytics = () => {
      // Only owners can see analytics
      if (!isOwner) {
         return null;
      }

      if (!albumId) return null;

      return (
         <Box sx={{ p: isMobile ? 2 : 3 }}>
            <AlbumAnalytics albumId={albumId} />
         </Box>
      );
   };

   if (loading) {
      return (
         <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8 }}>
            <Skeleton
               variant='rectangular'
               width={600}
               height={220}
               sx={{ mb: 3, borderRadius: 3 }}
            />
            <Skeleton variant='rectangular' width={600} height={320} sx={{ borderRadius: 3 }} />
         </Box>
      );
   }
   if (error || !album) {
      return (
         <Box sx={{ p: 4 }}>
            <Typography color='error'>{error || 'Album not found'}</Typography>
         </Box>
      );
   }

   // Album cover logic - prioritize album.coverPhotoUrl from Firestore
   let coverPhotoUrl = album.coverPhotoUrl || '';

   // Fallback to finding cover photo in albumPhotos
   if (!coverPhotoUrl && album.coverPhotoId && albumPhotos.length > 0) {
      const cover = albumPhotos.find((p) => p.id === album.coverPhotoId);
      coverPhotoUrl = cover?.url || '';
   } else if (!coverPhotoUrl && albumPhotos.length > 0) {
      coverPhotoUrl = albumPhotos[0].url;
   }

   return (
      <Box
         sx={{
            p: isMobile ? 1 : 3,
            width: '100%',
            maxWidth: isMobile ? '100vw' : 1100,
            mx: 'auto',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: isMobile ? 'grey.50' : 'background.default',
            overflowX: 'hidden',
            boxSizing: 'border-box',
         }}
      >
         {/* Top Section - Back button and Album Info (FIXED - No scrolling) */}
         <Box
            sx={{
               flexShrink: 0,
            }}
         >
            <Button
               variant='text'
               startIcon={<ArrowBackIcon />}
               sx={{
                  mb: isMobile ? 1 : 1.5,
                  fontWeight: 500,
                  fontSize: isMobile ? '0.875rem' : '1rem',
                  px: isMobile ? 1 : 2,
                  py: isMobile ? 0.5 : 1,
               }}
               onClick={() => navigate('/albums')}
               size={isMobile ? 'small' : 'medium'}
            >
               Back to Albums
            </Button>
            {/* Album Info Card */}
            <Paper
               elevation={0}
               sx={{
                  p: isMobile ? 1.5 : 2,
                  mb: isMobile ? 1 : 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  position: 'relative',
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  overflowX: 'hidden',
               }}
            >
               {/* Album Cover */}
               <Box
                  sx={{
                     width: '100%',
                     height: isMobile ? 100 : 120,
                     mb: 1.5,
                     borderRadius: 1.5,
                     overflow: 'hidden',
                     position: 'relative',
                     ...getDefaultBackgroundStyle('album', albumId || '', coverPhotoUrl),
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     bgcolor: coverPhotoUrl ? 'transparent' : 'grey.50',
                  }}
               >
                  {!coverPhotoUrl && (
                     <Box
                        sx={{
                           display: 'flex',
                           flexDirection: 'column',
                           alignItems: 'center',
                           color: 'grey.300',
                        }}
                     >
                        <InsertPhotoIcon sx={{ fontSize: 40, opacity: 0.6 }} />
                     </Box>
                  )}

                  {/* Cover Photo Actions - Only for Album Owner */}
                  {isOwner && (
                     <Box
                        sx={{
                           position: 'absolute',
                           top: 8,
                           right: 8,
                           display: 'flex',
                           gap: 1,
                        }}
                     >
                        {/* Hidden file input */}
                        <input
                           type='file'
                           id='cover-photo-input'
                           accept='image/*'
                           style={{ display: 'none' }}
                           onChange={handleCoverPhotoChange}
                        />

                        {/* Upload Button */}
                        <Button
                           size='small'
                           variant='contained'
                           startIcon={
                              uploadingCover ? (
                                 <CircularProgress size={16} sx={{ color: 'white' }} />
                              ) : (
                                 <CloudUploadIcon fontSize='small' />
                              )
                           }
                           onClick={() => {
                              const fileInput = document.getElementById(
                                 'cover-photo-input'
                              ) as HTMLInputElement;
                              if (fileInput) fileInput.click();
                           }}
                           disabled={uploadingCover}
                           sx={{
                              bgcolor: '#00C4A7',
                              color: 'white',
                              '&:hover': {
                                 bgcolor: '#007075',
                              },
                              boxShadow: 1,
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              px: 1.5,
                              py: 0.5,
                           }}
                        >
                           {uploadingCover ? 'Uploading...' : coverPhotoUrl ? 'Change' : 'Upload'}
                        </Button>

                        {/* Remove Button - Only show if cover photo exists */}
                        {coverPhotoUrl && !uploadingCover && (
                           <Button
                              size='small'
                              variant='outlined'
                              startIcon={<DeleteIcon fontSize='small' />}
                              onClick={handleRemoveCoverPhoto}
                              sx={{
                                 borderColor: '#dc2626',
                                 color: '#dc2626',
                                 bgcolor: 'rgba(255, 255, 255, 0.9)',
                                 '&:hover': {
                                    borderColor: '#b91c1c',
                                    bgcolor: 'rgba(220, 38, 38, 0.1)',
                                 },
                                 boxShadow: 1,
                                 textTransform: 'none',
                                 fontSize: '0.75rem',
                                 px: 1.5,
                                 py: 0.5,
                              }}
                           >
                              Remove
                           </Button>
                        )}
                     </Box>
                  )}
               </Box>
               {/* Album Info */}
               <Stack
                  direction='column'
                  spacing={isMobile ? 1.5 : 2}
                  alignItems='stretch'
                  justifyContent='flex-start'
                  sx={{
                     width: '100%',
                  }}
               >
                  <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                     <Typography
                        variant={isMobile ? 'h6' : 'h5'}
                        fontWeight={600}
                        gutterBottom
                        sx={{
                           fontSize: isMobile ? '1.1rem' : '1.5rem',
                           lineHeight: 1.3,
                           wordBreak: 'break-word',
                           color: '#004369',
                           mb: 0.5,
                        }}
                     >
                        {album.title}
                     </Typography>
                     {album.description && (
                        <Typography
                           variant='body2'
                           color='text.secondary'
                           sx={{
                              fontSize: isMobile ? '0.8rem' : '0.875rem',
                              lineHeight: 1.5,
                              wordBreak: 'break-word',
                              mb: 1,
                           }}
                        >
                           {album.description}
                        </Typography>
                     )}
                     {album.albumDate && (
                        <Typography
                           variant='body2'
                           color='text.secondary'
                           sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              fontSize: isMobile ? '0.75rem' : '0.85rem',
                              mb: 1,
                              fontWeight: 500,
                           }}
                        >
                           <CalendarTodayIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                           {new Date(album.albumDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                           })}
                        </Typography>
                     )}
                     <Box
                        sx={{
                           display: 'flex',
                           flexDirection: isMobile ? 'column' : 'row',
                           gap: isMobile ? 0.5 : 1,
                           alignItems: isMobile ? 'flex-start' : 'center',
                           mb: 1,
                           flexWrap: 'wrap',
                        }}
                     >
                        <Chip
                           label={`${album.theme || 'General'}`}
                           size='small'
                           sx={{
                              fontSize: '0.75rem',
                              height: '26px',
                              bgcolor: '#E0F7F8',
                              color: '#00C4A7',
                              fontWeight: 500,
                           }}
                        />
                        <Chip
                           icon={<PeopleAltIcon sx={{ fontSize: '0.875rem', color: '#00C4A7' }} />}
                           label={`${members.length > 0 ? members.length : album.memberIds?.length || 1} ${(members.length > 0 ? members.length : album.memberIds?.length || 1) === 1 ? 'member' : 'members'}`}
                           size='small'
                           variant='outlined'
                           sx={{
                              fontSize: '0.75rem',
                              height: '26px',
                              borderColor: '#00C4A7',
                              color: '#00C4A7',
                           }}
                        />
                        <Chip
                           icon={
                              <PhotoLibraryIcon sx={{ fontSize: '0.875rem', color: '#00C4A7' }} />
                           }
                           label={`${albumPhotos.length || (album.photoCount ?? album.photoIds?.length) || 0} ${(albumPhotos.length || (album.photoCount ?? album.photoIds?.length) || 0) === 1 ? 'photo' : 'photos'}`}
                           size='small'
                           variant='outlined'
                           sx={{
                              fontSize: '0.75rem',
                              height: '26px',
                              borderColor: '#00C4A7',
                              color: '#00C4A7',
                           }}
                        />
                     </Box>
                  </Box>
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 1,
                        width: '100%',
                     }}
                  >
                     <AvatarGroup
                        max={isMobile ? 3 : 5}
                        sx={{
                           mb: 1,
                           justifyContent: 'flex-start',
                           width: '100%',
                        }}
                     >
                        {loadingMembers ? (
                           <Avatar
                              sx={{
                                 bgcolor: 'grey.300',
                                 width: isMobile ? 28 : 32,
                                 height: isMobile ? 28 : 32,
                              }}
                           >
                              <CircularProgress size={isMobile ? 14 : 16} />
                           </Avatar>
                        ) : members && members.length > 0 ? (
                           members.map((member) => (
                              <Tooltip
                                 title={
                                    member.displayName || member.email || member.id || 'Unknown'
                                 }
                                 placement='top'
                                 key={member.id}
                              >
                                 <Avatar
                                    src={member.photoURL}
                                    alt={member.displayName || member.email || 'Unknown'}
                                    sx={{
                                       width: isMobile ? 28 : 32,
                                       height: isMobile ? 28 : 32,
                                       border: '2px solid white',
                                       boxShadow: 1,
                                    }}
                                 >
                                    {(
                                       member.displayName?.[0] ||
                                       member.email?.[0] ||
                                       member.id?.[0] ||
                                       'U'
                                    )?.toUpperCase()}
                                 </Avatar>
                              </Tooltip>
                           ))
                        ) : album.memberIds && album.memberIds.length > 0 ? (
                           // Fallback to member IDs if member details haven't loaded yet
                           album.memberIds.map((id) => (
                              <Tooltip title={id} placement='top' key={id}>
                                 <Avatar
                                    sx={{
                                       width: isMobile ? 28 : 32,
                                       height: isMobile ? 28 : 32,
                                       border: '2px solid white',
                                       boxShadow: 1,
                                       bgcolor: 'primary.main',
                                    }}
                                 >
                                    {id[0]}
                                 </Avatar>
                              </Tooltip>
                           ))
                        ) : (
                           <Avatar
                              sx={{
                                 width: isMobile ? 28 : 32,
                                 height: isMobile ? 28 : 32,
                                 border: '2px solid white',
                                 boxShadow: 1,
                                 bgcolor: 'grey.400',
                              }}
                           >
                              U
                           </Avatar>
                        )}
                     </AvatarGroup>
                     <Box
                        sx={{
                           display: 'flex',
                           flexWrap: 'wrap',
                           gap: 1,
                           alignItems: 'center',
                           justifyContent: 'flex-start',
                           width: '100%',
                           '& > *': {
                              flex: '0 0 auto',
                              marginBottom: 1,
                           },
                           '@media (max-width: 1200px)': {
                              '& > *': {
                                 flex: '0 0 calc(50% - 8px)',
                                 minWidth: '200px',
                              },
                           },
                           '@media (max-width: 768px)': {
                              flexDirection: 'column',
                              alignItems: 'stretch',
                              '& > *': {
                                 flex: '1 1 100%',
                                 width: '100%',
                                 minWidth: 'auto',
                                 marginBottom: 0.5,
                              },
                           },
                        }}
                     >
                        {canUpload && (
                           <Button
                              variant='contained'
                              startIcon={<AddPhotoAlternateIcon sx={{ fontSize: '1rem' }} />}
                              onClick={() => setAddPhotosOpen(true)}
                              size='small'
                              sx={{
                                 fontSize: '0.8125rem',
                                 py: 0.75,
                                 px: 1.5,
                                 bgcolor: '#00C4A7',
                                 color: 'white',
                                 textTransform: 'none',
                                 fontWeight: 500,
                                 borderRadius: 1,
                                 '&:hover': {
                                    bgcolor: '#017A7F',
                                 },
                                 '@media (max-width: 768px)': {
                                    width: '100%',
                                 },
                              }}
                           >
                              Add Photos
                           </Button>
                        )}
                        {canUpload && (
                           <Button
                              variant='outlined'
                              startIcon={<AddPhotoAlternateIcon sx={{ fontSize: '1rem' }} />}
                              onClick={() => setDirectUploadOpen(true)}
                              size='small'
                              sx={{
                                 fontSize: '0.8125rem',
                                 py: 0.75,
                                 px: 1.5,
                                 borderColor: '#00C4A7',
                                 color: '#00C4A7',
                                 textTransform: 'none',
                                 fontWeight: 500,
                                 borderRadius: 1,
                                 '&:hover': {
                                    borderColor: '#017A7F',
                                    bgcolor: 'rgba(1, 148, 154, 0.04)',
                                 },
                                 '@media (max-width: 768px)': {
                                    width: '100%',
                                 },
                              }}
                           >
                              Upload New
                           </Button>
                        )}
                        {albumPhotos.length > 0 && (
                           <>
                              {photoSelectionSet.size > 0 && (
                                 <Button
                                    variant='outlined'
                                    onClick={selectAllPhotos}
                                    size={isMobile ? 'small' : 'medium'}
                                    color={photoSelectionSet.size > 0 ? 'secondary' : 'primary'}
                                    sx={{
                                       fontSize: isMobile ? '0.7rem' : '0.8rem',
                                       py: isMobile ? 0.5 : 0.75,
                                       px: isMobile ? 1 : 1.25,
                                       minWidth: '100px',
                                       whiteSpace: 'nowrap',
                                       '@media (max-width: 768px)': {
                                          width: '100%',
                                          minWidth: 'auto',
                                       },
                                    }}
                                 >
                                    {photoSelectionSet.size === albumPhotos.length
                                       ? 'Deselect All'
                                       : 'Select All'}
                                 </Button>
                              )}

                              {photoSelectionSet.size > 0 && (
                                 <Typography
                                    variant='body2'
                                    color={photoSelectionSet.size > 5 ? 'error' : 'text.secondary'}
                                    sx={{
                                       fontSize: isMobile ? '0.7rem' : '0.8rem',
                                       display: 'flex',
                                       alignItems: 'center',
                                       flexWrap: 'wrap',
                                       gap: 0.5,
                                    }}
                                 >
                                    {photoSelectionSet.size} of {albumPhotos.length} photos selected
                                    {photoSelectionSet.size > 5 && (
                                       <Typography
                                          component='span'
                                          variant='caption'
                                          color='error'
                                          sx={{ ml: 1, fontWeight: 600 }}
                                       >
                                          (Max 5 for download)
                                       </Typography>
                                    )}
                                 </Typography>
                              )}

                              {photoSelectionSet.size > 0 && userRole === 'owner' && (
                                 <SharePhotosButton
                                    entityType='album'
                                    entityId={albumId!}
                                    entityTitle={album?.title || 'Album'}
                                    selectedPhotos={Array.from(photoSelectionSet)
                                       .map((id) => albumPhotos.find((p) => p.id === id))
                                       .filter(Boolean)}
                                    entityData={entityData}
                                    variant='outlined'
                                    size={isMobile ? 'small' : 'medium'}
                                    sx={{
                                       fontSize: isMobile ? '0.75rem' : '0.875rem',
                                       py: isMobile ? 0.5 : 0.75,
                                       px: isMobile ? 1.25 : 1.75,
                                       minWidth: isMobile ? 'auto' : '120px',
                                       whiteSpace: 'nowrap',
                                    }}
                                    onSuccess={(result) => {
                                       setSnackbarMessage('Photos shared successfully!');
                                       setSnackbarSeverity('success');
                                       setSnackbarOpen(true);
                                    }}
                                 />
                              )}

                              {photoSelectionSet.size > 0 && photoSelectionSet.size <= 5 && (
                                 <Button
                                    variant='contained'
                                    color='primary'
                                    startIcon={
                                       bulkDownloading ? (
                                          <CircularProgress size={16} sx={{ color: 'white' }} />
                                       ) : (
                                          <DownloadIcon />
                                       )
                                    }
                                    onClick={handleBulkDownload}
                                    disabled={bulkDownloading}
                                    size={isMobile ? 'small' : 'medium'}
                                    sx={{
                                       fontSize: isMobile ? '0.75rem' : '0.875rem',
                                       py: isMobile ? 0.5 : 0.75,
                                       px: isMobile ? 1.25 : 1.75,
                                       minWidth: '140px',
                                       whiteSpace: 'nowrap',
                                       '@media (max-width: 768px)': {
                                          width: '100%',
                                          minWidth: 'auto',
                                       },
                                       bgcolor: bulkDownloading ? 'grey.400' : 'primary.main',
                                       '&:hover': {
                                          bgcolor: bulkDownloading ? 'grey.400' : 'primary.dark',
                                       },
                                    }}
                                 >
                                    {bulkDownloading
                                       ? `Downloading ${bulkDownloadProgress.current}/${bulkDownloadProgress.total}`
                                       : `Download Selected (${photoSelectionSet.size})`}
                                 </Button>
                              )}
                           </>
                        )}
                        {canInvite && (
                           <Button
                              variant='outlined'
                              startIcon={<GroupAddIcon sx={{ fontSize: '1rem' }} />}
                              onClick={() => setInviteDialogOpen(true)}
                              size='small'
                              sx={{
                                 fontSize: '0.8125rem',
                                 py: 0.75,
                                 px: 1.5,
                                 borderColor: '#00C4A7',
                                 color: '#00C4A7',
                                 textTransform: 'none',
                                 fontWeight: 500,
                                 borderRadius: 1,
                                 '&:hover': {
                                    borderColor: '#017A7F',
                                    bgcolor: 'rgba(1, 148, 154, 0.04)',
                                 },
                                 '@media (max-width: 768px)': {
                                    width: '100%',
                                 },
                              }}
                           >
                              Invite Members
                           </Button>
                        )}
                        <Button
                           variant='outlined'
                           startIcon={
                              <ShareIcon sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }} />
                           }
                           onClick={() => setSharingDialogOpen(true)}
                           disabled={!album}
                           sx={{ display: 'none' }}
                        >
                           Share Album
                        </Button>
                     </Box>
                  </Box>
               </Stack>
            </Paper>
         </Box>

         {/* Photos Section - Takes remaining height */}
         <Paper
            elevation={0}
            sx={{
               p: isMobile ? 1.5 : 2,
               borderRadius: 2,
               border: '1px solid',
               borderColor: 'divider',
               bgcolor: 'background.paper',
               width: '100%',
               maxWidth: '100%',
               boxSizing: 'border-box',
               overflowX: 'hidden',
               flex: 1,
               display: 'flex',
               flexDirection: 'column',
               height: isMobile ? '1600px' : '2500px',
               minHeight: '600px',
            }}
         >
            {/* Tab Navigation - Fixed at top */}
            <Box
               sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  overflow: 'hidden',
                  width: '100%',
                  maxWidth: '100%',
                  flexShrink: 0,
               }}
            >
               <Tabs
                  value={validCurrentTab}
                  onChange={(_, newValue) => {
                     // Prevent viewers from accessing grid tab
                     if (userRole === 'viewer' && newValue === 'grid') {
                        console.warn('⚠️ Viewer attempted to access grid tab');
                        return;
                     }
                     setCurrentTab(newValue);
                  }}
                  variant='scrollable'
                  scrollButtons='auto'
                  allowScrollButtonsMobile
                  selectionFollowsFocus={false}
                  TabIndicatorProps={{
                     sx: { height: 3, borderRadius: '3px 3px 0 0' },
                  }}
                  sx={{
                     '& .MuiTab-root': {
                        minHeight: isMobile ? 48 : 48,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: isMobile ? '0.8rem' : '1rem',
                        padding: isMobile ? '12px 16px' : '12px 16px',
                        minWidth: isMobile ? 'auto' : 'auto',
                        opacity: 1,
                        color: 'text.primary',
                        '&.Mui-selected': {
                           color: 'primary.main',
                        },
                        '&:not(.Mui-selected)': {
                           opacity: 0.7,
                        },
                     },
                     '& .MuiTabs-scrollButtons': {
                        '&.Mui-disabled': {
                           opacity: 0.3,
                        },
                     },
                  }}
               >
                  {(isOwner || album?.enableParticipantParticipation === true) && (
                     <Tab
                        icon={<PeopleIcon sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }} />}
                        iconPosition='start'
                        label={
                           isMobile
                              ? `People (${members.length})`
                              : `Participants (${members.length})`
                        }
                        value='participants'
                        sx={{
                           flexDirection: isMobile ? 'row' : 'row',
                           gap: isMobile ? 0.5 : 1,
                        }}
                     />
                  )}
                  {isOwner && (
                     <Tab
                        icon={<InfoIcon sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }} />}
                        iconPosition='start'
                        label='Settings'
                        value='details'
                        sx={{
                           flexDirection: isMobile ? 'row' : 'row',
                           gap: isMobile ? 0.5 : 1,
                        }}
                     />
                  )}
                  {(() => {
                     // Only show "Photos" (grid) tab if user is owner or editor
                     const showPhotosTab =
                        !loading &&
                        album &&
                        userRole &&
                        userRole !== 'viewer' &&
                        (userRole === 'owner' || userRole === 'editor');
                     console.log('🔍 Photos Tab Render Check:', {
                        loading,
                        hasAlbum: !!album,
                        userRole,
                        isOwner,
                        showPhotosTab,
                        isViewer: userRole === 'viewer',
                     });
                     return showPhotosTab;
                  })() && (
                     <Tab
                        icon={<GridViewIcon sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }} />}
                        iconPosition='start'
                        label={
                           isMobile
                              ? `Photos (${albumPhotos.length})`
                              : `Photos (${albumPhotos.length})`
                        }
                        value='grid'
                        sx={{
                           flexDirection: isMobile ? 'row' : 'row',
                           gap: isMobile ? 0.5 : 1,
                        }}
                     />
                  )}
                  <Tab
                     icon={<ViewModuleIcon sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }} />}
                     iconPosition='start'
                     label={`Gallery (${filteredTimelinePhotos.length})`}
                     value='gallery'
                     sx={{
                        flexDirection: isMobile ? 'row' : 'row',
                        gap: isMobile ? 0.5 : 1,
                     }}
                  />
                  <Tab
                     icon={<CollectionsIcon sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }} />}
                     iconPosition='start'
                     label='Photo Groups'
                     value='photoGroups'
                     sx={{
                        flexDirection: isMobile ? 'row' : 'row',
                        gap: isMobile ? 0.5 : 1,
                     }}
                  />
                  {isOwner && (
                     <>
                        <Tab
                           icon={<LinkIcon sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }} />}
                           iconPosition='start'
                           label={isMobile ? 'Share' : 'Shareable Link'}
                           value='sharing'
                           onClick={(e) => {
                              e.stopPropagation();
                              setCurrentTab('sharing');
                           }}
                           sx={{
                              flexDirection: isMobile ? 'row' : 'row',
                              gap: isMobile ? 0.5 : 1,
                              pointerEvents: 'auto !important',
                              cursor: 'pointer !important',
                              '&:hover': {
                                 backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              },
                           }}
                        />
                        {/* Only show Waitlist tab for private albums */}
                        {!isPublicAlbum && (
                           <Tab
                              icon={
                                 <HourglassEmptyIcon
                                    sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}
                                 />
                              }
                              iconPosition='start'
                              label={
                                 isMobile
                                    ? `Waitlist (${waitingList.length})`
                                    : `Waiting List (${waitingList.length})`
                              }
                              value='waitlist'
                              onClick={(e) => {
                                 e.stopPropagation();
                                 setCurrentTab('waitlist');
                              }}
                              sx={{
                                 flexDirection: isMobile ? 'row' : 'row',
                                 gap: isMobile ? 0.5 : 1,
                                 pointerEvents: 'auto !important',
                                 cursor: 'pointer !important',
                                 '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                 },
                              }}
                           />
                        )}
                        <Tab
                           icon={
                              <AnalyticsIcon sx={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }} />
                           }
                           iconPosition='start'
                           label='Analytics'
                           value='analytics'
                           onClick={(e) => {
                              e.stopPropagation();
                              setCurrentTab('analytics');
                           }}
                           sx={{
                              flexDirection: isMobile ? 'row' : 'row',
                              gap: isMobile ? 0.5 : 1,
                              pointerEvents: 'auto !important',
                              cursor: 'pointer !important',
                              '&:hover': {
                                 backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              },
                           }}
                        />
                     </>
                  )}
               </Tabs>
            </Box>

            {/* Sticky Folder Bar - Only visible when scrolling in Photos tab */}
            {validCurrentTab === 'grid' && showStickyFolders && photoFolders.length > 0 && (
               <Box
                  sx={{
                     position: 'sticky',
                     top: 0,
                     zIndex: 10,
                     bgcolor: 'background.paper',
                     borderBottom: '2px solid',
                     borderColor: 'divider',
                     p: isMobile ? 1.5 : 2,
                     minHeight: isMobile ? '60px' : '70px',
                     boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                     display: 'flex',
                     alignItems: 'center',
                     gap: isMobile ? 1 : 1.5,
                     overflowX: 'auto',
                     overflowY: 'hidden',
                     maxWidth: '100%',
                     '&::-webkit-scrollbar': {
                        height: '10px',
                     },
                     '&::-webkit-scrollbar-track': {
                        background: 'rgba(0,0,0,0.05)',
                        borderRadius: '5px',
                     },
                     '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(0,0,0,0.3)',
                        borderRadius: '5px',
                        '&:hover': {
                           background: 'rgba(0,0,0,0.4)',
                        },
                     },
                  }}
               >
                  <Typography
                     variant='caption'
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'text.secondary',
                        mr: 1,
                        whiteSpace: 'nowrap',
                        fontSize: isMobile ? '0.75rem' : '0.85rem',
                        fontWeight: 500,
                     }}
                  >
                     {isMobile ? '📁 Drop here →' : '📁 Drop photo here →'}
                  </Typography>
                  {photoFolders.map((folder) => (
                     <DroppableFolderCard
                        key={folder.id}
                        folder={folder}
                        onDrop={handleDropToFolder}
                     >
                        <Chip
                           icon={<FolderIcon />}
                           label={`${folder.name} (${folder.photoIds.length})`}
                           size={isMobile ? 'medium' : 'medium'}
                           sx={{
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              height: isMobile ? '36px' : '40px',
                              fontSize: isMobile ? '0.85rem' : '0.9rem',
                              '&:hover': {
                                 bgcolor: 'primary.light',
                                 color: 'white',
                              },
                           }}
                           onClick={() => {
                              if (folder.photoIds.length > 0) {
                                 handleViewFolder(folder);
                              }
                           }}
                        />
                     </DroppableFolderCard>
                  ))}
               </Box>
            )}

            {/* Scrollable Tab Content - LARGE HEIGHT */}
            <Box
               ref={scrollContainerRef}
               sx={{
                  height: isMobile ? '1200px' : '2000px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pt: isMobile ? 1.5 : 2,
               }}
            >
               {/* Details View */}
               {validCurrentTab === 'details' && (
                  <Box>
                     <Typography
                        variant={isMobile ? 'h6' : 'h5'}
                        sx={{
                           mb: 3,
                           fontWeight: 600,
                           color: '#004369',
                        }}
                     >
                        Album Settings
                     </Typography>

                     <Stack spacing={2}>
                        {/* Participant Visibility */}
                        <Paper
                           elevation={0}
                           sx={{
                              p: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              bgcolor: 'background.paper',
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 justifyContent: 'space-between',
                                 gap: 2,
                              }}
                           >
                              <Box
                                 sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}
                              >
                                 <Box
                                    sx={{
                                       width: 40,
                                       height: 40,
                                       borderRadius: '50%',
                                       bgcolor: album?.enableParticipantParticipation
                                          ? 'rgba(1, 148, 154, 0.1)'
                                          : 'rgba(156, 163, 175, 0.1)',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       flexShrink: 0,
                                    }}
                                 >
                                    <PeopleIcon
                                       sx={{
                                          color: album?.enableParticipantParticipation
                                             ? '#00C4A7'
                                             : '#9ca3af',
                                          fontSize: 20,
                                       }}
                                    />
                                 </Box>
                                 <Box sx={{ flex: 1 }}>
                                    <Typography
                                       variant='subtitle1'
                                       sx={{ fontWeight: 600, mb: 0.5 }}
                                    >
                                       Participant Visibility
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                       {album?.enableParticipantParticipation
                                          ? 'Participants can see each other and add connections to their network. Participants can also invite others.'
                                          : 'Only you can see album participants and invite others.'}
                                    </Typography>
                                 </Box>
                              </Box>
                              {isOwner && (
                                 <Switch
                                    checked={album?.enableParticipantParticipation === true}
                                    onChange={handleToggleParticipantVisibility}
                                    disabled={updatingSettings}
                                    sx={{
                                       '& .MuiSwitch-switchBase.Mui-checked': {
                                          color: '#00C4A7',
                                       },
                                       '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                          backgroundColor: '#00C4A7',
                                       },
                                    }}
                                 />
                              )}
                           </Box>
                        </Paper>

                        {/* Enable Participant Photo Upload */}
                        <Paper
                           elevation={0}
                           sx={{
                              p: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              bgcolor: 'background.paper',
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 justifyContent: 'space-between',
                                 gap: 2,
                              }}
                           >
                              <Box
                                 sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}
                              >
                                 <Box
                                    sx={{
                                       width: 40,
                                       height: 40,
                                       borderRadius: '50%',
                                       bgcolor: album?.enableParticipantUpload
                                          ? 'rgba(1, 148, 154, 0.1)'
                                          : 'rgba(156, 163, 175, 0.1)',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       flexShrink: 0,
                                    }}
                                 >
                                    <AddPhotoAlternateIcon
                                       sx={{
                                          color: album?.enableParticipantUpload
                                             ? '#00C4A7'
                                             : '#9ca3af',
                                          fontSize: 20,
                                       }}
                                    />
                                 </Box>
                                 <Box sx={{ flex: 1 }}>
                                    <Typography
                                       variant='subtitle1'
                                       sx={{ fontWeight: 600, mb: 0.5 }}
                                    >
                                       Enable Participant Photo Upload
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                       {album?.enableParticipantUpload
                                          ? 'Participants can upload photos to this album.'
                                          : 'Enable to allow participants to upload photos. Otherwise, only you can upload.'}
                                    </Typography>
                                 </Box>
                              </Box>
                              {isOwner && (
                                 <Switch
                                    checked={album?.enableParticipantUpload === true}
                                    onChange={handleToggleParticipantUpload}
                                    disabled={updatingSettings}
                                    sx={{
                                       '& .MuiSwitch-switchBase.Mui-checked': {
                                          color: '#00C4A7',
                                       },
                                       '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                          backgroundColor: '#00C4A7',
                                       },
                                    }}
                                 />
                              )}
                           </Box>
                        </Paper>

                        {/* Enable Participant Invite */}
                        <Paper
                           elevation={0}
                           sx={{
                              p: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              bgcolor: 'background.paper',
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 justifyContent: 'space-between',
                                 gap: 2,
                              }}
                           >
                              <Box
                                 sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}
                              >
                                 <Box
                                    sx={{
                                       width: 40,
                                       height: 40,
                                       borderRadius: '50%',
                                       bgcolor: album?.enableParticipantInvite
                                          ? 'rgba(1, 148, 154, 0.1)'
                                          : 'rgba(156, 163, 175, 0.1)',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       flexShrink: 0,
                                    }}
                                 >
                                    <GroupAddIcon
                                       sx={{
                                          color: album?.enableParticipantInvite
                                             ? '#00C4A7'
                                             : '#9ca3af',
                                          fontSize: 20,
                                       }}
                                    />
                                 </Box>
                                 <Box sx={{ flex: 1 }}>
                                    <Typography
                                       variant='subtitle1'
                                       sx={{ fontWeight: 600, mb: 0.5 }}
                                    >
                                       Enable Participant Invitations
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                       {album?.enableParticipantInvite
                                          ? 'Participants can invite other members to this album.'
                                          : 'Enable to allow participants to invite others. Otherwise, only you can invite.'}
                                    </Typography>
                                 </Box>
                              </Box>
                              {isOwner && (
                                 <Switch
                                    checked={album?.enableParticipantInvite === true}
                                    onChange={handleToggleParticipantInvite}
                                    disabled={updatingSettings}
                                    sx={{
                                       '& .MuiSwitch-switchBase.Mui-checked': {
                                          color: '#00C4A7',
                                       },
                                       '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                          backgroundColor: '#00C4A7',
                                       },
                                    }}
                                 />
                              )}
                           </Box>
                        </Paper>

                        {/* Allow photographer upload on public page */}
                        <Paper
                           elevation={0}
                           sx={{
                              p: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              bgcolor: 'background.paper',
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 justifyContent: 'space-between',
                                 gap: 2,
                              }}
                           >
                              <Box
                                 sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}
                              >
                                 <Box
                                    sx={{
                                       width: 40,
                                       height: 40,
                                       borderRadius: '50%',
                                       bgcolor: album?.allowPhotographerUpload
                                          ? 'rgba(1, 148, 154, 0.1)'
                                          : 'rgba(156, 163, 175, 0.1)',
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       flexShrink: 0,
                                    }}
                                 >
                                    <CameraAltIcon
                                       sx={{
                                          color: album?.allowPhotographerUpload
                                             ? '#00C4A7'
                                             : '#9ca3af',
                                          fontSize: 20,
                                       }}
                                    />
                                 </Box>
                                 <Box sx={{ flex: 1 }}>
                                    <Typography
                                       variant='subtitle1'
                                       sx={{ fontWeight: 600, mb: 0.5 }}
                                    >
                                       Allow photographer upload on public page
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                       {album?.allowPhotographerUpload
                                          ? 'Event photographers can upload photos from the public album page.'
                                          : 'When enabled, event photographers can upload photos from the public album page. Only applies to albums linked to an event.'}
                                    </Typography>
                                 </Box>
                              </Box>
                              {isOwner && (
                                 <Switch
                                    checked={album?.allowPhotographerUpload === true}
                                    onChange={handleTogglePhotographerUpload}
                                    disabled={updatingSettings}
                                    sx={{
                                       '& .MuiSwitch-switchBase.Mui-checked': {
                                          color: '#00C4A7',
                                       },
                                       '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                          backgroundColor: '#00C4A7',
                                       },
                                    }}
                                 />
                              )}
                           </Box>
                        </Paper>

                        {/* Delete Album Section */}
                        {isOwner && (
                           <Paper
                              elevation={0}
                              sx={{
                                 p: 2,
                                 borderRadius: 2,
                                 bgcolor: 'background.paper',
                                 mt: 3,
                              }}
                           >
                              <Box>
                                 <Typography
                                    variant='subtitle1'
                                    sx={{ fontWeight: 600, mb: 0.5, color: '#ef4444' }}
                                 >
                                    Delete Album
                                 </Typography>
                                 <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                                    Once you delete this album, there is no going back. This will
                                    permanently delete the album and all its settings. Photos will
                                    remain in your library.
                                 </Typography>
                                 <Button
                                    variant='outlined'
                                    color='error'
                                    startIcon={<DeleteIcon />}
                                    onClick={() => setDeleteAlbumDialog(true)}
                                    sx={{
                                       borderWidth: 2,
                                       '&:hover': {
                                          borderWidth: 2,
                                          bgcolor: 'rgba(239, 68, 68, 0.1)',
                                       },
                                    }}
                                 >
                                    Delete This Album
                                 </Button>
                              </Box>
                           </Paper>
                        )}
                     </Stack>
                  </Box>
               )}

               {/* Participants View */}
               {validCurrentTab === 'participants' && (
                  <>
                     <Box
                        sx={{
                           display: 'flex',
                           justifyContent: 'space-between',
                           alignItems: isMobile ? 'flex-start' : 'center',
                           mb: isMobile ? 2 : 3,
                           flexWrap: 'wrap',
                           gap: isMobile ? 1.5 : 2,
                           flexDirection: isMobile ? 'column' : 'row',
                        }}
                     >
                        <Typography
                           variant={isMobile ? 'subtitle1' : 'h6'}
                           sx={{
                              fontWeight: 600,
                              fontSize: isMobile ? '1rem' : '1.25rem',
                           }}
                        >
                           Album Participants ({members.length})
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, width: isMobile ? '100%' : 'auto' }}>
                           {/* Send Reminder Button - only visible to owner/editor */}
                           {(userRole === 'owner' || userRole === 'editor') && (
                              <Button
                                 variant='contained'
                                 color='primary'
                                 startIcon={<ChatIcon />}
                                 onClick={handleOpenReminderModal}
                                 size={isMobile ? 'small' : 'small'}
                                 sx={{
                                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                                    py: isMobile ? 0.5 : 0.5,
                                    px: isMobile ? 1.5 : 1.5,
                                    borderRadius: isMobile ? 2 : 1,
                                    flex: isMobile ? 1 : 'none',
                                 }}
                              >
                                 Send Reminder
                              </Button>
                           )}
                           {canInvite && (
                              <Button
                                 variant='contained'
                                 startIcon={
                                    <PersonAddIcon
                                       sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
                                    />
                                 }
                                 onClick={() => setInviteDialogOpen(true)}
                                 size={isMobile ? 'small' : 'small'}
                                 sx={{
                                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                                    py: isMobile ? 0.5 : 0.5,
                                    px: isMobile ? 1.5 : 1.5,
                                    borderRadius: isMobile ? 2 : 1,
                                    flex: isMobile ? 1 : 'none',
                                 }}
                              >
                                 Invite People
                              </Button>
                           )}
                        </Box>
                     </Box>

                     {loadingMembers ? (
                        <Box
                           sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              py: isMobile ? 3 : 4,
                           }}
                        >
                           <CircularProgress size={isMobile ? 32 : 40} />
                        </Box>
                     ) : members.length === 0 ? (
                        <Paper
                           sx={{
                              p: isMobile ? 4 : 6,
                              textAlign: 'center',
                              bgcolor: 'background.paper',
                              borderRadius: isMobile ? 2 : 2,
                           }}
                        >
                           <PeopleIcon
                              sx={{
                                 fontSize: isMobile ? 40 : 48,
                                 color: 'text.secondary',
                                 mb: isMobile ? 1.5 : 2,
                              }}
                           />
                           <Typography
                              variant={isMobile ? 'subtitle1' : 'h6'}
                              color='text.secondary'
                              gutterBottom
                              sx={{
                                 fontSize: isMobile ? '1rem' : '1.25rem',
                                 fontWeight: 600,
                              }}
                           >
                              No participants yet
                           </Typography>
                           <Typography
                              variant='body2'
                              color='text.secondary'
                              sx={{
                                 mb: isMobile ? 2 : 3,
                                 fontSize: isMobile ? '0.9rem' : '0.875rem',
                                 lineHeight: 1.5,
                              }}
                           >
                              Invite people to share and collaborate on this album
                           </Typography>
                           {canInvite && (
                              <Button
                                 variant='contained'
                                 startIcon={
                                    <PersonAddIcon
                                       sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
                                    />
                                 }
                                 onClick={() => setInviteDialogOpen(true)}
                                 size={isMobile ? 'small' : 'medium'}
                                 sx={{
                                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                                    py: isMobile ? 0.5 : 1,
                                    px: isMobile ? 2 : 2,
                                    borderRadius: isMobile ? 2 : 1,
                                 }}
                              >
                                 Invite People
                              </Button>
                           )}
                        </Paper>
                     ) : (
                        <Paper sx={{ borderRadius: 2 }}>
                           <List>
                              {members.map((member, index) => (
                                 <ListItem
                                    key={member.id}
                                    onClick={() => handleOpenParticipantProfile(member)}
                                    sx={{
                                       cursor: 'pointer',
                                       py: isMobile ? 1.5 : 2,
                                       px: isMobile ? 2 : 3,
                                       borderBottom:
                                          index < members.length - 1 ? '1px solid' : 'none',
                                       borderColor: 'divider',
                                       flexDirection: isMobile ? 'column' : 'row',
                                       alignItems: isMobile ? 'flex-start' : 'center',
                                       '&:hover': {
                                          bgcolor: 'action.hover',
                                       },
                                    }}
                                 >
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          width: '100%',
                                          alignItems: 'center',
                                       }}
                                    >
                                       <ListItemAvatar>
                                          <Avatar
                                             src={
                                                member.profileData?.photoURL ||
                                                member.profileData?.profilePicture ||
                                                member.profileData?.avatar ||
                                                member.photoURL ||
                                                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                                   member.displayName || member.email || 'User'
                                                )}&background=6366f1&color=ffffff&size=256`
                                             }
                                             sx={{
                                                width: isMobile ? 40 : 48,
                                                height: isMobile ? 40 : 48,
                                                fontSize: isMobile ? '1rem' : '1.2rem',
                                             }}
                                          >
                                             {!(
                                                member.profileData?.photoURL ||
                                                member.profileData?.profilePicture ||
                                                member.profileData?.avatar ||
                                                member.photoURL
                                             ) &&
                                                (member.profileData?.displayName ||
                                                   member.profileData?.fullName ||
                                                   member.profileData?.name ||
                                                   member.displayName ||
                                                   member.email ||
                                                   'U')[0].toUpperCase()}
                                          </Avatar>
                                       </ListItemAvatar>
                                       <ListItemText
                                          primary={
                                             <Box
                                                sx={{
                                                   display: 'flex',
                                                   alignItems: 'center',
                                                   gap: 1,
                                                   flexWrap: 'wrap',
                                                }}
                                             >
                                                <Typography
                                                   variant='body1'
                                                   sx={{
                                                      fontWeight: 600,
                                                      fontSize: isMobile ? '0.9rem' : '1rem',
                                                   }}
                                                >
                                                   {member.profileData?.displayName ||
                                                      member.profileData?.fullName ||
                                                      member.profileData?.name ||
                                                      member.displayName ||
                                                      member.email?.split('@')[0] ||
                                                      'Unknown'}
                                                </Typography>
                                                {isOwner && (
                                                   <Chip
                                                      label={member.role}
                                                      size='small'
                                                      variant='outlined'
                                                      sx={{
                                                         textTransform: 'capitalize',
                                                         fontSize: isMobile ? '0.7rem' : '0.75rem',
                                                         height: isMobile ? 18 : 20,
                                                         ...(member.role === 'owner' && {
                                                            color: 'primary.main',
                                                            borderColor: 'primary.main',
                                                         }),
                                                         ...(member.role === 'editor' && {
                                                            color: 'secondary.main',
                                                            borderColor: 'secondary.main',
                                                         }),
                                                      }}
                                                   />
                                                )}
                                                {currentUser &&
                                                   member.id !== currentUser.uid &&
                                                   (() => {
                                                      const status = connectionStatuses.get(
                                                         member.id
                                                      );
                                                      if (status?.status === 'connected') {
                                                         return (
                                                            <Chip
                                                               label='Connected'
                                                               size='small'
                                                               sx={{
                                                                  fontSize: isMobile
                                                                     ? '0.65rem'
                                                                     : '0.7rem',
                                                                  height: isMobile ? 18 : 20,
                                                                  bgcolor: '#dcfce7',
                                                                  color: '#166534',
                                                                  fontWeight: 500,
                                                               }}
                                                            />
                                                         );
                                                      } else if (
                                                         status?.status === 'pending_sent'
                                                      ) {
                                                         return (
                                                            <Chip
                                                               label='Request Sent'
                                                               size='small'
                                                               sx={{
                                                                  fontSize: isMobile
                                                                     ? '0.65rem'
                                                                     : '0.7rem',
                                                                  height: isMobile ? 18 : 20,
                                                                  bgcolor: '#fef3c7',
                                                                  color: '#92400e',
                                                                  fontWeight: 500,
                                                               }}
                                                            />
                                                         );
                                                      } else if (
                                                         status?.status === 'pending_received'
                                                      ) {
                                                         return (
                                                            <Chip
                                                               label='Pending'
                                                               size='small'
                                                               sx={{
                                                                  fontSize: isMobile
                                                                     ? '0.65rem'
                                                                     : '0.7rem',
                                                                  height: isMobile ? 18 : 20,
                                                                  bgcolor: '#dbeafe',
                                                                  color: '#1e40af',
                                                                  fontWeight: 500,
                                                               }}
                                                            />
                                                         );
                                                      }
                                                      return null;
                                                   })()}
                                             </Box>
                                          }
                                          secondary={
                                             <Typography
                                                variant='body2'
                                                color='text.secondary'
                                                sx={{
                                                   fontSize: isMobile ? '0.8rem' : '0.875rem',
                                                }}
                                             >
                                                {member.email || 'No email'}
                                             </Typography>
                                          }
                                       />
                                       {!isMobile &&
                                          currentUser &&
                                          member.id !== currentUser.uid && (
                                             <ListItemSecondaryAction>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                   {(() => {
                                                      const status = connectionStatuses.get(
                                                         member.id
                                                      );
                                                      const isLoading = addingContact === member.id;

                                                      if (status?.status === 'connected') {
                                                         return (
                                                            <Chip
                                                               label='Connected'
                                                               size='small'
                                                               color='success'
                                                               icon={<CheckCircleIcon />}
                                                            />
                                                         );
                                                      } else if (
                                                         status?.status === 'pending_sent'
                                                      ) {
                                                         return (
                                                            <Chip
                                                               label='Request Sent'
                                                               size='small'
                                                               color='warning'
                                                               variant='outlined'
                                                            />
                                                         );
                                                      } else if (
                                                         status?.status === 'pending_received'
                                                      ) {
                                                         return (
                                                            <>
                                                               <Button
                                                                  variant='contained'
                                                                  size='small'
                                                                  color='primary'
                                                                  onClick={(e) => {
                                                                     e.stopPropagation();
                                                                     handleAcceptConnection(
                                                                        member,
                                                                        status.requestId!
                                                                     );
                                                                  }}
                                                                  disabled={isLoading}
                                                                  sx={{
                                                                     textTransform: 'none',
                                                                     borderRadius: 2,
                                                                     px: 2,
                                                                     minWidth: 70,
                                                                  }}
                                                               >
                                                                  {isLoading ? (
                                                                     <CircularProgress size={16} />
                                                                  ) : (
                                                                     'Accept'
                                                                  )}
                                                               </Button>
                                                               <Button
                                                                  variant='outlined'
                                                                  size='small'
                                                                  color='error'
                                                                  onClick={(e) => {
                                                                     e.stopPropagation();
                                                                     handleDeclineConnection(
                                                                        member,
                                                                        status.requestId!
                                                                     );
                                                                  }}
                                                                  disabled={isLoading}
                                                                  sx={{
                                                                     textTransform: 'none',
                                                                     borderRadius: 2,
                                                                     px: 2,
                                                                     minWidth: 70,
                                                                  }}
                                                               >
                                                                  {isLoading ? (
                                                                     <CircularProgress size={16} />
                                                                  ) : (
                                                                     'Decline'
                                                                  )}
                                                               </Button>
                                                            </>
                                                         );
                                                      } else {
                                                         return (
                                                            <Button
                                                               variant='outlined'
                                                               size='small'
                                                               onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  addToContacts(member);
                                                               }}
                                                               disabled={isLoading}
                                                               startIcon={
                                                                  isLoading ? (
                                                                     <CircularProgress size={16} />
                                                                  ) : null
                                                               }
                                                               sx={{
                                                                  textTransform: 'none',
                                                                  borderRadius: 2,
                                                                  px: 2,
                                                               }}
                                                            >
                                                               {isLoading
                                                                  ? 'Connecting...'
                                                                  : 'Connect'}
                                                            </Button>
                                                         );
                                                      }
                                                   })()}
                                                   {canInvite && (
                                                      <>
                                                         <IconButton
                                                            size='small'
                                                            onClick={(e) => {
                                                               e.stopPropagation();
                                                               handleEditMember(member);
                                                            }}
                                                            sx={{ color: 'secondary.main' }}
                                                         >
                                                            <EditIcon />
                                                         </IconButton>
                                                         <IconButton
                                                            size='small'
                                                            onClick={(e) => {
                                                               e.stopPropagation();
                                                               handleDeleteMember(member);
                                                            }}
                                                            sx={{ color: 'error.main' }}
                                                         >
                                                            <DeleteIcon />
                                                         </IconButton>
                                                      </>
                                                   )}
                                                </Box>
                                             </ListItemSecondaryAction>
                                          )}
                                    </Box>
                                    {isMobile && currentUser && member.id !== currentUser.uid && (
                                       <Box
                                          sx={{
                                             display: 'flex',
                                             gap: 1,
                                             mt: 1,
                                             ml: 7,
                                          }}
                                       >
                                          {(() => {
                                             const status = connectionStatuses.get(member.id);
                                             const isLoading = addingContact === member.id;

                                             if (status?.status === 'connected') {
                                                return (
                                                   <Chip
                                                      label='Connected'
                                                      size='small'
                                                      color='success'
                                                      icon={<CheckCircleIcon />}
                                                   />
                                                );
                                             } else if (status?.status === 'pending_sent') {
                                                return (
                                                   <Chip
                                                      label='Request Sent'
                                                      size='small'
                                                      color='warning'
                                                      variant='outlined'
                                                   />
                                                );
                                             } else if (status?.status === 'pending_received') {
                                                return (
                                                   <>
                                                      <Button
                                                         variant='contained'
                                                         size='small'
                                                         color='primary'
                                                         onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleAcceptConnection(
                                                               member,
                                                               status.requestId!
                                                            );
                                                         }}
                                                         disabled={isLoading}
                                                         sx={{
                                                            textTransform: 'none',
                                                            borderRadius: 2,
                                                            px: 2,
                                                            minWidth: 70,
                                                         }}
                                                      >
                                                         {isLoading ? (
                                                            <CircularProgress size={16} />
                                                         ) : (
                                                            'Accept'
                                                         )}
                                                      </Button>
                                                      <Button
                                                         variant='outlined'
                                                         size='small'
                                                         color='error'
                                                         onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeclineConnection(
                                                               member,
                                                               status.requestId!
                                                            );
                                                         }}
                                                         disabled={isLoading}
                                                         sx={{
                                                            textTransform: 'none',
                                                            borderRadius: 2,
                                                            px: 2,
                                                            minWidth: 70,
                                                         }}
                                                      >
                                                         {isLoading ? (
                                                            <CircularProgress size={16} />
                                                         ) : (
                                                            'Decline'
                                                         )}
                                                      </Button>
                                                   </>
                                                );
                                             } else {
                                                return (
                                                   <Button
                                                      variant='outlined'
                                                      size='small'
                                                      onClick={(e) => {
                                                         e.stopPropagation();
                                                         addToContacts(member);
                                                      }}
                                                      disabled={isLoading}
                                                      startIcon={
                                                         isLoading ? (
                                                            <CircularProgress size={16} />
                                                         ) : null
                                                      }
                                                      sx={{
                                                         textTransform: 'none',
                                                         borderRadius: 2,
                                                         px: 2,
                                                      }}
                                                   >
                                                      {isLoading ? 'Connecting...' : 'Connect'}
                                                   </Button>
                                                );
                                             }
                                          })()}
                                          {canInvite && (
                                             <>
                                                <IconButton
                                                   size='small'
                                                   onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleEditMember(member);
                                                   }}
                                                   sx={{ color: 'secondary.main' }}
                                                >
                                                   <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                   size='small'
                                                   onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleDeleteMember(member);
                                                   }}
                                                   sx={{ color: 'error.main' }}
                                                >
                                                   <DeleteIcon />
                                                </IconButton>
                                             </>
                                          )}
                                       </Box>
                                    )}
                                 </ListItem>
                              ))}
                           </List>
                        </Paper>
                     )}
                  </>
               )}

               {/* Grid View */}
               {validCurrentTab === 'grid' &&
                  !loading &&
                  album &&
                  userRole &&
                  userRole !== 'viewer' &&
                  (userRole === 'owner' || userRole === 'editor') && (
                     <>
                        {/* Only show loading spinner on INITIAL load (no photos yet) */}
                        {loadingAlbumPhotos && albumPhotos.length === 0 ? (
                           <Box
                              sx={{
                                 display: 'flex',
                                 flexDirection: 'column',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 py: 8,
                              }}
                           >
                              <CircularProgress size={40} sx={{ color: '#00C4A7', mb: 2 }} />
                              <Typography variant='body2' color='text.secondary'>
                                 Loading photos...
                              </Typography>
                           </Box>
                        ) : (
                           <>
                              <Box
                                 sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: isMobile ? 'flex-start' : 'center',
                                    mb: isMobile ? 2 : 3,
                                    flexWrap: 'wrap',
                                    gap: isMobile ? 1.5 : 2,
                                    flexDirection: isMobile ? 'column' : 'row',
                                 }}
                              >
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       alignItems: 'center',
                                       gap: isMobile ? 1 : 2,
                                       flexDirection: isMobile ? 'column' : 'row',
                                       width: isMobile ? '100%' : 'auto',
                                    }}
                                 >
                                    <Typography
                                       variant={isMobile ? 'subtitle1' : 'h6'}
                                       sx={{
                                          fontWeight: 600,
                                          fontSize: isMobile ? '1rem' : '1.25rem',
                                       }}
                                    >
                                       Photos in Album ({albumPhotos.length})
                                    </Typography>
                                    {albumPhotos.length > 0 && (
                                       <>
                                          <Button
                                             variant='outlined'
                                             size='small'
                                             startIcon={
                                                <PhotoLibraryIcon
                                                   sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
                                                />
                                             }
                                             onClick={() => {
                                                setCurrentPage(1);
                                                setViewAllPhotosOpen(true);
                                             }}
                                             sx={{
                                                borderRadius: isMobile ? 2 : 2,
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: isMobile ? '0.8rem' : '0.875rem',
                                                py: isMobile ? 0.5 : 0.5,
                                                px: isMobile ? 1.5 : 1.5,
                                                width: isMobile ? '100%' : 'auto',
                                             }}
                                          >
                                             View All ({albumPhotos.length})
                                          </Button>
                                          <Button
                                             variant='contained'
                                             size='small'
                                             startIcon={
                                                <CreateNewFolderIcon
                                                   sx={{ fontSize: isMobile ? '1rem' : '1.25rem' }}
                                                />
                                             }
                                             onClick={() => setFolderModalOpen(true)}
                                             sx={{
                                                borderRadius: isMobile ? 2 : 2,
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: isMobile ? '0.8rem' : '0.875rem',
                                                py: isMobile ? 0.5 : 0.5,
                                                px: isMobile ? 1.5 : 1.5,
                                                width: isMobile ? '100%' : 'auto',
                                                background:
                                                   'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                                                '&:hover': {
                                                   background:
                                                      'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                                                },
                                             }}
                                          >
                                             Group Photos
                                          </Button>
                                       </>
                                    )}
                                 </Box>
                              </Box>

                              {albumPhotos.length > 0 ? (
                                 groupByDate && groupedPhotos ? (
                                    // Grouped by date view
                                    Object.entries(groupedPhotos).map(([date, photos]) => (
                                       <Box key={date} sx={{ mb: isMobile ? 3 : 4 }}>
                                          <Typography
                                             variant={isMobile ? 'subtitle1' : 'h6'}
                                             sx={{
                                                mb: isMobile ? 1.5 : 2,
                                                fontWeight: 600,
                                                color: 'primary.main',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                fontSize: isMobile ? '1rem' : '1.25rem',
                                             }}
                                          >
                                             📅 {date} ({photos.length} photo
                                             {photos.length !== 1 ? 's' : ''})
                                          </Typography>
                                          <Grid
                                             container
                                             spacing={isMobile ? 1.5 : 2}
                                             sx={{ width: '100%', margin: 0 }}
                                          >
                                             {photos.map((photo) => (
                                                <Grid
                                                   item
                                                   xs={12}
                                                   sm={6}
                                                   md={4}
                                                   lg={3}
                                                   xl={2}
                                                   key={photo.id}
                                                >
                                                   <Card
                                                      sx={{
                                                         borderRadius: isMobile ? 2 : 3,
                                                         boxShadow: isMobile ? 1 : 2,
                                                         overflow: 'hidden',
                                                         height: isMobile ? 160 : 180,
                                                         display: 'flex',
                                                         flexDirection: 'column',
                                                         justifyContent: 'center',
                                                         position: 'relative',
                                                         border: photoSelectionSet.has(photo.id)
                                                            ? '3px solid #1976d2'
                                                            : 'none',
                                                      }}
                                                   >
                                                      {/* Cover photo indicator */}
                                                      {album?.coverPhotoId === photo.id && (
                                                         <Box
                                                            sx={{
                                                               position: 'absolute',
                                                               top: isMobile ? 6 : 8,
                                                               left: isMobile ? 6 : 8,
                                                               zIndex: 2,
                                                               bgcolor: 'primary.main',
                                                               color: 'white',
                                                               borderRadius: isMobile ? 0.5 : 1,
                                                               px: isMobile ? 0.5 : 1,
                                                               py: isMobile ? 0.25 : 0.5,
                                                               display: 'flex',
                                                               alignItems: 'center',
                                                               gap: isMobile ? 0.25 : 0.5,
                                                            }}
                                                         >
                                                            <StarIcon
                                                               sx={{ fontSize: isMobile ? 12 : 16 }}
                                                            />
                                                            <Typography
                                                               variant='caption'
                                                               fontWeight={600}
                                                               sx={{
                                                                  fontSize: isMobile
                                                                     ? '0.6rem'
                                                                     : '0.75rem',
                                                               }}
                                                            >
                                                               Cover
                                                            </Typography>
                                                         </Box>
                                                      )}

                                                      {/* Photo action buttons */}
                                                      <Box
                                                         sx={{
                                                            position: 'absolute',
                                                            top: isMobile ? 6 : 8,
                                                            right: isMobile ? 6 : 8,
                                                            zIndex: 2,
                                                            display: 'flex',
                                                            gap: 0.5,
                                                         }}
                                                      >
                                                         {/* Download photo button - visible to all users */}
                                                         <IconButton
                                                            sx={{
                                                               bgcolor: 'rgba(0,0,0,0.5)',
                                                               color: 'white',
                                                               width: isMobile ? 24 : 28,
                                                               height: isMobile ? 24 : 28,
                                                               '&:hover': {
                                                                  bgcolor: 'rgba(0,0,0,0.7)',
                                                               },
                                                            }}
                                                            size='small'
                                                            disabled={bulkDownloading}
                                                            onClick={(e) => {
                                                               e.stopPropagation();
                                                               handleDownloadPhoto(photo);
                                                            }}
                                                            title={
                                                               bulkDownloading
                                                                  ? 'Bulk download in progress'
                                                                  : downloadingPhotos.has(photo.id)
                                                                    ? 'Downloading...'
                                                                    : 'Download photo'
                                                            }
                                                         >
                                                            {downloadingPhotos.has(photo.id) ? (
                                                               <CircularProgress
                                                                  size={16}
                                                                  sx={{ color: 'red' }}
                                                               />
                                                            ) : (
                                                               <DownloadIcon
                                                                  fontSize={
                                                                     isMobile ? 'small' : 'small'
                                                                  }
                                                               />
                                                            )}
                                                         </IconButton>
                                                         {/* Delete photo button - only for owners and editors */}
                                                         {(userRole === 'owner' || userRole === 'editor') && (
                                                            <IconButton
                                                               sx={{
                                                                  bgcolor: 'rgba(211, 47, 47, 0.8)',
                                                                  color: 'white',
                                                                  width: isMobile ? 24 : 28,
                                                                  height: isMobile ? 24 : 28,
                                                                  '&:hover': {
                                                                     bgcolor: 'rgba(211, 47, 47, 1)',
                                                                  },
                                                               }}
                                                               size='small'
                                                               onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  setSelectedPhotoForMenu(photo);
                                                                  setDeletePhotoDialogOpen(true);
                                                               }}
                                                               title='Remove photo from album'
                                                            >
                                                               <DeleteIcon
                                                                  fontSize={
                                                                     isMobile ? 'small' : 'small'
                                                                  }
                                                               />
                                                            </IconButton>
                                                         )}
                                                         {/* Photo menu button - only for album owners */}
                                                         {userRole === 'owner' && (
                                                            <IconButton
                                                               sx={{
                                                                  bgcolor: 'rgba(0,0,0,0.5)',
                                                                  color: 'white',
                                                                  width: isMobile ? 24 : 28,
                                                                  height: isMobile ? 24 : 28,
                                                                  '&:hover': {
                                                                     bgcolor: 'rgba(0,0,0,0.7)',
                                                                  },
                                                               }}
                                                               size='small'
                                                               onClick={(e) =>
                                                                  handlePhotoMenuOpen(e, photo)
                                                               }
                                                            >
                                                               <MoreVertIcon
                                                                  sx={{
                                                                     fontSize: isMobile
                                                                        ? '1rem'
                                                                        : '1.25rem',
                                                                  }}
                                                               />
                                                            </IconButton>
                                                         )}
                                                      </Box>

                                                      {/* Selection checkbox - visible to all users */}
                                                      <Box
                                                         position='absolute'
                                                         top={8}
                                                         right={40}
                                                         bgcolor={
                                                            photoSelectionSet.has(photo.id)
                                                               ? '#1976d2'
                                                               : 'rgba(0,0,0,0.6)'
                                                         }
                                                         borderRadius='50%'
                                                         p={0.3}
                                                         sx={{
                                                            transition: 'all 0.2s ease',
                                                            border: photoSelectionSet.has(photo.id)
                                                               ? '2px solid white'
                                                               : '2px solid transparent',
                                                            zIndex: 15,
                                                         }}
                                                      >
                                                         <Checkbox
                                                            checked={photoSelectionSet.has(
                                                               photo.id
                                                            )}
                                                            onChange={(e) => {
                                                               e.stopPropagation();
                                                               togglePhotoSelection(photo.id);
                                                            }}
                                                            size='small'
                                                            sx={{
                                                               color: 'white',
                                                               p: 0,
                                                               '&.Mui-checked': {
                                                                  color: 'white',
                                                               },
                                                            }}
                                                         />
                                                      </Box>

                                                      {isVideo(photo) ? (
                                                         <Box
                                                            component='video'
                                                            src={photo.url || photo.videoUrl}
                                                            poster={photo.thumbnailUrl}
                                                            muted
                                                            loop
                                                            playsInline
                                                            height={isMobile ? 120 : 140}
                                                            sx={{
                                                               objectFit: 'cover',
                                                               width: '100%',
                                                               cursor: 'pointer',
                                                               transition:
                                                                  'transform 0.2s ease-in-out',
                                                               opacity: photoSelectionSet.has(
                                                                  photo.id
                                                               )
                                                                  ? 0.8
                                                                  : 1,
                                                               '&:hover': {
                                                                  transform: isMobile
                                                                     ? 'none'
                                                                     : 'scale(1.02)',
                                                               },
                                                            }}
                                                            onClick={() =>
                                                               openPhotoModal(
                                                                  albumPhotos.findIndex(
                                                                     (p) => p.id === photo.id
                                                                  )
                                                               )
                                                            }
                                                         />
                                                      ) : (
                                                         <CardMedia
                                                            component='img'
                                                            height={isMobile ? '120' : '140'}
                                                            image={photo.url || '/placeholder.jpg'}
                                                            alt={photo.title || 'Photo'}
                                                            sx={{
                                                               objectFit: 'cover',
                                                               width: '100%',
                                                               cursor: 'pointer',
                                                               transition:
                                                                  'transform 0.2s ease-in-out',
                                                               opacity: photoSelectionSet.has(
                                                                  photo.id
                                                               )
                                                                  ? 0.8
                                                                  : 1,
                                                               '&:hover': {
                                                                  transform: isMobile
                                                                     ? 'none'
                                                                     : 'scale(1.02)',
                                                               },
                                                            }}
                                                            onClick={() =>
                                                               openPhotoModal(
                                                                  albumPhotos.findIndex(
                                                                     (p) => p.id === photo.id
                                                                  )
                                                               )
                                                            }
                                                         />
                                                      )}
                                                      <CardContent
                                                         sx={{
                                                            p: isMobile ? 0.5 : 1,
                                                            pt: isMobile ? 0.5 : 1,
                                                         }}
                                                      >
                                                         {/* Date centered */}
                                                         {getBestPhotoDate(photo) && (
                                                            <Box
                                                               sx={{
                                                                  display: 'flex',
                                                                  justifyContent: 'center',
                                                                  alignItems: 'center',
                                                                  gap: 0.5,
                                                                  mb: 0.5,
                                                               }}
                                                            >
                                                               <Typography
                                                                  variant='caption'
                                                                  color='text.secondary'
                                                                  sx={{
                                                                     fontSize: isMobile
                                                                        ? '0.65rem'
                                                                        : '0.75rem',
                                                                     fontWeight: 500,
                                                                  }}
                                                               >
                                                                  📅 {formatPhotoDate(photo)}
                                                               </Typography>
                                                               {formatLocationCoordinates(
                                                                  photo.location
                                                               ) && (
                                                                  <Typography
                                                                     variant='caption'
                                                                     color='text.secondary'
                                                                     sx={{
                                                                        fontSize: isMobile
                                                                           ? '0.65rem'
                                                                           : '0.75rem',
                                                                     }}
                                                                  >
                                                                     📍
                                                                  </Typography>
                                                               )}
                                                            </Box>
                                                         )}
                                                         <Box
                                                            sx={{
                                                               display: 'flex',
                                                               alignItems: 'center',
                                                               justifyContent: 'space-between',
                                                            }}
                                                         >
                                                            {photo.uploaderProfile &&
                                                               (photo.uploaderProfile.displayName ||
                                                                  photo.uploaderProfile.name ||
                                                                  photo.uploaderProfile
                                                                     .fullName) && (
                                                                  <Box
                                                                     onMouseDown={(e) => e.stopPropagation()}
                                                                     onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const uid = (photo as { userId?: string; uploadedBy?: string }).userId || (photo as { userId?: string; uploadedBy?: string }).uploadedBy;
                                                                        if (uid) setProfilePopoverUserId(uid);
                                                                     }}
                                                                     sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        cursor: (photo as { userId?: string; uploadedBy?: string }).userId || (photo as { userId?: string; uploadedBy?: string }).uploadedBy ? 'pointer' : 'default',
                                                                     }}
                                                                  >
                                                                     <Avatar
                                                                        sx={{
                                                                           width: 20,
                                                                           height: 20,
                                                                           mr: 1,
                                                                        }}
                                                                        src={
                                                                           photo.uploaderProfile
                                                                              ?.profilePicture ||
                                                                           photo.uploaderProfile
                                                                              ?.photoURL ||
                                                                           photo.uploaderProfile
                                                                              ?.profilePhoto ||
                                                                           photo.uploaderProfile
                                                                              ?.avatar
                                                                        }
                                                                     >
                                                                        {photo.uploaderProfile?.displayName?.charAt(
                                                                           0
                                                                        ) ||
                                                                           photo.uploaderProfile?.name?.charAt(
                                                                              0
                                                                           ) ||
                                                                           photo.uploaderProfile?.fullName?.charAt(
                                                                              0
                                                                           ) ||
                                                                           '?'}
                                                                     </Avatar>
                                                                     <Typography
                                                                        variant='caption'
                                                                        color='text.secondary'
                                                                        noWrap
                                                                     >
                                                                        {photo.uploaderProfile
                                                                           ?.displayName ||
                                                                           photo.uploaderProfile
                                                                              ?.name ||
                                                                           photo.uploaderProfile
                                                                              ?.fullName}
                                                                     </Typography>
                                                                  </Box>
                                                               )}
                                                            {currentUser && (
                                                               <PhotoLikeButton
                                                                  photoId={photo.id}
                                                                  userId={currentUser.uid}
                                                                  initialLikeCount={
                                                                     photo.likeCount || 0
                                                                  }
                                                                  initialIsLiked={
                                                                     photo.isLiked || false
                                                                  }
                                                                  size='small'
                                                                  variant='compact'
                                                                  showCount={true}
                                                               />
                                                            )}
                                                         </Box>
                                                      </CardContent>
                                                   </Card>
                                                </Grid>
                                             ))}
                                          </Grid>
                                       </Box>
                                    ))
                                 ) : (
                                    // Regular sorted view with folders inline
                                    <>
                                       <Grid
                                          container
                                          spacing={isMobile ? 1.5 : 2}
                                          sx={{ width: '100%', margin: 0 }}
                                       >
                                          {/* Render folders first */}
                                          <Box
                                             ref={foldersAreaRef}
                                             sx={{ width: '100%', display: 'contents' }}
                                          >
                                             {photoFolders.map((folder) => (
                                                <Grid
                                                   item
                                                   xs={12}
                                                   sm={12}
                                                   md={6}
                                                   lg={4}
                                                   xl={3}
                                                   key={folder.id}
                                                >
                                                   <DroppableFolderCard
                                                      folder={folder}
                                                      onDrop={handleDropToFolder}
                                                   >
                                                      <Card
                                                         sx={{
                                                            borderRadius: isMobile ? 2 : 3,
                                                            boxShadow: isMobile ? 1 : 2,
                                                            overflow: 'hidden',
                                                            aspectRatio: '1 / 1',
                                                            width: '100%',
                                                            minWidth: isMobile ? '280px' : '320px',
                                                            height: 'auto',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            position: 'relative',
                                                            bgcolor: 'background.paper',
                                                         }}
                                                      >
                                                         {/* Folder Header */}
                                                         <Box
                                                            sx={{
                                                               p: 2,
                                                               bgcolor: 'primary.main',
                                                               color: 'white',
                                                               display: 'flex',
                                                               alignItems: 'center',
                                                               justifyContent: 'space-between',
                                                            }}
                                                         >
                                                            <Box
                                                               sx={{
                                                                  display: 'flex',
                                                                  alignItems: 'center',
                                                                  gap: 1,
                                                                  cursor: 'pointer',
                                                               }}
                                                               onClick={() => {
                                                                  if (folder.photoIds.length > 0) {
                                                                     handleViewFolder(folder);
                                                                  }
                                                               }}
                                                            >
                                                               <FolderIcon />
                                                               <Typography
                                                                  variant='subtitle1'
                                                                  fontWeight={600}
                                                                  noWrap
                                                                  sx={{
                                                                     maxWidth: isMobile
                                                                        ? '120px'
                                                                        : '150px',
                                                                     overflow: 'hidden',
                                                                     textOverflow: 'ellipsis',
                                                                     whiteSpace: 'nowrap',
                                                                  }}
                                                                  title={folder.name}
                                                               >
                                                                  {folder.name}
                                                               </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                               <IconButton
                                                                  size='small'
                                                                  onClick={(e) => {
                                                                     e.stopPropagation();
                                                                     handleEditFolder(folder);
                                                                  }}
                                                                  sx={{ color: 'white' }}
                                                                  title='Edit folder name'
                                                               >
                                                                  <EditIcon fontSize='small' />
                                                               </IconButton>
                                                               <IconButton
                                                                  size='small'
                                                                  onClick={(e) => {
                                                                     e.stopPropagation();
                                                                     handleDeleteFolder(folder.id);
                                                                  }}
                                                                  sx={{ color: 'white' }}
                                                                  title='Delete folder'
                                                               >
                                                                  <DeleteIcon fontSize='small' />
                                                               </IconButton>
                                                            </Box>
                                                         </Box>

                                                         {/* Folder Content */}
                                                         <CardContent
                                                            sx={{
                                                               flexGrow: 1,
                                                               p: 3,
                                                               display: 'flex',
                                                               flexDirection: 'column',
                                                               height: '100%',
                                                            }}
                                                         >
                                                            {/* Photo count with navigation */}
                                                            <Box
                                                               sx={{
                                                                  display: 'flex',
                                                                  alignItems: 'center',
                                                                  justifyContent: 'space-between',
                                                                  mb: 2,
                                                               }}
                                                            >
                                                               <Typography
                                                                  variant='body2'
                                                                  color='text.secondary'
                                                               >
                                                                  {folder.photoIds.length} photo
                                                                  {folder.photoIds.length !== 1
                                                                     ? 's'
                                                                     : ''}
                                                               </Typography>

                                                               {/* Prev/Next Navigation */}
                                                               {folder.photoIds.length > 4 && (
                                                                  <Box
                                                                     sx={{
                                                                        display: 'flex',
                                                                        gap: 0.5,
                                                                        mb: 0.3,
                                                                     }}
                                                                  >
                                                                     <IconButton
                                                                        size='small'
                                                                        onClick={(e) => {
                                                                           e.stopPropagation();
                                                                           handleFolderPhotosPrev(
                                                                              folder.id
                                                                           );
                                                                        }}
                                                                        disabled={
                                                                           (folderPhotoStartIndex[
                                                                              folder.id
                                                                           ] || 0) === 0
                                                                        }
                                                                        sx={{
                                                                           padding: 0.5,
                                                                           bgcolor: 'primary.main',
                                                                           color: 'white',
                                                                           display: 'flex',
                                                                           alignItems: 'center',
                                                                           justifyContent: 'center',
                                                                           '&:hover': {
                                                                              bgcolor:
                                                                                 'primary.dark',
                                                                           },
                                                                           '&:disabled': {
                                                                              bgcolor:
                                                                                 'rgba(0, 0, 0, 0.12)',
                                                                              color: 'rgba(0, 0, 0, 0.26)',
                                                                           },
                                                                        }}
                                                                     >
                                                                        <ArrowBackIosIcon fontSize='small' />
                                                                     </IconButton>
                                                                     <IconButton
                                                                        size='small'
                                                                        onClick={(e) => {
                                                                           e.stopPropagation();
                                                                           handleFolderPhotosNext(
                                                                              folder.id,
                                                                              folder.photoIds.length
                                                                           );
                                                                        }}
                                                                        disabled={
                                                                           (folderPhotoStartIndex[
                                                                              folder.id
                                                                           ] || 0) +
                                                                              4 >=
                                                                           folder.photoIds.length
                                                                        }
                                                                        sx={{
                                                                           padding: 0.5,
                                                                           bgcolor: 'primary.main',
                                                                           color: 'white',
                                                                           display: 'flex',
                                                                           alignItems: 'center',
                                                                           justifyContent: 'center',
                                                                           '&:hover': {
                                                                              bgcolor:
                                                                                 'primary.dark',
                                                                           },
                                                                           '&:disabled': {
                                                                              bgcolor:
                                                                                 'rgba(0, 0, 0, 0.12)',
                                                                              color: 'rgba(0, 0, 0, 0.26)',
                                                                           },
                                                                        }}
                                                                     >
                                                                        <ArrowForwardIosIcon fontSize='small' />
                                                                     </IconButton>
                                                                  </Box>
                                                               )}
                                                            </Box>

                                                            {/* Photo thumbnails */}
                                                            {folder.photoIds.length > 0 && (
                                                               <Box
                                                                  sx={{
                                                                     display: 'grid',
                                                                     gridTemplateColumns:
                                                                        'repeat(2, 1fr)',
                                                                     gap: 0.9,
                                                                     flexGrow: 0,
                                                                     alignContent: 'start',
                                                                  }}
                                                               >
                                                                  {(() => {
                                                                     const startIndex =
                                                                        folderPhotoStartIndex[
                                                                           folder.id
                                                                        ] || 0;
                                                                     const visiblePhotoIds =
                                                                        folder.photoIds.slice(
                                                                           startIndex,
                                                                           startIndex + 4
                                                                        );

                                                                     return visiblePhotoIds.map(
                                                                        (photoId) => {
                                                                           const photo =
                                                                              albumPhotos.find(
                                                                                 (p) =>
                                                                                    p.id === photoId
                                                                              );
                                                                           if (!photo) return null;
                                                                           return (
                                                                              <Box
                                                                                 key={photoId}
                                                                                 sx={{
                                                                                    position:
                                                                                       'relative',
                                                                                    paddingTop:
                                                                                       '60%',
                                                                                    borderRadius: 1,
                                                                                    overflow:
                                                                                       'hidden',
                                                                                    boxShadow:
                                                                                       '0 2px 4px rgba(0,0,0,0.1)',
                                                                                    transition:
                                                                                       'transform 0.2s, box-shadow 0.2s',
                                                                                    '&:hover': {
                                                                                       transform:
                                                                                          'scale(1.02)',
                                                                                       boxShadow:
                                                                                          '0 4px 8px rgba(0,0,0,0.15)',
                                                                                    },
                                                                                 }}
                                                                              >
                                                                                 {isVideo(
                                                                                    photo
                                                                                 ) ? (
                                                                                    <Box
                                                                                       component='video'
                                                                                       src={
                                                                                          photo.url ||
                                                                                          (photo as { videoUrl?: string })
                                                                                             .videoUrl
                                                                                       }
                                                                                       poster={
                                                                                          photo.thumbnailUrl ||
                                                                                          undefined
                                                                                       }
                                                                                       muted
                                                                                       loop
                                                                                       playsInline
                                                                                       sx={{
                                                                                          position:
                                                                                             'absolute',
                                                                                          top: 0,
                                                                                          left: 0,
                                                                                          width: '100%',
                                                                                          height:
                                                                                             '100%',
                                                                                          objectFit:
                                                                                             'cover',
                                                                                          cursor:
                                                                                             'pointer',
                                                                                       }}
                                                                                       onClick={() => {
                                                                                          if (
                                                                                             folder
                                                                                                .photoIds
                                                                                                .length >
                                                                                             0
                                                                                          ) {
                                                                                             handleViewFolder(
                                                                                                folder
                                                                                             );
                                                                                          }
                                                                                       }}
                                                                                    />
                                                                                 ) : (
                                                                                    <Box
                                                                                       component='img'
                                                                                       src={photo.url}
                                                                                       alt={
                                                                                          photo.title
                                                                                       }
                                                                                       sx={{
                                                                                          position:
                                                                                             'absolute',
                                                                                          top: 0,
                                                                                          left: 0,
                                                                                          width: '100%',
                                                                                          height:
                                                                                             '100%',
                                                                                          objectFit:
                                                                                             'cover',
                                                                                          cursor:
                                                                                             'pointer',
                                                                                       }}
                                                                                       onClick={() => {
                                                                                          if (
                                                                                             folder
                                                                                                .photoIds
                                                                                                .length >
                                                                                             0
                                                                                          ) {
                                                                                             handleViewFolder(
                                                                                                folder
                                                                                             );
                                                                                          }
                                                                                       }}
                                                                                    />
                                                                                 )}
                                                                                 {/* Delete Icon Overlay */}
                                                                                 <Box
                                                                                    sx={{
                                                                                       position:
                                                                                          'absolute',
                                                                                       top: 2,
                                                                                       right: 2,
                                                                                       zIndex: 2,
                                                                                    }}
                                                                                 >
                                                                                    <IconButton
                                                                                       size='small'
                                                                                       onClick={(
                                                                                          e
                                                                                       ) => {
                                                                                          e.stopPropagation();
                                                                                          handleRemovePhotoFromFolder(
                                                                                             folder.id,
                                                                                             photoId
                                                                                          );
                                                                                       }}
                                                                                       sx={{
                                                                                          bgcolor:
                                                                                             '#f44336',
                                                                                          color: 'white',
                                                                                          padding:
                                                                                             '1px',
                                                                                          width: '18px',
                                                                                          height:
                                                                                             '18px',
                                                                                          minWidth:
                                                                                             '18px',
                                                                                          minHeight:
                                                                                             '18px',
                                                                                          boxShadow:
                                                                                             '0 2px 4px rgba(0,0,0,0.2)',
                                                                                          '&:hover':
                                                                                             {
                                                                                                bgcolor:
                                                                                                   '#d32f2f',
                                                                                                transform:
                                                                                                   'scale(1.1)',
                                                                                             },
                                                                                       }}
                                                                                       title='Remove from folder'
                                                                                    >
                                                                                       <CloseIcon
                                                                                          sx={{
                                                                                             fontSize: 12,
                                                                                          }}
                                                                                       />
                                                                                    </IconButton>
                                                                                 </Box>
                                                                              </Box>
                                                                           );
                                                                        }
                                                                     );
                                                                  })()}
                                                               </Box>
                                                            )}

                                                            {folder.photoIds.length === 0 && (
                                                               <Box
                                                                  sx={{
                                                                     display: 'flex',
                                                                     flexDirection: 'column',
                                                                     alignItems: 'center',
                                                                     justifyContent: 'center',
                                                                     py: 3,
                                                                     color: 'text.secondary',
                                                                  }}
                                                               >
                                                                  <FolderIcon
                                                                     sx={{ fontSize: 48, mb: 1 }}
                                                                  />
                                                                  <Typography variant='caption'>
                                                                     Drag photos here
                                                                  </Typography>
                                                               </Box>
                                                            )}
                                                         </CardContent>
                                                      </Card>
                                                   </DroppableFolderCard>
                                                </Grid>
                                             ))}
                                          </Box>

                                          {/* Render ungrouped photos or all photos if no folders */}
                                          {(photoFolders.length > 0
                                             ? getUngroupedPhotos
                                             : organizedPhotos
                                          ).map((photo) => (
                                             <Grid
                                                item
                                                xs={12}
                                                sm={6}
                                                md={4}
                                                lg={3}
                                                xl={2}
                                                key={photo.id}
                                             >
                                                <Card
                                                   sx={{
                                                      borderRadius: isMobile ? 2 : 3,
                                                      boxShadow: isMobile ? 1 : 2,
                                                      overflow: 'hidden',
                                                      height: isMobile ? 160 : 180,
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      justifyContent: 'center',
                                                      position: 'relative',
                                                      border: photoSelectionSet.has(photo.id)
                                                         ? '3px solid #1976d2'
                                                         : 'none',
                                                   }}
                                                >
                                                   <DraggablePhotoCard
                                                      photo={photo}
                                                      photoFoldersExist={photoFolders.length > 0}
                                                   >
                                                      {/* Cover photo indicator */}
                                                      {album?.coverPhotoId === photo.id && (
                                                         <Box
                                                            sx={{
                                                               position: 'absolute',
                                                               top: isMobile ? 6 : 8,
                                                               left: isMobile ? 6 : 8,
                                                               zIndex: 2,
                                                               bgcolor: 'primary.main',
                                                               color: 'white',
                                                               borderRadius: isMobile ? 0.5 : 1,
                                                               px: isMobile ? 0.5 : 1,
                                                               py: isMobile ? 0.25 : 0.5,
                                                               display: 'flex',
                                                               alignItems: 'center',
                                                               gap: isMobile ? 0.25 : 0.5,
                                                            }}
                                                         >
                                                            <StarIcon
                                                               sx={{ fontSize: isMobile ? 12 : 16 }}
                                                            />
                                                            <Typography
                                                               variant='caption'
                                                               fontWeight={600}
                                                               sx={{
                                                                  fontSize: isMobile
                                                                     ? '0.6rem'
                                                                     : '0.75rem',
                                                               }}
                                                            >
                                                               Cover
                                                            </Typography>
                                                         </Box>
                                                      )}

                                                      {/* Photo action buttons */}
                                                      <Box
                                                         sx={{
                                                            position: 'absolute',
                                                            top: isMobile ? 6 : 8,
                                                            right: isMobile ? 6 : 8,
                                                            zIndex: 2,
                                                            display: 'flex',
                                                            gap: 0.5,
                                                         }}
                                                      >
                                                         {/* Download photo button - visible to all users */}
                                                         <IconButton
                                                            sx={{
                                                               bgcolor: 'rgba(0,0,0,0.5)',
                                                               color: 'white',
                                                               width: isMobile ? 24 : 28,
                                                               height: isMobile ? 24 : 28,
                                                               '&:hover': {
                                                                  bgcolor: 'rgba(0,0,0,0.7)',
                                                               },
                                                            }}
                                                            size='small'
                                                            disabled={bulkDownloading}
                                                            onClick={(e) => {
                                                               e.stopPropagation();
                                                               handleDownloadPhoto(photo);
                                                            }}
                                                            title={
                                                               bulkDownloading
                                                                  ? 'Bulk download in progress'
                                                                  : downloadingPhotos.has(photo.id)
                                                                    ? 'Downloading...'
                                                                    : 'Download photo'
                                                            }
                                                         >
                                                            {downloadingPhotos.has(photo.id) ? (
                                                               <CircularProgress
                                                                  size={16}
                                                                  sx={{ color: 'red' }}
                                                               />
                                                            ) : (
                                                               <DownloadIcon
                                                                  fontSize={
                                                                     isMobile ? 'small' : 'small'
                                                                  }
                                                               />
                                                            )}
                                                         </IconButton>
                                                         {/* Delete photo button - only for owners and editors */}
                                                         {(userRole === 'owner' || userRole === 'editor') && (
                                                            <IconButton
                                                               sx={{
                                                                  bgcolor: 'rgba(211, 47, 47, 0.8)',
                                                                  color: 'white',
                                                                  width: isMobile ? 24 : 28,
                                                                  height: isMobile ? 24 : 28,
                                                                  '&:hover': {
                                                                     bgcolor: 'rgba(211, 47, 47, 1)',
                                                                  },
                                                               }}
                                                               size='small'
                                                               onClick={(e) => {
                                                                  e.stopPropagation();
                                                                  setSelectedPhotoForMenu(photo);
                                                                  setDeletePhotoDialogOpen(true);
                                                               }}
                                                               title='Remove photo from album'
                                                            >
                                                               <DeleteIcon
                                                                  fontSize={
                                                                     isMobile ? 'small' : 'small'
                                                                  }
                                                               />
                                                            </IconButton>
                                                         )}
                                                         {/* Photo menu button - only for album owners */}
                                                         {userRole === 'owner' && (
                                                            <IconButton
                                                               sx={{
                                                                  bgcolor: 'rgba(0,0,0,0.5)',
                                                                  color: 'white',
                                                                  width: isMobile ? 24 : 28,
                                                                  height: isMobile ? 24 : 28,
                                                                  '&:hover': {
                                                                     bgcolor: 'rgba(0,0,0,0.7)',
                                                                  },
                                                               }}
                                                               size='small'
                                                               onClick={(e) =>
                                                                  handlePhotoMenuOpen(e, photo)
                                                               }
                                                            >
                                                               <MoreVertIcon
                                                                  sx={{
                                                                     fontSize: isMobile
                                                                        ? '1rem'
                                                                        : '1.25rem',
                                                                  }}
                                                               />
                                                            </IconButton>
                                                         )}
                                                      </Box>

                                                      {/* Selection checkbox - visible to all users */}
                                                      <Box
                                                         position='absolute'
                                                         top={8}
                                                         left={8}
                                                         zIndex={10}
                                                      >
                                                         <Box
                                                            sx={{
                                                               bgcolor: photoSelectionSet.has(
                                                                  photo.id
                                                               )
                                                                  ? '#1976d2'
                                                                  : 'rgba(0,0,0,0.6)',
                                                               borderRadius: '50%',
                                                               p: 0.3,
                                                               transition: 'all 0.2s ease',
                                                               border: photoSelectionSet.has(
                                                                  photo.id
                                                               )
                                                                  ? '2px solid white'
                                                                  : '2px solid transparent',
                                                            }}
                                                         >
                                                            <Checkbox
                                                               checked={photoSelectionSet.has(
                                                                  photo.id
                                                               )}
                                                               onChange={(e) => {
                                                                  e.stopPropagation();
                                                                  togglePhotoSelection(photo.id);
                                                               }}
                                                               size='small'
                                                               sx={{
                                                                  color: 'white',
                                                                  p: 0,
                                                                  '&.Mui-checked': {
                                                                     color: 'white',
                                                                  },
                                                               }}
                                                            />
                                                         </Box>
                                                      </Box>

                                                      {isVideo(photo) ? (
                                                         <Box
                                                            component='video'
                                                            src={photo.url || photo.videoUrl}
                                                            poster={photo.thumbnailUrl}
                                                            muted
                                                            loop
                                                            playsInline
                                                            height={isMobile ? 120 : 140}
                                                            sx={{
                                                               objectFit: 'cover',
                                                               width: '100%',
                                                               cursor: 'pointer',
                                                               transition:
                                                                  'transform 0.2s ease-in-out',
                                                               opacity: photoSelectionSet.has(
                                                                  photo.id
                                                               )
                                                                  ? 0.8
                                                                  : 1,
                                                               '&:hover': {
                                                                  transform: isMobile
                                                                     ? 'none'
                                                                     : 'scale(1.02)',
                                                               },
                                                            }}
                                                            onClick={() =>
                                                               openPhotoModal(
                                                                  albumPhotos.findIndex(
                                                                     (p) => p.id === photo.id
                                                                  )
                                                               )
                                                            }
                                                         />
                                                      ) : (
                                                         <CardMedia
                                                            component='img'
                                                            height={isMobile ? '120' : '140'}
                                                            image={photo.url || '/placeholder.jpg'}
                                                            alt={photo.title || 'Photo'}
                                                            sx={{
                                                               objectFit: 'cover',
                                                               width: '100%',
                                                               cursor: 'pointer',
                                                               transition:
                                                                  'transform 0.2s ease-in-out',
                                                               opacity: photoSelectionSet.has(
                                                                  photo.id
                                                               )
                                                                  ? 0.8
                                                                  : 1,
                                                               '&:hover': {
                                                                  transform: isMobile
                                                                     ? 'none'
                                                                     : 'scale(1.02)',
                                                               },
                                                            }}
                                                            onClick={() =>
                                                               openPhotoModal(
                                                                  albumPhotos.findIndex(
                                                                     (p) => p.id === photo.id
                                                                  )
                                                               )
                                                            }
                                                         />
                                                      )}
                                                   </DraggablePhotoCard>
                                                      <CardContent
                                                         sx={{
                                                            p: isMobile ? 0.5 : 1,
                                                            pt: isMobile ? 0.5 : 1,
                                                         }}
                                                      >
                                                         {/* Date centered */}
                                                         {getBestPhotoDate(photo) && (
                                                            <Box
                                                               sx={{
                                                                  display: 'flex',
                                                                  justifyContent: 'center',
                                                                  alignItems: 'center',
                                                                  gap: 0.5,
                                                                  mb: 0.5,
                                                               }}
                                                            >
                                                               <Typography
                                                                  variant='caption'
                                                                  color='text.secondary'
                                                                  sx={{
                                                                     fontSize: isMobile
                                                                        ? '0.65rem'
                                                                        : '0.75rem',
                                                                     fontWeight: 500,
                                                                  }}
                                                               >
                                                                  📅 {formatPhotoDate(photo)}
                                                               </Typography>
                                                               {formatLocationCoordinates(
                                                                  photo.location
                                                               ) && (
                                                                  <Typography
                                                                     variant='caption'
                                                                     color='text.secondary'
                                                                     sx={{
                                                                        fontSize: isMobile
                                                                           ? '0.65rem'
                                                                           : '0.75rem',
                                                                     }}
                                                                  >
                                                                     📍
                                                                  </Typography>
                                                               )}
                                                            </Box>
                                                         )}
                                                         <Box
                                                            sx={{
                                                               display: 'flex',
                                                               alignItems: 'center',
                                                               justifyContent: 'space-between',
                                                            }}
                                                         >
                                                            {photo.uploaderProfile &&
                                                               (photo.uploaderProfile.displayName ||
                                                                  photo.uploaderProfile.name ||
                                                                  photo.uploaderProfile
                                                                     .fullName) && (
                                                                  <Box
                                                                     onMouseDown={(e) => e.stopPropagation()}
                                                                     onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const uid = (photo as { userId?: string; uploadedBy?: string }).userId || (photo as { userId?: string; uploadedBy?: string }).uploadedBy;
                                                                        if (uid) setProfilePopoverUserId(uid);
                                                                     }}
                                                                     sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        cursor: (photo as { userId?: string; uploadedBy?: string }).userId || (photo as { userId?: string; uploadedBy?: string }).uploadedBy ? 'pointer' : 'default',
                                                                     }}
                                                                  >
                                                                     <Avatar
                                                                        sx={{
                                                                           width: isMobile
                                                                              ? 16
                                                                              : 20,
                                                                           height: isMobile
                                                                              ? 16
                                                                              : 20,
                                                                           mr: isMobile ? 0.5 : 1,
                                                                        }}
                                                                        src={
                                                                           photo.uploaderProfile
                                                                              ?.profilePicture ||
                                                                           photo.uploaderProfile
                                                                              ?.photoURL ||
                                                                           photo.uploaderProfile
                                                                              ?.profilePhoto ||
                                                                           photo.uploaderProfile
                                                                              ?.avatar
                                                                        }
                                                                     >
                                                                        {photo.uploaderProfile?.displayName?.charAt(
                                                                           0
                                                                        ) ||
                                                                           photo.uploaderProfile?.name?.charAt(
                                                                              0
                                                                           ) ||
                                                                           photo.uploaderProfile?.fullName?.charAt(
                                                                              0
                                                                           ) ||
                                                                           '?'}
                                                                     </Avatar>
                                                                     <Typography
                                                                        variant='caption'
                                                                        color='text.secondary'
                                                                        noWrap
                                                                        sx={{
                                                                           fontSize: isMobile
                                                                              ? '0.65rem'
                                                                              : '0.75rem',
                                                                        }}
                                                                     >
                                                                        {photo.uploaderProfile
                                                                           ?.displayName ||
                                                                           photo.uploaderProfile
                                                                              ?.name ||
                                                                           photo.uploaderProfile
                                                                              ?.fullName}
                                                                     </Typography>
                                                                  </Box>
                                                               )}
                                                            {currentUser && (
                                                               <PhotoLikeButton
                                                                  photoId={photo.id}
                                                                  userId={currentUser.uid}
                                                                  initialLikeCount={
                                                                     photo.likeCount || 0
                                                                  }
                                                                  initialIsLiked={
                                                                     photo.isLiked || false
                                                                  }
                                                                  size='small'
                                                                  variant='compact'
                                                                  showCount={true}
                                                               />
                                                            )}
                                                         </Box>
                                                      </CardContent>
                                                   </Card>
                                             </Grid>
                                          ))}
                                       </Grid>
                                    </>
                                 )
                              ) : (
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       flexDirection: 'column',
                                       alignItems: 'center',
                                       py: 6,
                                       color: 'grey.400',
                                    }}
                                 >
                                    <ImageNotSupportedIcon sx={{ fontSize: 64, mb: 2 }} />
                                    <Typography
                                       variant='body1'
                                       color='text.secondary'
                                       sx={{ mb: 1 }}
                                    >
                                       No photos in this album yet.
                                    </Typography>
                                    {canUpload && (
                                       <Button
                                          variant='contained'
                                          color='primary'
                                          startIcon={<AddPhotoAlternateIcon />}
                                          onClick={() => setAddPhotosOpen(true)}
                                       >
                                          Add Photos
                                       </Button>
                                    )}
                                 </Box>
                              )}

                              {/* Infinite scroll loading indicator */}
                              {loadingAlbumPhotos && albumPhotos.length > 0 && (
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       flexDirection: 'column',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                       py: 4,
                                    }}
                                 >
                                    <CircularProgress size={30} sx={{ color: '#00C4A7', mb: 1 }} />
                                    <Typography variant='caption' color='text.secondary'>
                                       Loading more photos...
                                    </Typography>
                                 </Box>
                              )}

                              {/* No more photos indicator */}
                              {!loadingAlbumPhotos && !hasMorePhotos && albumPhotos.length > 0 && (
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       justifyContent: 'center',
                                       py: 3,
                                    }}
                                 >
                                    <Typography variant='body2' color='text.secondary'>
                                       ✨ All photos loaded ({albumPhotos.length} total)
                                    </Typography>
                                 </Box>
                              )}
                           </>
                        )}
                     </>
                  )}

               {/* Gallery View */}
               {validCurrentTab === 'gallery' && (
                  <>
                     {loadingAlbumPhotos && albumPhotos.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                           <CircularProgress size={60} />
                        </Box>
                     ) : (
                        <GalleryView
                           photos={filteredTimelinePhotos as any}
                           albumId={albumId || ''}
                           onDownloadPhoto={handleDownloadPhoto}
                           onDeletePhoto={(photo: any) => {
                              setSelectedPhotoForMenu(photo);
                              setDeletePhotoDialogOpen(true);
                           }}
                           downloadingPhotoIds={downloadingPhotos}
                           canDelete={userRole === 'owner' || userRole === 'editor'}
                           currentUser={currentUser}
                        />
                     )}
                  </>
               )}

               {/* Photo Groups View */}
               {validCurrentTab === 'photoGroups' && (
                  <>
                  {loadingAlbumPhotos && albumPhotos.length === 0 ? (
                     <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress size={60} />
                     </Box>
                  ) : (
                     <PhotoGroupsBento
                        photos={albumPhotos as any}
                        albumId={albumId || ''}
                        userRole={userRole as 'owner' | 'editor' | 'viewer' | null}
                        onPhotoDeleted={reloadPhotos}
                        album={album}
                        currentUser={currentUser}
                     />
                  )}
                  </>
               )}

               {/* Magazine Export View */}
               {validCurrentTab === 'magazine' &&
                  !loading &&
                  album &&
                  userRole &&
                  userRole !== 'viewer' &&
                  (userRole === 'owner' || userRole === 'editor') && (
                     <MagazineExport
                        photos={albumPhotos as any}
                        albumTitle={album.title}
                        albumDescription={album.description}
                        photoSelectionSet={photoSelectionSet}
                        onTogglePhotoSelection={togglePhotoSelection}
                        onSharePhoto={handleShareSinglePhoto}
                     />
                  )}

               {/* Shareable Link View */}
               {validCurrentTab === 'sharing' && renderAlbumSharing()}

               {/* Waiting List View */}
               {validCurrentTab === 'waitlist' && renderWaitlist()}

               {/* Analytics View */}
               {validCurrentTab === 'analytics' && renderAnalytics()}
            </Box>
         </Paper>
         {/* Add Photos Dialog */}
         <Dialog
            open={addPhotosOpen}
            onClose={() => setAddPhotosOpen(false)}
            maxWidth='md'
            fullWidth
         >
            <DialogTitle>Select Photos to Add</DialogTitle>
            <DialogContent>
               {addError && (
                  <Typography color='error' sx={{ mb: 2 }}>
                     {addError}
                  </Typography>
               )}
               <PhotoSelector selectedPhotos={selectedPhotos} onPhotoSelect={setSelectedPhotos} />
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setAddPhotosOpen(false)} disabled={addingPhotos}>
                  Cancel
               </Button>
               <Button
                  variant='contained'
                  color='primary'
                  disabled={addingPhotos || selectedPhotos.length === 0}
                  onClick={async () => {
                     setAddingPhotos(true);
                     setAddError(null);
                     try {
                        // Log selectedPhotos for debugging
                        console.log('Selected photos to add:', selectedPhotos);
                        // Only send valid, non-null IDs
                        const uniquePhotoIds = Array.from(
                           new Set(selectedPhotos.map((p) => p.id).filter(Boolean))
                        );
                        if (uniquePhotoIds.length === 0)
                           throw new Error('No valid photo IDs selected');
                        const headers = await getAuthHeaders();

                        const res = await fetch(
                           `${process.env.REACT_APP_API_URL}/api/albums/${album.id}/add-photos`,
                           {
                              method: 'POST',
                              headers,
                              body: JSON.stringify({ photoIds: uniquePhotoIds }),
                           }
                        );
                        if (!res.ok) throw new Error('Failed to add photos');
                        setAddPhotosOpen(false);
                        setSelectedPhotos([]);
                        // Refresh album details
                        setLoading(true);
                        getAuthHeaders()
                           .then((headers) =>
                              fetch(`${process.env.REACT_APP_API_URL}/api/albums/${album.id}`, {
                                 headers,
                              })
                           )
                           .then((res) => res.json())
                           .then((data) => {
                              setAlbum(data.album || null);
                              setLoading(false);
                              console.log('Album after adding photos:', data.album);
                           })
                           .catch(() => setLoading(false));
                     } catch (err: any) {
                        setAddError(err.message || 'Failed to add photos');
                     } finally {
                        setAddingPhotos(false);
                     }
                  }}
               >
                  {addingPhotos ? 'Adding...' : 'Add to Album'}
               </Button>
            </DialogActions>
         </Dialog>
         {/* Direct Upload Dialog */}
         <Dialog open={directUploadOpen} onClose={handleCloseDirectUpload} maxWidth='md' fullWidth>
            <DialogTitle>Upload Photos to Album</DialogTitle>
            <DialogContent>
               {uploadError && (
                  <Alert severity='error' sx={{ mb: 2 }}>
                     {uploadError}
                  </Alert>
               )}

               <Box sx={{ mb: 2 }}>
                  <input
                     type='file'
                     multiple
                     accept='image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp,image/tiff,image/svg+xml'
                     onChange={handleFileSelect}
                     style={{ display: 'none' }}
                     id='photo-upload-input'
                  />
                  <label htmlFor='photo-upload-input'>
                     <Button
                        variant='outlined'
                        component='span'
                        fullWidth
                        sx={{ py: 2, borderStyle: 'dashed', borderWidth: 2 }}
                     >
                        📁 Choose Images ({selectedFiles.length} selected)
                     </Button>
                  </label>
               </Box>

               {selectedFiles.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                     <Typography variant='subtitle2' gutterBottom>
                        Selected Images:
                     </Typography>
                     <Grid
                        container
                        spacing={2}
                        sx={{ maxHeight: 300, overflowY: 'auto', width: '100%', margin: 0 }}
                     >
                        {selectedFiles.map((file, index) => (
                           <Grid item xs={6} sm={4} md={3} key={index}>
                              <Card
                                 sx={{
                                    position: 'relative',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: file.size > 50 * 1024 * 1024 ? '2px solid red' : 'none',
                                    opacity: file.size > 50 * 1024 * 1024 ? 0.7 : 1,
                                 }}
                              >
                                 {filePreviews[file.name] ? (
                                    <CardMedia
                                       component='img'
                                       height='120'
                                       image={filePreviews[file.name]}
                                       alt={file.name}
                                       sx={{ objectFit: 'cover' }}
                                    />
                                 ) : (
                                    <Box
                                       sx={{
                                          height: 120,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          bgcolor: 'grey.100',
                                       }}
                                    >
                                       <Typography variant='caption'>📄</Typography>
                                    </Box>
                                 )}

                                 {/* Remove button */}
                                 <IconButton
                                    size='small'
                                    onClick={() => removeFile(file)}
                                    sx={{
                                       position: 'absolute',
                                       top: 4,
                                       right: 4,
                                       bgcolor: 'rgba(255,255,255,0.8)',
                                       '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                    }}
                                 >
                                    ❌
                                 </IconButton>

                                 {/* File name and metadata */}
                                 <Box
                                    sx={{
                                       p: 1,
                                       bgcolor: 'rgba(0,0,0,0.7)',
                                       position: 'absolute',
                                       bottom: 0,
                                       left: 0,
                                       right: 0,
                                    }}
                                 >
                                    <Typography variant='caption' color='white' noWrap>
                                       {file.name}
                                    </Typography>

                                    {/* File size and size warning */}
                                    <Box sx={{ mt: 0.5 }}>
                                       <Typography
                                          variant='caption'
                                          color={file.size > 50 * 1024 * 1024 ? 'red' : 'lightgray'}
                                          sx={{ display: 'block', fontSize: '0.65rem' }}
                                       >
                                          📏 {(file.size / (1024 * 1024)).toFixed(1)}MB
                                          {file.size > 50 * 1024 * 1024 && ' ⚠️ Too large!'}
                                       </Typography>
                                    </Box>
                                    {extractedMetadata[file.name] && (
                                       <Box sx={{ mt: 0.5 }}>
                                          {extractedMetadata[file.name]?.takenDate && (
                                             <Typography
                                                variant='caption'
                                                color='lightgreen'
                                                sx={{ display: 'block', fontSize: '0.65rem' }}
                                             >
                                                📅{' '}
                                                {extractedMetadata[
                                                   file.name
                                                ].takenDate?.toLocaleDateString()}
                                             </Typography>
                                          )}
                                          {extractedMetadata[file.name]?.location && (
                                             <Typography
                                                variant='caption'
                                                color='lightblue'
                                                sx={{ display: 'block', fontSize: '0.65rem' }}
                                             >
                                                📍{' '}
                                                {typeof extractedMetadata[file.name].location
                                                   ?.latitude === 'number'
                                                   ? extractedMetadata[
                                                        file.name
                                                     ].location.latitude.toFixed(3)
                                                   : 'N/A'}
                                                ,{' '}
                                                {typeof extractedMetadata[file.name].location
                                                   ?.longitude === 'number'
                                                   ? extractedMetadata[
                                                        file.name
                                                     ].location.longitude.toFixed(3)
                                                   : 'N/A'}
                                             </Typography>
                                          )}
                                          {!extractedMetadata[file.name]?.takenDate &&
                                             !extractedMetadata[file.name]?.location && (
                                                <Typography
                                                   variant='caption'
                                                   color='orange'
                                                   sx={{ display: 'block', fontSize: '0.65rem' }}
                                                >
                                                   ⚠️ No date/location data
                                                </Typography>
                                             )}
                                       </Box>
                                    )}
                                 </Box>
                              </Card>
                           </Grid>
                        ))}
                     </Grid>
                  </Box>
               )}

               {uploading && (
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                     <Typography variant='body2' gutterBottom>
                        {batchProgress ? (
                           <>
                              ☁️ Uploading Batch {batchProgress.currentBatch} of{' '}
                              {batchProgress.totalBatches}
                              {batchProgress.failedFiles > 0 &&
                                 ` (${batchProgress.failedFiles} failed)`}
                           </>
                        ) : uploadProgress < 25 ? (
                           '📷 Preparing photos...'
                        ) : (
                           '☁️ Uploading photos...'
                        )}
                     </Typography>
                     <LinearProgress variant='determinate' value={uploadProgress} />
                     <Typography
                        variant='caption'
                        sx={{ mt: 1, display: 'block', color: 'text.secondary' }}
                     >
                        {batchProgress
                           ? `${batchProgress.completedFiles} of ${batchProgress.totalFiles} photos uploaded (${uploadProgress}%)`
                           : `${uploadProgress}% complete`}
                     </Typography>
                  </Box>
               )}
            </DialogContent>
            <DialogActions>
               <Button onClick={handleCloseDirectUpload} disabled={uploading}>
                  Cancel
               </Button>
               <Button
                  onClick={handleDirectUpload}
                  variant='contained'
                  disabled={uploading || selectedFiles.length === 0}
                  startIcon={uploading ? <CircularProgress size={16} /> : <AddPhotoAlternateIcon />}
               >
                  {uploading
                     ? 'Uploading...'
                     : `Upload ${selectedFiles.length} Image${selectedFiles.length !== 1 ? 's' : ''}`}
               </Button>
            </DialogActions>
         </Dialog>
         {/* Photo context menu */}
         <Menu
            anchorEl={photoMenuAnchor}
            open={Boolean(photoMenuAnchor)}
            onClose={handlePhotoMenuClose}
         >
            <MenuItem
               onClick={handleSetCover}
               disabled={settingCover || album?.coverPhotoId === selectedPhotoForMenu?.id}
            >
               <StarIcon sx={{ mr: 1, fontSize: 20 }} />
               {album?.coverPhotoId === selectedPhotoForMenu?.id ? 'Current Cover' : 'Set as Cover'}
            </MenuItem>
            <MenuItem onClick={handleRemoveFromAlbum}>
               <Box sx={{ mr: 1, fontSize: 20 }}>🗑️</Box>
               Remove from Album
            </MenuItem>
         </Menu>

         {/* Photo Modal */}
         <Backdrop
            sx={{
               color: '#fff',
               zIndex: (theme) => theme.zIndex.drawer + 1,
               bgcolor: 'rgba(0, 0, 0, 0.95)',
            }}
            open={modalOpen}
            onClick={closePhotoModal}
         >
            <Fade in={modalOpen}>
               <Box
                  sx={{
                     position: 'relative',
                     width: '100%',
                     height: '100%',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     p: 2,
                  }}
                  onClick={(e) => e.stopPropagation()}
               >
                  {/* Close Button */}
                  <IconButton
                     sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        color: 'white',
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                           bgcolor: 'rgba(0, 0, 0, 0.7)',
                        },
                        zIndex: 2,
                     }}
                     onClick={closePhotoModal}
                  >
                     <CloseIcon />
                  </IconButton>

                  {/* Previous Button */}
                  {albumPhotos.length > 1 && (
                     <IconButton
                        sx={{
                           position: 'absolute',
                           left: 16,
                           color: 'white',
                           bgcolor: 'rgba(0, 0, 0, 0.5)',
                           backdropFilter: 'blur(10px)',
                           '&:hover': {
                              bgcolor: 'rgba(0, 0, 0, 0.7)',
                           },
                           zIndex: 2,
                        }}
                        onClick={goToPrevPhoto}
                     >
                        <ArrowBackIosIcon />
                     </IconButton>
                  )}

                  {/* Next Button */}
                  {albumPhotos.length > 1 && (
                     <IconButton
                        sx={{
                           position: 'absolute',
                           right: 16,
                           color: 'white',
                           bgcolor: 'rgba(0, 0, 0, 0.5)',
                           backdropFilter: 'blur(10px)',
                           '&:hover': {
                              bgcolor: 'rgba(0, 0, 0, 0.7)',
                           },
                           zIndex: 2,
                        }}
                        onClick={goToNextPhoto}
                     >
                        <ArrowForwardIosIcon />
                     </IconButton>
                  )}

                  {/* Photo Container */}
                  <Box
                     sx={{
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                     }}
                  >
                     {albumPhotos[currentPhotoIndex] && (
                        <Box sx={{ position: 'relative' }}>
                           {isVideo(albumPhotos[currentPhotoIndex]) ? (
                              <video
                                 src={
                                    albumPhotos[currentPhotoIndex].url ||
                                    (albumPhotos[currentPhotoIndex] as { videoUrl?: string })
                                       .videoUrl
                                 }
                                 poster={
                                    albumPhotos[currentPhotoIndex].thumbnailUrl || undefined
                                 }
                                 controls
                                 style={{
                                    maxWidth: '100%',
                                    maxHeight: '90vh',
                                    objectFit: 'contain',
                                    borderRadius: '8px',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                                 }}
                              />
                           ) : (
                              <img
                                 src={albumPhotos[currentPhotoIndex].url}
                                 alt={albumPhotos[currentPhotoIndex].title || 'Photo'}
                                 style={{
                                    maxWidth: '100%',
                                    maxHeight: '90vh',
                                    objectFit: 'contain',
                                    borderRadius: '8px',
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                                 }}
                              />
                           )}

                           {/* Photo Info */}
                           <Box
                              sx={{
                                 position: 'absolute',
                                 bottom: -60,
                                 left: 0,
                                 right: 0,
                                 textAlign: 'center',
                                 color: 'white',
                              }}
                           >
                              {albumPhotos[currentPhotoIndex].title && (
                                 <Typography variant='h6' sx={{ mb: 1 }}>
                                    {albumPhotos[currentPhotoIndex].title}
                                 </Typography>
                              )}

                              <Box
                                 sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 1 }}
                              >
                                 {getBestPhotoDate(albumPhotos[currentPhotoIndex]) && (
                                    <Typography
                                       variant='body2'
                                       sx={{
                                          opacity: 0.9,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                       }}
                                    >
                                       📅 {formatPhotoDate(albumPhotos[currentPhotoIndex], 'long')}
                                    </Typography>
                                 )}

                                 {formatLocationCoordinates(
                                    albumPhotos[currentPhotoIndex].location
                                 ) && (
                                    <Typography
                                       variant='body2'
                                       sx={{
                                          opacity: 0.9,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                       }}
                                    >
                                       📍{' '}
                                       {formatLocationCoordinates(
                                          albumPhotos[currentPhotoIndex].location
                                       )}
                                    </Typography>
                                 )}
                              </Box>

                              {formatCameraInfo(albumPhotos[currentPhotoIndex]) && (
                                 <Typography
                                    variant='caption'
                                    sx={{ opacity: 0.7, display: 'block', mb: 1 }}
                                 >
                                    {formatCameraInfo(albumPhotos[currentPhotoIndex])}
                                 </Typography>
                              )}

                              <Typography variant='body2' sx={{ opacity: 0.7 }}>
                                 {currentPhotoIndex + 1} of {albumPhotos.length}
                              </Typography>
                           </Box>
                        </Box>
                     )}
                  </Box>

                  {/* Touch/Swipe Areas for Mobile */}
                  {albumPhotos.length > 1 && (
                     <>
                        <Box
                           sx={{
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: '20%',
                              cursor: 'pointer',
                              display: { xs: 'block', sm: 'none' },
                           }}
                           onClick={goToPrevPhoto}
                        />
                        <Box
                           sx={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              bottom: 0,
                              width: '20%',
                              cursor: 'pointer',
                              display: { xs: 'block', sm: 'none' },
                           }}
                           onClick={goToNextPhoto}
                        />
                     </>
                  )}
               </Box>
            </Fade>
         </Backdrop>

         {/* Snackbar for user feedback */}
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert
               onClose={() => setSnackbarOpen(false)}
               severity={snackbarSeverity}
               sx={{
                  width: '100%',
                  backgroundColor: snackbarSeverity === 'info' ? '#00C4A7' : undefined,
                  color: snackbarSeverity === 'info' ? 'white' : undefined,
                  '& .MuiAlert-message': {
                     color: snackbarSeverity === 'info' ? 'white' : undefined,
                  },
                  '& .MuiAlert-icon': {
                     color: snackbarSeverity === 'info' ? 'white' : undefined,
                  },
               }}
               action={
                  snackbarSeverity === 'info' ? (
                     <Button
                        size="small"
                        onClick={() => {
                           setSnackbarOpen(false);
                           navigate('/personal-photos?tab=downloaded');
                        }}
                        sx={{
                           color: 'white',
                           border: '1px solid white',
                           '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                           },
                        }}
                     >
                        View
                     </Button>
                  ) : null
               }
            >
               {snackbarMessage}
            </Alert>
         </Snackbar>

         {/* Album Invite Dialog */}
         {album && (
            <AlbumInviteDialog
               open={inviteDialogOpen}
               onClose={() => setInviteDialogOpen(false)}
               album={album}
               onInviteSuccess={() => {
                  // Optionally refresh album data here
                  setInviteDialogOpen(false);
               }}
            />
         )}

         {/* Participant Reminder Modal */}
         <ParticipantReminderModal
            open={reminderModalOpen}
            onClose={handleCloseReminderModal}
            photos={albumPhotos.map((p) => ({
               id: p.id,
               url: p.url,
               title: p.title || 'Untitled',
            }))}
            selectedPhotos={selectedPhotosForReminder}
            onTogglePhoto={handleTogglePhotoForReminder}
            message={reminderMessage}
            onMessageChange={setReminderMessage}
            onSend={handleSendReminder}
            sending={sendingReminder}
            members={members}
            loadingPhotos={loadingAlbumPhotos}
            hasMorePhotos={hasMorePhotos}
            onLoadMorePhotos={loadMorePhotos}
         />

         {/* Album Sharing Dialog */}
         {album && (
            <AlbumSharingDialog
               open={sharingDialogOpen}
               onClose={() => setSharingDialogOpen(false)}
               album={album}
            />
         )}

         {/* Edit Member Dialog */}
         <Dialog open={editMemberDialog} onClose={() => setEditMemberDialog(false)}>
            <DialogTitle>Edit Member Role</DialogTitle>
            <DialogContent>
               <Typography variant='body2' sx={{ mb: 2 }}>
                  Change the role for {memberToEdit?.displayName || memberToEdit?.email}
               </Typography>
               <Box sx={{ mt: 2 }}>
                  <Button
                     variant={newMemberRole === 'viewer' ? 'contained' : 'outlined'}
                     onClick={() => setNewMemberRole('viewer')}
                     sx={{ mr: 1 }}
                  >
                     Viewer
                  </Button>
                  <Button
                     variant={newMemberRole === 'editor' ? 'contained' : 'outlined'}
                     onClick={() => setNewMemberRole('editor')}
                     sx={{ mr: 1 }}
                  >
                     Editor
                  </Button>
                  <Button
                     variant={newMemberRole === 'owner' ? 'contained' : 'outlined'}
                     onClick={() => setNewMemberRole('owner')}
                  >
                     Owner
                  </Button>
               </Box>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setEditMemberDialog(false)}>Cancel</Button>
               <Button onClick={confirmMemberRoleChange} variant='contained'>
                  Save
               </Button>
            </DialogActions>
         </Dialog>

         {/* View All Photos Modal */}
         <Dialog
            open={viewAllPhotosOpen}
            onClose={handleCloseViewAllPhotos}
            maxWidth='lg'
            fullWidth
            PaperProps={{
               sx: {
                  borderRadius: 3,
                  maxHeight: '90vh',
               },
            }}
         >
            <DialogTitle
               sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  pb: 2,
               }}
            >
               <Typography variant='h6' component='div' sx={{ fontWeight: 600 }}>
                  All Photos ({albumPhotos.length})
               </Typography>
               <IconButton
                  onClick={handleCloseViewAllPhotos}
                  sx={{
                     color: 'grey.500',
                     '&:hover': { bgcolor: 'grey.100' },
                  }}
               >
                  <CloseIcon />
               </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 3, position: 'relative' }}>
               {/* Floating folder bar - always visible in View All mode */}
               {photoFolders.length > 0 && (
                  <Box
                     sx={{
                        position: 'sticky',
                        top: -24,
                        zIndex: 10,
                        bgcolor: 'background.paper',
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                        p: 1.5,
                        mb: 2,
                        mx: -3,
                        px: 3,
                        boxShadow: 2,
                        display: 'flex',
                        gap: 1,
                        overflowX: 'auto',
                        '&::-webkit-scrollbar': {
                           height: '6px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                           background: 'rgba(0,0,0,0.2)',
                           borderRadius: '3px',
                        },
                     }}
                  >
                     <Typography
                        variant='caption'
                        sx={{
                           display: 'flex',
                           alignItems: 'center',
                           color: 'text.secondary',
                           mr: 1,
                           whiteSpace: 'nowrap',
                        }}
                     >
                        Drag photos to folders →
                     </Typography>
                     {photoFolders.map((folder) => (
                        <DroppableFolderCard
                           key={folder.id}
                           folder={folder}
                           onDrop={handleDropToFolder}
                        >
                           <Chip
                              icon={<FolderIcon />}
                              label={`${folder.name} (${folder.photoIds.length})`}
                              color='primary'
                              variant='outlined'
                              sx={{
                                 cursor: 'pointer',
                                 '&:hover': {
                                    bgcolor: 'primary.light',
                                    color: 'white',
                                 },
                              }}
                              onClick={() => {
                                 if (folder.photoIds.length > 0) {
                                    handleViewFolder(folder);
                                 }
                              }}
                           />
                        </DroppableFolderCard>
                     ))}
                  </Box>
               )}

               {albumPhotos.length > 0 && (
                  <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
                     {albumPhotos
                        .slice((currentPage - 1) * photosPerPage, currentPage * photosPerPage)
                        .map((photo) => (
                           <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={photo.id}>
                              <Card
                                 sx={{
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    transition: 'transform 0.2s ease-in-out',
                                    '&:hover': {
                                       transform: 'scale(1.02)',
                                       boxShadow: 3,
                                    },
                                 }}
                              >
                                 <DraggablePhotoCard
                                    photo={photo}
                                    photoFoldersExist={photoFolders.length > 0}
                                 >
                                    {isVideo(photo) ? (
                                       <Box
                                          component='video'
                                          src={photo.url || (photo as { videoUrl?: string }).videoUrl}
                                          poster={photo.thumbnailUrl || undefined}
                                          muted
                                          loop
                                          playsInline
                                          sx={{
                                             height: 140,
                                             objectFit: 'cover',
                                             cursor: 'pointer',
                                          }}
                                          onClick={() =>
                                             window.open(
                                                photo.url || (photo as { videoUrl?: string }).videoUrl,
                                                '_blank'
                                             )
                                          }
                                       />
                                    ) : (
                                       <CardMedia
                                          component='img'
                                          height='140'
                                          image={photo.url}
                                          alt={photo.title}
                                          sx={{
                                             objectFit: 'cover',
                                             cursor: 'pointer',
                                          }}
                                          onClick={() => window.open(photo.url, '_blank')}
                                       />
                                    )}

                                    <Box
                                       sx={{
                                          position: 'absolute',
                                          top: 5,
                                          right: 5,
                                          display: 'flex',
                                          gap: 1,
                                          alignItems: 'center',
                                       }}
                                    >
                                       {/* Delete icon - only visible for owner/editor */}
                                       {(userRole === 'owner' || userRole === 'editor') && (
                                          <IconButton
                                             sx={{
                                                backgroundColor: 'rgba(211, 47, 47, 0.9)',
                                                color: 'white',
                                                width: 32,
                                                height: 32,
                                                '&:hover': {
                                                   backgroundColor: 'rgba(211, 47, 47, 1)',
                                                },
                                             }}
                                             onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedPhotoForMenu(photo);
                                                setDeletePhotoDialogOpen(true);
                                             }}
                                             size="small"
                                          >
                                             <DeleteIcon fontSize="small" />
                                          </IconButton>
                                       )}
                                       <Button
                                          sx={{
                                             backgroundColor: 'primary.main',
                                             color: 'white',
                                             cursor: 'pointer',
                                             fontSize: '12px',
                                             padding: '5px 10px',
                                             minHeight: 'max-content',
                                             height: 'max-content',
                                             display: 'flex',
                                             alignItems: 'center',
                                             justifyContent: 'center',
                                             '&:hover': {
                                                backgroundColor: 'primary.dark',
                                             },
                                          }}
                                          onClick={(e) => {
                                             e.stopPropagation();
                                             const photoIndex = albumPhotos
                                                .slice(
                                                   (currentPage - 1) * photosPerPage,
                                                   currentPage * photosPerPage
                                                )
                                                .findIndex((p) => p.id === photo.id);
                                             setPhotoGalleryIndex(photoIndex);
                                             setPhotoGalleryOpen(true);
                                          }}
                                       >
                                          View
                                       </Button>
                                    </Box>
                                 </DraggablePhotoCard>

                                 <CardContent sx={{ p: 2 }}>
                                       <Typography variant='subtitle2' noWrap>
                                          {photo?.title || 'Untitled'}
                                       </Typography>
                                       {photo.uploaderProfile &&
                                          (photo.uploaderProfile.displayName ||
                                             photo.uploaderProfile.name ||
                                             photo.uploaderProfile.fullName) && (
                                             <Box
                                                onMouseDown={(e) => e.stopPropagation()}
                                                onClick={(e) => {
                                                   e.stopPropagation();
                                                   const uid = (photo as { userId?: string; uploadedBy?: string }).userId || (photo as { userId?: string; uploadedBy?: string }).uploadedBy;
                                                   if (uid) setProfilePopoverUserId(uid);
                                                }}
                                                sx={{
                                                   display: 'flex',
                                                   alignItems: 'center',
                                                   mt: 1,
                                                   cursor: (photo as { userId?: string; uploadedBy?: string }).userId || (photo as { userId?: string; uploadedBy?: string }).uploadedBy ? 'pointer' : 'default',
                                                }}
                                             >
                                                <Avatar
                                                   sx={{ width: 20, height: 20, mr: 1 }}
                                                   src={
                                                      photo.uploaderProfile?.profilePicture ||
                                                      photo.uploaderProfile?.photoURL ||
                                                      photo.uploaderProfile?.profilePhoto ||
                                                      photo.uploaderProfile?.avatar
                                                   }
                                                >
                                                   {photo.uploaderProfile?.displayName?.charAt(0) ||
                                                      photo.uploaderProfile?.name?.charAt(0) ||
                                                      photo.uploaderProfile?.fullName?.charAt(0) ||
                                                      '?'}
                                                </Avatar>
                                                <Typography
                                                   variant='caption'
                                                   color='text.secondary'
                                                >
                                                   {photo.uploaderProfile?.displayName ||
                                                      photo.uploaderProfile?.name ||
                                                      photo.uploaderProfile?.fullName}
                                                </Typography>
                                             </Box>
                                          )}
                                 </CardContent>
                              </Card>
                           </Grid>
                        ))}
                  </Grid>
               )}
            </DialogContent>

            <DialogActions sx={{ p: 3, flexDirection: 'column', gap: 2 }}>
               {/* Pagination Controls */}
               {Math.ceil(albumPhotos.length / photosPerPage) > 1 && (
                  <Box
                     sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                     }}
                  >
                     <Pagination
                        count={Math.ceil(albumPhotos.length / photosPerPage)}
                        page={currentPage}
                        onChange={(_, page) => setCurrentPage(page)}
                        color='primary'
                        size='large'
                        showFirstButton
                        showLastButton
                        sx={{
                           '& .MuiPaginationItem-root': {
                              borderRadius: 2,
                           },
                        }}
                     />
                  </Box>
               )}

               {/* Close Button */}
               <Button
                  onClick={handleCloseViewAllPhotos}
                  variant='outlined'
                  sx={{
                     borderRadius: 2,
                     textTransform: 'none',
                     fontWeight: 500,
                     px: 4,
                  }}
               >
                  Close
               </Button>
            </DialogActions>
         </Dialog>

         {/* Delete Member Dialog */}
         <Dialog
            open={deleteMemberDialog}
            onClose={() => !deletingMember && setDeleteMemberDialog(false)}
         >
            <DialogTitle>Remove Member</DialogTitle>
            <DialogContent>
               <Typography variant='body1'>
                  Are you sure you want to remove{' '}
                  {memberToDelete?.displayName || memberToDelete?.email} from this album?
               </Typography>
               <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                  This action cannot be undone.
               </Typography>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setDeleteMemberDialog(false)} disabled={deletingMember}>
                  Cancel
               </Button>
               <Button
                  onClick={confirmMemberDeletion}
                  color='error'
                  variant='contained'
                  disabled={deletingMember}
                  startIcon={deletingMember ? <CircularProgress size={20} color='inherit' /> : null}
               >
                  {deletingMember ? 'Removing...' : 'Remove'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Delete Photo from Album Dialog */}
         <Dialog
            open={deletePhotoDialogOpen}
            onClose={() => !deletingPhoto && setDeletePhotoDialogOpen(false)}
         >
            <DialogTitle>Remove Photo from Album</DialogTitle>
            <DialogContent>
               <Typography variant='body1'>
                  Are you sure you want to remove this photo from the album?
               </Typography>
               {selectedPhotoForMenu && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                     <img
                        src={selectedPhotoForMenu.url}
                        alt={selectedPhotoForMenu.title || 'Photo'}
                        style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '4px' }}
                     />
                  </Box>
               )}
               <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
                  Note: This will only remove the photo from this album. The photo will remain in your library.
               </Typography>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => {
                  setDeletePhotoDialogOpen(false);
                  setSelectedPhotoForMenu(null);
               }} disabled={deletingPhoto}>
                  Cancel
               </Button>
               <Button
                  onClick={async () => {
                     if (!selectedPhotoForMenu || !album) {
                        console.log('❌ Cannot delete: missing photo or album');
                        return;
                     }
                     
                     console.log('🗑️ Starting photo deletion:', selectedPhotoForMenu.id);
                     setDeletingPhoto(true);
                     try {
                        const headers = await getAuthHeaders();
                        console.log('📤 Sending delete request to API...');
                        const response = await fetch(
                           `${process.env.REACT_APP_API_URL}/api/albums/${album.id}/remove-photo`,
                           {
                              method: 'POST',
                              headers,
                              body: JSON.stringify({ photoId: selectedPhotoForMenu.id }),
                           }
                        );

                        console.log('📥 API response status:', response.status);

                        if (!response.ok) {
                           const responseText = await response.text();
                           console.error('❌ API error:', responseText);
                           let errorMessage = 'Failed to remove photo from album';
                           try {
                              const errorData = JSON.parse(responseText);
                              errorMessage = errorData.message || errorMessage;
                           } catch {
                              errorMessage = `Server error (${response.status}): ${response.statusText}`;
                           }
                           throw new Error(errorMessage);
                        }

                        console.log('✅ Photo removed from backend successfully');

                        // Update local album state
                        setAlbum((prev) => {
                           if (!prev) return null;
                           const updatedPhotoIds = (prev.photoIds || []).filter(
                              (id) => id !== selectedPhotoForMenu.id
                           );
                           console.log('📝 Updated album photoIds:', updatedPhotoIds.length);
                           return { ...prev, photoIds: updatedPhotoIds };
                        });

                        // Reload photos from Firestore (backend has updated both album and photo document)
                        console.log('🔄 Reloading photos from Firestore...');
                        reloadPhotos();
                        console.log('✅ Photo reload triggered');

                        // Show success message
                        setSnackbarMessage('Photo removed from album successfully');
                        setSnackbarSeverity('success');
                        setSnackbarOpen(true);

                        // Close dialog and reset states
                        setDeletePhotoDialogOpen(false);
                        setSelectedPhotoForMenu(null);
                        
                        console.log('✅ Deletion complete!');
                     } catch (error) {
                        console.error('❌ Error removing photo from album:', error);
                        setSnackbarMessage(
                           error instanceof Error ? error.message : 'Failed to remove photo from album'
                        );
                        setSnackbarSeverity('error');
                        setSnackbarOpen(true);
                     } finally {
                        setDeletingPhoto(false);
                     }
                  }}
                  color='error'
                  variant='contained'
                  disabled={deletingPhoto}
                  startIcon={deletingPhoto ? <CircularProgress size={20} color='inherit' /> : null}
               >
                  {deletingPhoto ? 'Removing...' : 'Remove'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Delete Album Dialog */}
         <Dialog
            open={deleteAlbumDialog}
            onClose={() => !deletingAlbum && setDeleteAlbumDialog(false)}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle sx={{ color: '#ef4444', fontWeight: 600 }}>Delete Album?</DialogTitle>
            <DialogContent>
               <Typography variant='body1' gutterBottom>
                  Are you sure you want to permanently delete the album{' '}
                  <strong>"{album?.title}"</strong>?
               </Typography>
               <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
                  This action cannot be undone. The album and all its settings will be permanently
                  removed. Photos will remain in your library and can be added to other albums.
               </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
               <Button onClick={() => setDeleteAlbumDialog(false)} disabled={deletingAlbum}>
                  Cancel
               </Button>
               <Button
                  onClick={handleDeleteAlbum}
                  color='error'
                  variant='contained'
                  disabled={deletingAlbum}
                  startIcon={deletingAlbum ? <CircularProgress size={20} /> : <DeleteIcon />}
               >
                  {deletingAlbum ? 'Deleting...' : 'Delete Album'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Participant Profile Modal */}
         <ParticipantProfileModal
            open={participantProfileModalOpen}
            onClose={handleCloseParticipantProfile}
            participant={selectedParticipant as any}
         />

         {/* Photo Gallery Popup */}
         <PhotoGalleryPopup
            open={photoGalleryOpen}
            onClose={() => setPhotoGalleryOpen(false)}
            photos={
               (
                  albumPhotos?.slice(
                     (currentPage - 1) * photosPerPage,
                     currentPage * photosPerPage
                  ) || []
               ).map((photo) => ({
                  ...photo,
                  filename: (photo as any).filename || photo.title || 'Unknown',
                  thumbnailUrl: (photo as any).thumbnailUrl || photo.url,
                  title: photo.title || 'Untitled',
                  description: photo.description || '',
                  tags: photo.tags || [],
                  takenAt:
                     photo.takenAt instanceof Date
                        ? photo.takenAt
                        : (photo.takenAt as any)?.toDate
                          ? (photo.takenAt as any).toDate()
                          : new Date(),
                  uploadedAt:
                     photo.uploadedAt instanceof Date
                        ? photo.uploadedAt
                        : (photo.uploadedAt as any)?.toDate
                          ? (photo.uploadedAt as any).toDate()
                          : new Date(),
               })) as any
            }
            initialPhotoIndex={photoGalleryIndex}
         />

         {/* Folder Creation Modal */}
         <Dialog
            open={folderModalOpen}
            onClose={() => {
               setFolderModalOpen(false);
               setNewFolderName('');
            }}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle>Create New Folder</DialogTitle>
            <DialogContent>
               <TextField
                  autoFocus
                  margin='dense'
                  label='Folder Name'
                  type='text'
                  fullWidth
                  variant='outlined'
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyPress={(e) => {
                     if (e.key === 'Enter') {
                        handleCreateFolder();
                     }
                  }}
                  sx={{ mt: 2 }}
               />
            </DialogContent>
            <DialogActions>
               <Button
                  onClick={() => {
                     setFolderModalOpen(false);
                     setNewFolderName('');
                  }}
               >
                  Cancel
               </Button>
               <Button
                  onClick={handleCreateFolder}
                  variant='contained'
                  disabled={!newFolderName.trim()}
                  sx={{
                     background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                     '&:hover': {
                        background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                     },
                  }}
               >
                  Create Folder
               </Button>
            </DialogActions>
         </Dialog>

         {/* Edit Folder Modal */}
         <Dialog
            open={editFolderModalOpen}
            onClose={() => {
               setEditFolderModalOpen(false);
               setFolderToEdit(null);
               setEditedFolderName('');
            }}
            maxWidth='sm'
            fullWidth
         >
            <DialogTitle>Edit Folder Name</DialogTitle>
            <DialogContent>
               <TextField
                  autoFocus
                  margin='dense'
                  label='Folder Name'
                  type='text'
                  fullWidth
                  variant='outlined'
                  value={editedFolderName}
                  onChange={(e) => setEditedFolderName(e.target.value)}
                  onKeyPress={(e) => {
                     if (e.key === 'Enter') {
                        handleSaveEditedFolder();
                     }
                  }}
                  sx={{ mt: 2 }}
               />
            </DialogContent>
            <DialogActions>
               <Button
                  onClick={() => {
                     setEditFolderModalOpen(false);
                     setFolderToEdit(null);
                     setEditedFolderName('');
                  }}
                  disabled={savingFolder}
               >
                  Cancel
               </Button>
               <Button
                  onClick={handleSaveEditedFolder}
                  variant='contained'
                  disabled={!editedFolderName.trim() || savingFolder}
                  sx={{
                     background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                     '&:hover': {
                        background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                     },
                  }}
               >
                  {savingFolder ? 'Saving...' : 'Save'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* View Folder Photos Modal */}
         <Dialog
            open={viewFolderModalOpen}
            onClose={() => {
               setViewFolderModalOpen(false);
               setFolderToView(null);
               setCurrentFolderPhotoIndex(0);
            }}
            maxWidth='lg'
            fullWidth
            PaperProps={{
               sx: {
                  height: '90vh',
                  maxHeight: '900px',
                  bgcolor: '#1a1a1a',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.08)',
               },
            }}
         >
            <DialogTitle
               sx={{
                  bgcolor: '#252525',
                  color: 'rgba(255,255,255,0.95)',
                  borderBottom: '1px solid rgba(255,255,255,0.1)',
               }}
            >
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                     <FolderIcon sx={{ color: '#00C4A7' }} />
                     <Typography
                        variant='h6'
                        sx={{
                           overflow: 'hidden',
                           textOverflow: 'ellipsis',
                           whiteSpace: 'nowrap',
                           maxWidth: '400px',
                           color: 'inherit',
                        }}
                        title={folderToView?.name}
                     >
                        {folderToView?.name}
                     </Typography>
                     <Chip
                        label={`${folderToView?.photoIds.length || 0} photos`}
                        size='small'
                        sx={{ bgcolor: 'rgba(0,196,167,0.2)', color: '#00C4A7' }}
                     />
                  </Box>
                  <IconButton
                     onClick={() => {
                        setViewFolderModalOpen(false);
                        setFolderToView(null);
                        setCurrentFolderPhotoIndex(0);
                     }}
                     sx={{ color: 'rgba(255,255,255,0.8)' }}
                  >
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 0, overflow: 'hidden', bgcolor: '#0d0d0d' }}>
               {folderToView && folderToView.photoIds.length > 0 && (
                  <Box
                     sx={{
                        position: 'relative',
                        height: '100%',
                        minHeight: '60vh',
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: '#0d0d0d',
                     }}
                  >
                     {/* Photo Display - explicit dark background so no white shows */}
                     <Box
                        sx={{
                           flex: 1,
                           minHeight: 0,
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           position: 'relative',
                           bgcolor: '#0d0d0d',
                        }}
                     >
                        {(() => {
                           const photo = albumPhotos.find(
                              (p) => p.id === folderToView.photoIds[currentFolderPhotoIndex]
                           );
                           if (!photo) return null;
                           return isVideo(photo) ? (
                              <Box
                                 component='video'
                                 src={photo.url || (photo as { videoUrl?: string }).videoUrl}
                                 poster={photo.thumbnailUrl || undefined}
                                 controls
                                 sx={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    backgroundColor: 'transparent',
                                 }}
                              />
                           ) : (
                              <Box
                                 component='img'
                                 src={photo.url}
                                 alt={photo.title || 'Photo'}
                                 sx={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    backgroundColor: 'transparent',
                                 }}
                              />
                           );
                        })()}
                     </Box>

                     {/* Navigation Buttons - Outside photo container */}
                     {folderToView && folderToView.photoIds.length > 1 && (
                        <>
                           {currentFolderPhotoIndex > 0 && (
                              <IconButton
                                 onClick={handlePrevFolderPhoto}
                                 sx={{
                                    position: 'absolute',
                                    left: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                                    zIndex: 10,
                                    '&:hover': { bgcolor: 'white' },
                                 }}
                              >
                                 <ArrowBackIosIcon />
                              </IconButton>
                           )}
                           {currentFolderPhotoIndex < folderToView.photoIds.length - 1 && (
                              <IconButton
                                 onClick={handleNextFolderPhoto}
                                 sx={{
                                    position: 'absolute',
                                    right: 16,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                                    zIndex: 10,
                                    '&:hover': { bgcolor: 'white' },
                                 }}
                              >
                                 <ArrowForwardIosIcon />
                              </IconButton>
                           )}
                        </>
                     )}

                     {/* Photo Counter */}
                     <Box
                        sx={{
                           position: 'absolute',
                           bottom: 16,
                           left: '50%',
                           transform: 'translateX(-50%)',
                           bgcolor: 'rgba(0, 0, 0, 0.7)',
                           color: 'white',
                           px: 2,
                           py: 1,
                           borderRadius: 2,
                        }}
                     >
                        <Typography variant='body2'>
                           {currentFolderPhotoIndex + 1} / {folderToView.photoIds.length}
                        </Typography>
                     </Box>

                     {/* Remove from Folder Button */}
                     <IconButton
                        onClick={() => {
                           if (folderToView) {
                              const photoId = folderToView.photoIds[currentFolderPhotoIndex];
                              handleRemovePhotoFromFolder(folderToView.id, photoId);

                              // Update local state to remove photo from view
                              const updatedPhotoIds = folderToView.photoIds.filter(
                                 (id) => id !== photoId
                              );

                              if (updatedPhotoIds.length === 0) {
                                 // Close modal if no more photos
                                 setViewFolderModalOpen(false);
                                 setFolderToView(null);
                                 setCurrentFolderPhotoIndex(0);
                              } else {
                                 // Adjust index if needed
                                 if (currentFolderPhotoIndex >= updatedPhotoIds.length) {
                                    setCurrentFolderPhotoIndex(updatedPhotoIds.length - 1);
                                 }
                                 setFolderToView({ ...folderToView, photoIds: updatedPhotoIds });
                              }
                           }
                        }}
                        sx={{
                           position: 'absolute',
                           top: 16,
                           right: 16,
                           bgcolor: 'rgba(255, 0, 0, 0.8)',
                           color: 'white',
                           '&:hover': {
                              bgcolor: 'rgba(200, 0, 0, 0.9)',
                           },
                        }}
                        title='Remove from folder'
                     >
                        <DeleteIcon />
                     </IconButton>

                     {/* Photo Info */}
                     <Box
                        sx={{
                           bgcolor: '#252525',
                           p: 2,
                           borderTop: '1px solid rgba(255,255,255,0.08)',
                        }}
                     >
                        {(() => {
                           const photo = albumPhotos.find(
                              (p) => p.id === folderToView.photoIds[currentFolderPhotoIndex]
                           );
                           if (!photo) return null;
                           return (
                              <Box>
                                 <Typography variant='subtitle1' fontWeight={600} sx={{ color: 'rgba(255,255,255,0.95)' }}>
                                    {photo.title || 'Untitled'}
                                 </Typography>
                                 {photo.description && (
                                    <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                       {photo.description}
                                    </Typography>
                                 )}
                              </Box>
                           );
                        })()}
                     </Box>
                  </Box>
               )}
            </DialogContent>
         </Dialog>

         {/* Uploader profile popup (same as public album page) */}
         <Dialog
            open={!!profilePopoverUserId}
            onClose={() => {
               setProfilePopoverUserId(null);
               setProfilePopoverMessageMode(false);
               setProfilePopoverMessageText('');
               setProfilePopoverSenderEmail('');
               setProfilePopoverSenderName('');
               setProfilePopoverMessageError(null);
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
               sx: {
                  borderRadius: 2,
                  overflow: 'hidden',
                  background: 'linear-gradient(165deg, #252530 0%, #1a1b22 50%, #16161a 100%)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  boxShadow: '0 24px 48px rgba(0,0,0,0.32), 0 0 0 1px rgba(255,255,255,0.03) inset',
               },
            }}
         >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', py: 1.5, px: 2.5, borderBottom: '1px solid rgba(0,196,167,0.15)' }}>
               <IconButton
                  onClick={() => {
                     setProfilePopoverUserId(null);
                     setProfilePopoverMessageMode(false);
                     setProfilePopoverMessageText('');
                     setProfilePopoverSenderEmail('');
                     setProfilePopoverSenderName('');
                     setProfilePopoverMessageError(null);
                  }}
                  sx={{ color: 'rgba(0,196,167,0.8)', '&:hover': { color: '#00C4A7', bgcolor: 'rgba(0,196,167,0.1)' } }}
                  size="small"
               >
                  <CloseIcon fontSize="small" />
               </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2.5, pb: 2.5, px: 2.5, maxHeight: '75vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
               {profilePopoverLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                     <CircularProgress sx={{ color: '#00C4A7' }} size={28} />
                  </Box>
               ) : profilePopoverData?.photographerProfile ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                     {profilePopoverMessageMode ? (
                        <>
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                              <Button
                                 startIcon={<ArrowBackIcon />}
                                 size="small"
                                 onClick={() => {
                                    setProfilePopoverMessageMode(false);
                                    setProfilePopoverMessageError(null);
                                 }}
                                 sx={{ color: '#00C4A7', textTransform: 'none' }}
                              >
                                 Back
                              </Button>
                           </Box>
                           <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 1.5 }}>
                              Send a message to {profilePopoverData.photographerProfile.name || 'the photographer'}
                           </Typography>
                           <TextField
                              fullWidth
                              multiline
                              rows={4}
                              placeholder="Your message..."
                              value={profilePopoverMessageText}
                              onChange={(e) => setProfilePopoverMessageText(e.target.value)}
                              disabled={profilePopoverSending}
                              size="small"
                              sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)', color: '#fff' } }}
                           />
                           {!currentUser && (
                              <>
                                 <TextField
                                    fullWidth
                                    required
                                    type="email"
                                    label="Your email (so they can reply)"
                                    value={profilePopoverSenderEmail}
                                    onChange={(e) => setProfilePopoverSenderEmail(e.target.value)}
                                    disabled={profilePopoverSending}
                                    size="small"
                                    sx={{ mb: 1, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)', color: '#fff' } }}
                                 />
                                 <TextField
                                    fullWidth
                                    label="Your name (optional)"
                                    value={profilePopoverSenderName}
                                    onChange={(e) => setProfilePopoverSenderName(e.target.value)}
                                    disabled={profilePopoverSending}
                                    size="small"
                                    sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)', color: '#fff' } }}
                                 />
                              </>
                           )}
                           {profilePopoverMessageError && (
                              <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setProfilePopoverMessageError(null)}>
                                 {profilePopoverMessageError}
                              </Alert>
                           )}
                           <Button
                              variant="contained"
                              fullWidth
                              disabled={profilePopoverSending || !profilePopoverMessageText.trim() || (!currentUser && !profilePopoverSenderEmail.trim())}
                              startIcon={profilePopoverSending ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <SendIcon />}
                              onClick={async () => {
                                 if (!profilePopoverUserId || !profilePopoverMessageText.trim()) return;
                                 if (!currentUser && !profilePopoverSenderEmail.trim()) {
                                    setProfilePopoverMessageError('Please enter your email so the photographer can reply.');
                                    return;
                                 }
                                 setProfilePopoverMessageError(null);
                                 setProfilePopoverSending(true);
                                 try {
                                    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
                                    if (currentUser) {
                                       const authHeaders = await getAuthHeaders();
                                       Object.assign(headers, authHeaders);
                                    }
                                    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/photographer-inquiry`, {
                                       method: 'POST',
                                       headers,
                                       body: JSON.stringify({
                                          photographerUserId: profilePopoverUserId,
                                          message: profilePopoverMessageText.trim(),
                                          albumId: album?.id ?? '',
                                          albumTitle: album?.title ?? undefined,
                                          ...(profilePopoverSenderEmail.trim() && { senderEmail: profilePopoverSenderEmail.trim() }),
                                          ...(profilePopoverSenderName.trim() && { senderName: profilePopoverSenderName.trim() }),
                                       }),
                                    });
                                    const data = await res.json().catch(() => ({}));
                                    if (!res.ok) {
                                       setProfilePopoverMessageError(data.message || data.error || 'Failed to send message');
                                       return;
                                    }
                                    if (data.emailSent === false) {
                                       setSnackbarMessage(data.message || 'Message saved, but the notification email to the photographer could not be sent. They can still see it in their Inbox.');
                                       setSnackbarSeverity('warning');
                                    } else {
                                       setSnackbarMessage('Your message was sent. The photographer will see it in their Inbox.');
                                       setSnackbarSeverity('success');
                                    }
                                    setSnackbarOpen(true);
                                    setProfilePopoverMessageText('');
                                    setProfilePopoverSenderEmail('');
                                    setProfilePopoverSenderName('');
                                    setProfilePopoverMessageMode(false);
                                 } catch (err: any) {
                                    setProfilePopoverMessageError(err?.message || 'Something went wrong. Please try again.');
                                 } finally {
                                    setProfilePopoverSending(false);
                                 }
                              }}
                              sx={{ bgcolor: '#00C4A7', color: '#fff', '&:hover': { bgcolor: '#017A7F' }, textTransform: 'none' }}
                           >
                              {profilePopoverSending ? 'Sending…' : 'Send'}
                           </Button>
                        </>
                     ) : (
                        <>
                     <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0, pr: 0.5 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
                        <Box sx={{ position: 'relative', flexShrink: 0 }}>
                           <Avatar
                              src={profilePopoverData.photographerProfile.profilePhotoUrl}
                              sx={{
                                 width: 72,
                                 height: 72,
                                 border: '1px solid rgba(0,196,167,0.4)',
                                 boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                              }}
                           >
                              {profilePopoverData.photographerProfile.name?.charAt(0) || '?'}
                           </Avatar>
                           <Box sx={{ position: 'absolute', bottom: -2, right: -2 }}>
                              <PhotographerBadge variant="avatarOverlay" size="md" />
                           </Box>
                        </Box>
                        <Box>
                           <Typography variant="subtitle1" sx={{ color: '#00C4A7', fontWeight: 500, mb: 0.25 }}>
                              {profilePopoverData.photographerProfile.name || 'Photographer'}
                           </Typography>
                           <Typography variant="caption" sx={{ color: 'rgba(0,196,167,0.9)', display: 'block' }}>
                              Photographer
                           </Typography>
                           {profilePopoverData.photographerProfile.photographerType && (
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block', mt: 0.25, textTransform: 'capitalize' }}>
                                 {profilePopoverData.photographerProfile.photographerType}
                              </Typography>
                           )}
                           {profilePopoverData.photographerProfile.portfolioPhotoCount != null && (
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                 <PhotoLibraryIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }} />
                                 {profilePopoverData.photographerProfile.portfolioPhotoCount} portfolio photos
                              </Typography>
                           )}
                        </Box>
                     </Box>
                     {profilePopoverData.photographerProfile.companyPhotoUrl && (
                        <Box sx={{ mb: 2 }}>
                           <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', display: 'block', mb: 0.75 }}>
                              Company / Studio
                           </Typography>
                           <Box
                              component="img"
                              src={profilePopoverData.photographerProfile.companyPhotoUrl}
                              alt="Company"
                              sx={{
                                 width: '100%',
                                 maxHeight: 180,
                                 objectFit: 'cover',
                                 borderRadius: 1.5,
                                 border: '1px solid rgba(255,255,255,0.08)',
                              }}
                           />
                        </Box>
                     )}
                     {profilePopoverData.photographerProfile.companyName && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                           <BusinessIcon sx={{ color: '#00C4A7', fontSize: 16 }} />
                           <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 400 }}>
                              {profilePopoverData.photographerProfile.companyName}
                           </Typography>
                        </Box>
                     )}
                     {profilePopoverData.photographerProfile.description && (
                        <Box sx={{ mb: 2 }}>
                           <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, fontWeight: 400 }}>
                              {profilePopoverData.photographerProfile.description}
                           </Typography>
                        </Box>
                     )}
                     <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {profilePopoverData.photographerProfile.contactNumber && (
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon sx={{ color: '#00C4A7', fontSize: 16 }} />
                              <Typography
                                 component="a"
                                 href={`tel:${profilePopoverData.photographerProfile.contactNumber}`}
                                 variant="body2"
                                 sx={{ color: '#00C4A7', textDecoration: 'none', fontWeight: 400, '&:hover': { color: 'rgba(0,196,167,0.85)' } }}
                              >
                                 {profilePopoverData.photographerProfile.contactNumber}
                              </Typography>
                           </Box>
                        )}
                        {profilePopoverData.photographerProfile.website && (
                           <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LanguageIcon sx={{ color: '#00C4A7', fontSize: 16 }} />
                              <Typography
                                 component="a"
                                 href={profilePopoverData.photographerProfile.website}
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 variant="body2"
                                 sx={{ color: '#00C4A7', fontWeight: 400, '&:hover': { color: 'rgba(0,196,167,0.85)' } }}
                              >
                                 {profilePopoverData.photographerProfile.website}
                              </Typography>
                           </Box>
                        )}
                     </Box>
                     </Box>
                     <Box sx={{ flexShrink: 0, borderTop: '1px solid rgba(255,255,255,0.08)', pt: 2, mt: 2 }}>
                        <Button
                           variant="outlined"
                           fullWidth
                           startIcon={<MessageIcon />}
                           onClick={() => setProfilePopoverMessageMode(true)}
                           sx={{
                              borderColor: '#00C4A7',
                              color: '#00C4A7',
                              textTransform: 'none',
                              '&:hover': { borderColor: '#017A7F', bgcolor: 'rgba(0,196,167,0.08)' },
                           }}
                        >
                           Message
                        </Button>
                     </Box>
                        </>
                     )}
                  </Box>
               ) : profilePopoverUserId && !profilePopoverLoading ? (
                  <Box sx={{ py: 2.5, textAlign: 'center' }}>
                     <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 400 }}>
                        Photographer profile not available for view.
                     </Typography>
                  </Box>
               ) : null}
            </DialogContent>
         </Dialog>

         {/* Individual Photo Sharing Modal */}
         <PhotoSharingModal
            open={shareModalOpen}
            onClose={() => {
               setShareModalOpen(false);
               setPhotoToShare(null);
            }}
            entityType='album'
            entityId={albumId!}
            entityTitle={album?.title || 'Album'}
            selectedPhotos={photoToShare ? [photoToShare] : []}
            entityData={entityData}
            onSuccess={(result) => {
               setShareModalOpen(false);
               setPhotoToShare(null);
               setSnackbarMessage('Photo shared successfully!');
               setSnackbarSeverity('success');
               setSnackbarOpen(true);
            }}
         />
      </Box>
   );
};

export default AlbumDetailPage;
